import { pdfMixins } from '@/pdf-mixins'
import store from '@/store'
import { mixins } from '@/mixins'
import { jsPDF } from 'jspdf'
import moment from 'moment'
import appConstants from '@/constants/constants'
import {
  actContractNumberHidden, actFillSignerDoctorName, actMedicalServiceRenderedShowPatientSignature, actPatientLabel,
  actShowPatientSignature, generalMedicalCardHideResidencePlaceDetailedFields, generalMedicalCardPrintFormat,
  lastVisitHideDoctorColumn, lastVisitShowCredentials, lastVisitShowEmptyField, purchaseOrderAdministratorHidden,
  purchaseOrderExecutorLabel, purchaseOrderFillExecutorDoctorName, purchaseOrderShowServicesText,
  treatmentDiaryHideDoctorColumn, treatmentDiaryShowCredentials, treatmentDiaryShowEmptyField,
} from '@/composables/documentSettingsHelper'
import { getPaymentDiscountAmountByTreatmentDiaryServices } from '@/composables/priceCalculationHelper'
import { getPurchaseOrderInfo } from '@/composables/dentalLaboratory'
import { saveExcelFileFromResponse } from '@/composables/fileHelper'
import { showCommonToast, showFailedOperationToast } from '@/composables/toast'
import { getIntegratorId, getLogoPng, getProgramVersionName } from '@/composables/currentUser'
import useTreatmentDiary from '@/composables/treatmentDiary'
import usePatient from '@/composables/patient'
import useIntegrator from '@/composables/integrator'
import stampSrc from '@/assets/images/integrator/stamp.png'
import toothVariant1Src from '@/assets/images/dental-laboratory/tooth_variant_1.png'
import toothVariant2Src from '@/assets/images/dental-laboratory/tooth_variant_2.png'
import toothVariant3Src from '@/assets/images/dental-laboratory/tooth_variant_3.png'
import toothVariant4Src from '@/assets/images/dental-laboratory/tooth_variant_4.png'
import toothVariant5Src from '@/assets/images/dental-laboratory/tooth_variant_5.png'
import useTeeth from '@/composables/teeth'
import useFormatter from '@/composables/formatter'

export const textToRows = (
  text, prevText, pdf, pdfMargin, pdfPosY, pdfFont, withoutLine = false, lineHeightFactor = 2,
  pdfRightMargin = null
) => {
  const pdfWidth = pdf.internal.pageSize.width
  const scaleFactor = pdf.internal.scaleFactor
  let prevTextSize = getTextWidth(pdf, prevText)
  pdfRightMargin = pdfRightMargin ? pdfRightMargin : pdfMargin
  const textMaxXPosition = pdfWidth - pdfRightMargin
  if (text) {
    const pdfHeight = pdf.internal.pageSize.height
    text = text.trim().replaceAll('\r\n', '\n').replaceAll('\r', '\n')
    let phrases = []
    if (/\n/.exec(text)) {
      phrases = text.split("\n")
    } else {
      phrases.push(text)
    }
    pdf.setFont(pdfFont)
    phrases.map((phrase, phraseIndex) => {
      phrase = phrase.trim()
      prevTextSize = phraseIndex ? 0 : (prevTextSize ? prevTextSize + 1 : 0)
      const textMargin = pdfMargin + pdfRightMargin
      pdf.setLineHeightFactor(lineHeightFactor)
      if (getTextWidth(pdf, phrase) + textMargin + prevTextSize > pdfWidth) {
        const splittedText = pdf.splitTextToSize(phrase, pdfWidth - textMargin - prevTextSize)
        pdf.text(splittedText[0], pdfMargin + prevTextSize, pdfPosY)
        if (!withoutLine) {
          pdfPosY += 1
          pdf.line(pdfMargin + prevTextSize, pdfPosY, textMaxXPosition, pdfPosY)
        }
        let otherText = splittedText.slice(1)
        otherText = pdf.splitTextToSize(otherText.join(' '), pdfWidth - textMargin)
        const otherTextLength = otherText.length
        let counter = 0
        while (counter < otherTextLength) {
          const height = pdf.getLineHeight(otherText[counter]) / scaleFactor
          if (pdfHeight < pdfPosY + pdfMargin + height) {
            pdf.addPage()
            pdfPosY = 10
            pdf.setLineHeightFactor(lineHeightFactor)
          } else if (!counter) {
            pdfPosY += height
          }
          pdf.text(otherText[counter], pdfMargin, pdfPosY)
          if (!withoutLine) {
            pdfPosY += 1
            pdf.line(pdfMargin, pdfPosY, textMaxXPosition, pdfPosY)
          }
          pdfPosY += height
          ++counter
        }
      } else {
        if (pdfHeight < pdfPosY + pdfMargin + (pdf.getLineHeight(phrase) / scaleFactor)) {
          pdf.addPage()
          pdfPosY = 10
          pdf.setLineHeightFactor(lineHeightFactor)
        }
        pdf.text(phrase, prevTextSize ? pdfMargin + prevTextSize : pdfMargin, pdfPosY)
        if (!withoutLine) {
          pdfPosY += 1
          pdf.line(prevTextSize ? pdfMargin + prevTextSize : pdfMargin, pdfPosY, textMaxXPosition, pdfPosY)
        }
        pdfPosY += pdf.getLineHeight(phrase) / scaleFactor
      }
    })
  } else if (prevText) {
    if (!withoutLine) {
      pdfPosY += 1
      pdf.line(prevTextSize ? pdfMargin + prevTextSize + 1 : pdfMargin, pdfPosY, textMaxXPosition, pdfPosY)
    }
    pdfPosY += pdf.getLineHeight('-') / scaleFactor
  }
  return pdfPosY
}

export const textToLines = (
  text, prevText, pdf, pdfMargin, pdfPosY, pdfFont, withoutLine = false, scaleFactor = null,
  useParagraph = true, pdfMarginRight = null, paragraphOffset = null, addEmptyLines = null
) => {
  const pdfWidth = pdf.internal.pageSize.width
  const lineHeightFactor = 2
  scaleFactor = scaleFactor ? scaleFactor : pdf.internal.scaleFactor
  paragraphOffset = paragraphOffset ? paragraphOffset : 0
  pdfMarginRight = pdfMarginRight ? pdfMarginRight : pdfMargin
  let prevTextSize = getTextWidth(pdf, prevText)
  if (text) {
    const pdfHeight = pdf.internal.pageSize.height
    text = text.trim()
    let phrases = []
    if (/\n/.exec(text)) {
      phrases = text.split("\n")
    } else {
      phrases.push(text)
    }
    pdf.setFont(pdfFont)
    phrases.map((phrase, phraseIndex) => {
      phrase = phrase.trim()
      prevTextSize = phraseIndex || !useParagraph ? 0 : (prevTextSize ? prevTextSize + 1 : 0)
      const textMargin = pdfMarginRight ? pdfMargin + pdfMarginRight : (pdfMargin * 2)
      pdf.setLineHeightFactor(lineHeightFactor)
      const textX = pdfMargin + prevTextSize + paragraphOffset
      if (getTextWidth(pdf, phrase) + textMargin + prevTextSize + paragraphOffset > pdfWidth) {
        const splitText = pdf.splitTextToSize(phrase, pdfWidth - textMargin - prevTextSize - paragraphOffset)
        pdf.text(splitText[0], textX, pdfPosY)
        if (!withoutLine) {
          const lineX1 = pdfMargin + prevTextSize + paragraphOffset
          pdf.line(lineX1, pdfPosY + 1, pdfWidth - pdfMarginRight, pdfPosY + 1)
        }
        let otherText = splitText.slice(1)
        otherText = pdf.splitTextToSize(otherText.join(' '), pdfWidth - textMargin)
        const otherTextLength = otherText.length
        let counter = 0
        while (counter < otherTextLength) {
          const height = pdf.getTextDimensions(otherText[counter]).h + scaleFactor
          if (pdfHeight < pdfPosY + 10 + height) {
            pdf.addPage()
            pdfPosY = 10
            pdf.setLineHeightFactor(lineHeightFactor)
          } else if (!counter) {
            pdfPosY += height
          }
          pdf.text(otherText[counter], pdfMargin, pdfPosY)
          if (!withoutLine) {
            pdf.line(pdfMargin, pdfPosY + 1, pdfWidth - pdfMarginRight, pdfPosY + 1)
          }
          pdfPosY += height
          ++counter
        }
      } else {
        if (pdfHeight < pdfPosY + 10 + pdf.getTextDimensions(phrase).h + scaleFactor) {
          pdf.addPage()
          pdfPosY = 10
          pdf.setLineHeightFactor(lineHeightFactor)
        }
        pdf.text(phrase, textX, pdfPosY)
        if (!withoutLine) {
          const lineX1 = pdfMargin + prevTextSize + paragraphOffset
          pdf.line(lineX1, pdfPosY + 1, pdfWidth - pdfMarginRight, pdfPosY + 1)
          if (addEmptyLines) {
            while (addEmptyLines > 0) {
              pdfPosY += pdf.getTextDimensions(phrase).h + scaleFactor
              pdf.line(pdfMargin, pdfPosY + 1, pdfWidth - pdfMarginRight, pdfPosY + 1)
              addEmptyLines -=1
            }
          }
        }
        pdfPosY += pdf.getTextDimensions(phrase).h + scaleFactor
      }
    })
  } else if (prevText) {
    const phrase = '-'
    if (!withoutLine) {
      const lineX1 = pdfMargin + prevTextSize + paragraphOffset
      pdf.line(lineX1, pdfPosY + 1, pdfWidth - pdfMarginRight, pdfPosY + 1)
      if (addEmptyLines) {
        while (addEmptyLines > 0) {
          pdfPosY += pdf.getTextDimensions(phrase).h + scaleFactor
          pdf.line(pdfMargin, pdfPosY + 1, pdfWidth - pdfMarginRight, pdfPosY + 1)
          addEmptyLines -=1
        }
      }
    }
    pdfPosY += pdf.getTextDimensions(phrase).h + scaleFactor
  }
  return pdfPosY
}

export const createActOfServicesRenderedForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования акта оказанных услуг. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const client = purchaseOrder.client
  const patient = purchaseOrder.patient
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const pdfMargin = 8
  const pdfFontSize = 10
  const pdfRowHeight = 7
  const pdfWidth = 210
  const pdfHeight = 297
  const lineHeightFactor = 1.5

  let pdfCurrentPositionY = 0
  let pdfFile = new jsPDF({orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true})

  // header
  pdfFile = mixins.methods.getPdfDocumentHeader(pdfFile, dentalLaboratory, pdfFontNormal, pdfMargin)
  pdfCurrentPositionY = mixins.methods.getPositionAfterHeader(dentalLaboratory, 42, false)

  pdfFile.setFontSize(pdfFontSize)
  pdfFile.setFont(pdfFontBold)
  mixins.methods.setActTitle(pdfFile, dentalLaboratory, purchaseOrder, pdfWidth, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  if (dentalLaboratory.city) {
    pdfCurrentPositionY += pdfRowHeight
    pdfFile.text(
      'Место составления ' + dentalLaboratory.city, pdfWidth - pdfMargin, pdfCurrentPositionY, 'right'
    )
  }

  pdfCurrentPositionY += (pdfRowHeight * 2)

  let rightDataPosition = pdfWidth / 5
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('Дата:', pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  let stringDate = moment(parseInt(purchaseOrder.act_creation_date)).format('L')
  pdfFile.text(stringDate, rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(
    rightDataPosition,
    pdfCurrentPositionY + 1,
    rightDataPosition + getTextWidth(pdfFile, stringDate),
    pdfCurrentPositionY + 1,
  )

  if (!actContractNumberHidden()) {
    pdfFile.setFont(pdfFontBold)
    let actText = 'Договор №:  '
    pdfFile.text(actText, pdfWidth / 2, pdfCurrentPositionY, 'center')
    pdfFile.setFont(pdfFontNormal)
    const contractNumber = mixins.methods.getActContractNumber(purchaseOrder, patient)
    const contractNumberWidth = getTextWidth(pdfFile, contractNumber)
    const actTextWidth = getTextWidth(pdfFile, actText)
    pdfFile.text(contractNumber, (pdfWidth / 2) + (actTextWidth / 2) + 1, pdfCurrentPositionY)
    pdfFile.line(
      (pdfWidth / 2) + (actTextWidth / 2),
      pdfCurrentPositionY + 1,
      (pdfWidth / 2) + ((actTextWidth / 2) + contractNumberWidth + 2),
      pdfCurrentPositionY + 1
    )
  }

  pdfFile.setFont(pdfFontBold)
  pdfCurrentPositionY += pdfRowHeight

  pdfFile.text('Заказчик:', pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(client.name ? client.name : '', rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)

  if (client.address) {
    pdfFile.setFont(pdfFontBold)
    pdfCurrentPositionY += pdfRowHeight

    pdfFile.text('Адрес:', pdfMargin, pdfCurrentPositionY)

    pdfFile.setFont(pdfFontNormal)
    pdfFile.text(client.address, rightDataPosition, pdfCurrentPositionY)
    pdfFile.line(
      rightDataPosition,
      pdfCurrentPositionY + 1,
      pdfWidth - pdfMargin,
      pdfCurrentPositionY + 1
    )
  }
  pdfCurrentPositionY += pdfRowHeight

  if (purchaseOrder.doctor) {
    pdfFile.setFont(pdfFontBold)
    pdfFile.text('Ф.И.О врача:', pdfMargin, pdfCurrentPositionY)
    pdfFile.setFont(pdfFontNormal)
    pdfFile.text(purchaseOrder.doctor.full_name + '', rightDataPosition, pdfCurrentPositionY)
    pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)
    pdfCurrentPositionY += pdfRowHeight
  }
  pdfCurrentPositionY += pdfRowHeight

  // Table services title
  const title = getServicesTableTitleForActOfServicesRendered(dentalLaboratory.name)
  pdfCurrentPositionY = textToRows(
    title, null, pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )
  pdfFile.setLineHeightFactor(1)

  let rows = [
    [
      {content: 'Номер', styles: {halign: 'center'}},
      {content: 'Наименование', styles: {halign: 'center'}},
      {content: 'Кол', styles: {halign: 'center'}},
      {content: 'Цена', styles: {halign: 'center'}},
      {content: 'Сумма', styles: {halign: 'center'}}
    ]
  ]

  purchaseOrder.services_history.forEach(s => {
    rows.push([
      {content: s.number, styles: {halign: 'center'}},
      {content: s.name},
      {content: s.count, styles: {halign: 'center'}},
      {content: mixins.methods.formatPrice(s.total_price, true), styles: {halign: 'center'}},
      {content: mixins.methods.formatPrice(s.total_price * s.count, true), styles: {halign: 'center'}}
    ])
  })
  let totalPrice = purchaseOrder.services_history.reduce(
    (total, s) => total + parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false)), 0
  )
  totalPrice = parseFloat(mixins.methods.formatPrice(totalPrice, false))
  const discountAmount = purchaseOrder.services_history.length
    ? getPaymentDiscountAmountByTreatmentDiaryServices(
      purchaseOrder.services_history, purchaseOrder.calc_discount_percent
    )
    : (purchaseOrder.calc_discount_percent / 100) * totalPrice
  let salePrice = purchaseOrder.calc_discount_percent ? (totalPrice - discountAmount) : totalPrice
  salePrice = salePrice < 0 ? 0 : salePrice
  rows.push([
    {content: 'Итого', styles: {halign: 'left'}, colSpan: 4},
    {content: mixins.methods.formatPrice(totalPrice, true), styles: { halign: 'center' }}
  ])
  pdfFile.autoTable({
    theme: 'grid',
    startY: pdfCurrentPositionY,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: pdfFontSize,
    },
    didParseCell: hook => {
      if (hook.row.index === 0) {
        hook.cell.styles.font = pdfFontBold
      }
    },
    didDrawPage: hook => {
      pdfCurrentPositionY = (hook.cursor.y + pdfRowHeight)
    },
  })

  let text = 'Вышеперечисленные услуги выполнены полностью. Заказчик по объему, качеству и срокам' +
    ' оказания услуг претензий не имеет. Настоящий акт является основанием к оплате оказанных услуг.'
  pdfCurrentPositionY = textToRows(
    text, '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )
  pdfCurrentPositionY += pdfRowHeight
  if (pdfHeight - pdfRowHeight < pdfCurrentPositionY) {
    pdfFile.addPage()
    pdfCurrentPositionY = 10
  }
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('ИТОГО', pdfMargin, pdfCurrentPositionY)
  pdfCurrentPositionY += pdfRowHeight

  let totalText = 'Начислено:   '
  pdfFile.text(totalText, pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)

  pdfFile.text(
    mixins.methods.toFixed(totalPrice, true), pdfMargin + getTextWidth(pdfFile, totalText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let discountText = 'Скидка %:   '
  pdfFile.text(discountText, (pdfWidth / 4) + 4, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    purchaseOrder.calc_discount_percent ? purchaseOrder.calc_discount_percent.toString() : '0',
    (pdfWidth / 4) + 4 + getTextWidth(pdfFile, discountText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let payText = 'К оплате:   '
  pdfFile.text(payText, pdfWidth / 2, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    (salePrice > 0 && totalPrice) || purchaseOrder.calc_discount_percent === 100
      ? mixins.methods.toFixed(salePrice, true)
      : mixins.methods.toFixed(totalPrice, true),
    (pdfWidth / 2) + getTextWidth(pdfFile, payText),
    pdfCurrentPositionY
  )

  pdfCurrentPositionY += (pdfRowHeight * 2)
  const doctorInitials = actFillSignerDoctorName() && purchaseOrder.doctor
    ? purchaseOrder.doctor.full_name_initials : null
  addSignerBlockToPurchaseOrderAct(
    pdfFile, pdfCurrentPositionY, pdfWidth, pdfMargin, pdfRowHeight, pdfFontBold, pdfFontNormal, doctorInitials
  )

  pdfFile.save(mixins.methods.getActTitle(purchaseOrder) + ' - ' + client.name + '.pdf')
}

export const createActOfComplexServicesRenderedForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования акта оказанных комплексов услуг. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const patient = purchaseOrder.patient
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const pdfMargin = 8
  const pdfFontSize = 10
  const pdfRowHeight = 7
  const pdfWidth = 210
  const pdfHeight = 297
  const lineHeightFactor = 1.5

  let pdfCurrentPositionY = 0
  let pdfFile = new jsPDF({orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true})

  // header
  pdfFile = mixins.methods.getPdfDocumentHeader(pdfFile, dentalLaboratory, pdfFontNormal, pdfMargin)
  pdfCurrentPositionY = mixins.methods.getPositionAfterHeader(dentalLaboratory, 42, false)

  pdfFile.setFontSize(pdfFontSize)
  let patientName = patient.full_name

  pdfFile.setFont(pdfFontBold)
  mixins.methods.setActTitle(pdfFile, dentalLaboratory, purchaseOrder, pdfWidth, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  if (dentalLaboratory.city) {
    pdfCurrentPositionY += pdfRowHeight
    pdfFile.text(
      'Место составления ' + dentalLaboratory.city, pdfWidth - pdfMargin, pdfCurrentPositionY, 'right'
    )
  }

  pdfCurrentPositionY += (pdfRowHeight * 2)

  let rightDataPosition = pdfWidth / 5
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('Дата:', pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  let stringDate = moment(parseInt(purchaseOrder.act_creation_date)).format('L')
  pdfFile.text(stringDate, rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(
    rightDataPosition,
    pdfCurrentPositionY + 1,
    rightDataPosition + getTextWidth(pdfFile, stringDate),
    pdfCurrentPositionY + 1,
  )

  if (!actContractNumberHidden()) {
    pdfFile.setFont(pdfFontBold)
    let actText = 'Договор №:  '
    pdfFile.text(actText, pdfWidth / 2, pdfCurrentPositionY, 'center')
    pdfFile.setFont(pdfFontNormal)
    const contractNumber = mixins.methods.getActContractNumber(purchaseOrder, patient)
    const contractNumberWidth = getTextWidth(pdfFile, contractNumber)
    const actTextWidth = getTextWidth(pdfFile, actText)
    pdfFile.text(contractNumber, (pdfWidth / 2) + (actTextWidth / 2) + 1, pdfCurrentPositionY)
    pdfFile.line(
      (pdfWidth / 2) + (actTextWidth / 2),
      pdfCurrentPositionY + 1,
      (pdfWidth / 2) + ((actTextWidth / 2) + contractNumberWidth + 2),
      pdfCurrentPositionY + 1
    )
  }

  pdfFile.setFont(pdfFontBold)
  pdfCurrentPositionY += pdfRowHeight

  pdfFile.text('Заказчик:', pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)

  pdfFile.text(patientName, rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)

  pdfFile.setFont(pdfFontBold)
  pdfCurrentPositionY += pdfRowHeight

  pdfFile.text('Адрес:', pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(patient.address ? patient.address : '', rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)
  pdfCurrentPositionY += pdfRowHeight

  if (purchaseOrder.doctor) {
    pdfFile.setFont(pdfFontBold)
    pdfFile.text('Ф.И.О врача:', pdfMargin, pdfCurrentPositionY)
    pdfFile.setFont(pdfFontNormal)
    pdfFile.text(purchaseOrder.doctor.full_name + '', rightDataPosition, pdfCurrentPositionY)
    pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)
    pdfCurrentPositionY += pdfRowHeight
  }
  pdfCurrentPositionY += pdfRowHeight

  // Table services title
  const title = getServicesTableTitleForActOfServicesRendered(dentalLaboratory.name)
  pdfCurrentPositionY = textToRows(
    title, null, pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )
  pdfFile.setLineHeightFactor(1)

  const additionalServices = []
  const treatmentPlansTableData = {}
  purchaseOrder.services_history.map(s => {
    if (s.complex) {
      const complex = s.complex
      if (complex.id in treatmentPlansTableData) {
        treatmentPlansTableData[complex.id].amount +=
          parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false))
      } else {
        treatmentPlansTableData[complex.id] = {
          number: complex.number,
          name: complex.name,
          count: complex.count,
          price: complex.price,
          amount: parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false))
        }
      }
    } else {
      additionalServices.push(s)
    }
  })

  pdfCurrentPositionY += pdfRowHeight / 2
  pdfCurrentPositionY = textToRows(
    'Комплексы услуг:', '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )

  const tablesHeader = [
    {content: 'Номер', styles: {halign: 'center'}},
    {content: 'Наименование', styles: {halign: 'center'}},
    {content: 'Количество', styles: {halign: 'center'}},
    {content: 'Цена', styles: {halign: 'center'}},
    {content: 'Сумма', styles: {halign: 'center'}}
  ]
  let rows = [tablesHeader]

  let totalPlansPrice = 0
  Object.values(treatmentPlansTableData).map(plan => {
    totalPlansPrice += plan.amount
    rows.push([
      {content: plan.number, styles: {halign: 'center'}},
      {content: plan.name, styles: {halign: 'left'}},
      {content: plan.count, styles: {halign: 'center'}},
      {content: mixins.methods.formatPrice(plan.price, true), styles: {halign: 'center'}},
      {content: mixins.methods.formatPrice(plan.amount, true), styles: {halign: 'center'}},
    ])
  })

  rows.push([
    {content: 'Итого', styles: {halign: 'left'}, colSpan: 4},
    {
      content: mixins.methods.formatPrice(totalPlansPrice, true), styles: { halign: 'center' }
    }
  ])
  let totalPrice = purchaseOrder.services_history.reduce(
    (total, s) => total + parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false)), 0
  )
  totalPrice = parseFloat(mixins.methods.formatPrice(totalPrice, false))
  const discountAmount = purchaseOrder.services_history.length
    ? getPaymentDiscountAmountByTreatmentDiaryServices(
      purchaseOrder.services_history, purchaseOrder.calc_discount_percent
    )
    : (purchaseOrder.calc_discount_percent / 100) * totalPrice
  let salePrice = purchaseOrder.calc_discount_percent ? (totalPrice - discountAmount) : totalPrice
  salePrice = salePrice < 0 ? 0 : salePrice
  pdfFile.setLineHeightFactor(1)
  pdfFile.autoTable({
    theme: 'grid',
    startY: pdfCurrentPositionY - pdfRowHeight / 2,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: pdfFontSize,
    },
    didParseCell: hook => {
      if (hook.row.index === 0) {
        hook.cell.styles.font = pdfFontBold
      }
    },
    didDrawPage: hook => {
      pdfCurrentPositionY = (hook.cursor.y + pdfRowHeight)
    },
  })

  if (additionalServices.length) {
    pdfCurrentPositionY = textToRows(
      'Дополнительные услуги:', '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true,
      lineHeightFactor
    )
    rows = [tablesHeader]

    let totalServicesPrice = 0
    additionalServices.map(s => {
      totalServicesPrice += s.total_price * s.count
      rows.push([
        {content: s.number, styles: {halign: 'center'}},
        {content: s.name},
        {content: s.count, styles: {halign: 'center'}},
        {content: mixins.methods.formatPrice(s.total_price, true), styles: {halign: 'center'}},
        {
          content: mixins.methods.formatPrice(s.total_price * s.count, true),
          styles: {halign: 'center'}
        }
      ])
    })

    rows.push([
      {content: 'Итого', styles: {halign: 'left'}, colSpan: 4},
      {content: mixins.methods.formatPrice(totalServicesPrice, true), styles: { halign: 'center' }}
    ])

    pdfFile.setLineHeightFactor(1)
    pdfFile.autoTable({
      theme: 'grid',
      startY: pdfCurrentPositionY - pdfRowHeight / 2,
      margin: pdfMargin,
      body: rows,
      bodyStyles: {
        cellPadding: 1,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      styles: {
        font: pdfFontNormal,
        fontSize: pdfFontSize,
      },
      didParseCell: hook => {
        if (hook.row.index === 0) {
          hook.cell.styles.font = pdfFontBold
        }
      },
      didDrawPage: hook => {
        pdfCurrentPositionY = (hook.cursor.y + pdfRowHeight)
      },
    })
  }

  let text = 'Вышеперечисленные услуги выполнены полностью. Заказчик по объему, качеству и срокам' +
    ' оказания услуг претензий не имеет. Настоящий акт является основанием к оплате оказанных услуг.'
  pdfCurrentPositionY = textToRows(
    text, '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )
  pdfCurrentPositionY += pdfRowHeight
  if (pdfHeight - pdfRowHeight < pdfCurrentPositionY) {
    pdfFile.addPage()
    pdfCurrentPositionY = 10
  }
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('ИТОГО', pdfMargin, pdfCurrentPositionY)
  pdfCurrentPositionY += pdfRowHeight

  let totalText = 'Начислено:   '
  pdfFile.text(totalText, pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)

  pdfFile.text(
    mixins.methods.toFixed(totalPrice, true),
    pdfMargin + getTextWidth(pdfFile, totalText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let discountText = 'Скидка %:   '
  pdfFile.text(discountText, (pdfWidth / 4) + 4, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    purchaseOrder.calc_discount_percent ? purchaseOrder.calc_discount_percent.toString() : '0',
    (pdfWidth / 4) + 4 + getTextWidth(pdfFile, discountText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let payText = 'К оплате:   '
  pdfFile.text(payText, pdfWidth / 2, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    (salePrice > 0 && totalPrice) || purchaseOrder.calc_discount_percent === 100
      ? mixins.methods.toFixed(salePrice, true)
      : mixins.methods.toFixed(totalPrice, true),
    (pdfWidth / 2) + getTextWidth(pdfFile, payText),
    pdfCurrentPositionY
  )

  pdfCurrentPositionY += (pdfRowHeight * 2)
  const doctorInitials = actFillSignerDoctorName() && purchaseOrder.doctor
    ? purchaseOrder.doctor.full_name_initials : null
  addSignerBlockToPurchaseOrderAct(
    pdfFile, pdfCurrentPositionY, pdfWidth, pdfMargin, pdfRowHeight, pdfFontBold, pdfFontNormal, doctorInitials
  )

  pdfFile.save(
    mixins.methods.getActTitle(purchaseOrder) + ' (комплексы услуг) - ' + patient.full_name + '.pdf'
  )
}

export const createActOfServicesRenderedShortForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования акта оказанных услуг. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const patient = purchaseOrder.patient
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const pdfMargin = 8
  const pdfFontSize = 10
  const pdfRowHeight = 7
  const pdfWidth = 210
  const lineHeightFactor = 1.5

  let pdfCurrentPositionY = 0
  let pdfFile = new jsPDF({orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true})

  // header
  pdfFile = mixins.methods.getPdfDocumentHeader(pdfFile, dentalLaboratory, pdfFontNormal, pdfMargin)
  pdfCurrentPositionY = mixins.methods.getPositionAfterHeader(dentalLaboratory, 42, false)

  pdfFile.setFontSize(pdfFontSize)
  let patientName = patient.full_name

  pdfFile.setFont(pdfFontBold)
  mixins.methods.setActTitle(pdfFile, dentalLaboratory, purchaseOrder, pdfWidth, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  if (dentalLaboratory.city) {
    pdfCurrentPositionY += pdfRowHeight
    pdfFile.text(
      'Место составления ' + dentalLaboratory.city, pdfWidth - pdfMargin, pdfCurrentPositionY, 'right'
    )
    pdfCurrentPositionY += pdfRowHeight
  } else {
    pdfCurrentPositionY += (pdfRowHeight * 2)
  }

  let rightDataPosition = pdfWidth / 5
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('Дата:', pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  let stringDate = moment(parseInt(purchaseOrder.act_creation_date)).format('L')
  pdfFile.text(stringDate, rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(
    rightDataPosition,
    pdfCurrentPositionY + 1,
    rightDataPosition + getTextWidth(pdfFile, stringDate),
    pdfCurrentPositionY + 1,
  )

  if (!actContractNumberHidden()) {
    pdfFile.setFont(pdfFontBold)
    let actText = 'Договор №:  '
    pdfFile.text(actText, pdfWidth / 2, pdfCurrentPositionY, 'center')
    pdfFile.setFont(pdfFontNormal)
    const contractNumber = mixins.methods.getActContractNumber(purchaseOrder, patient)
    const contractNumberWidth = getTextWidth(pdfFile, contractNumber)
    const actTextWidth = getTextWidth(pdfFile, actText)
    pdfFile.text(contractNumber, (pdfWidth / 2) + (actTextWidth / 2) + 1, pdfCurrentPositionY)
    pdfFile.line(
      (pdfWidth / 2) + (actTextWidth / 2),
      pdfCurrentPositionY + 1,
      (pdfWidth / 2) + ((actTextWidth / 2) + contractNumberWidth + 2),
      pdfCurrentPositionY + 1
    )
  }

  pdfFile.setFont(pdfFontBold)
  pdfCurrentPositionY += pdfRowHeight

  pdfFile.text('Заказчик:', pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)

  pdfFile.text(patientName, rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)

  pdfFile.setFont(pdfFontBold)
  pdfCurrentPositionY += pdfRowHeight

  pdfFile.text('Адрес:', pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(patient.address ? patient.address : '', rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)
  pdfCurrentPositionY += pdfRowHeight

  if (purchaseOrder.doctor) {
    pdfFile.setFont(pdfFontBold)
    pdfFile.text('Ф.И.О врача:', pdfMargin, pdfCurrentPositionY)
    pdfFile.setFont(pdfFontNormal)
    pdfFile.text(purchaseOrder.doctor.full_name + '', rightDataPosition, pdfCurrentPositionY)
    pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)
    pdfCurrentPositionY += pdfRowHeight
  }

  // Table services title
  const title = getServicesTableTitleForActOfServicesRendered(dentalLaboratory.name)
  pdfCurrentPositionY = textToLines(
    title, null, pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true,
    lineHeightFactor, false
  )
  pdfFile.setLineHeightFactor(1)

  let rows = [[
    {content: 'Услуги', styles: {halign: 'center'}},
    {content: 'Сумма', styles: {halign: 'center'}}
  ]]

  let servicesNumbers = []
  let totalPrice = purchaseOrder.services_history.reduce(
    (totalPrice, s) =>
    {
      servicesNumbers.push(s.number)
      return totalPrice + parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false))
    },
    0
  )
  rows.push([
    {content: servicesNumbers.sort().join(', ')},
    {content: mixins.methods.formatPrice(totalPrice, true), styles: {halign: 'center', valign: 'middle'}}
  ])
  totalPrice = parseFloat(mixins.methods.formatPrice(totalPrice, false))
  const discountAmount = purchaseOrder.services_history.length
    ? getPaymentDiscountAmountByTreatmentDiaryServices(
      purchaseOrder.services_history, purchaseOrder.calc_discount_percent
    )
    : (purchaseOrder.calc_discount_percent / 100) * totalPrice
  let salePrice = purchaseOrder.calc_discount_percent ? (totalPrice - discountAmount) : totalPrice
  salePrice = salePrice < 0 ? 0 : salePrice
  pdfFile.autoTable({
    theme: 'grid',
    startY: pdfCurrentPositionY - pdfRowHeight / 2,
    body: rows,
    margin: pdfMargin,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: pdfFontSize,
    },
    didParseCell: hook => {
      if (hook.row.index === 0) {
        hook.cell.styles.font = pdfFontBold
      }
    },
    didDrawPage: hook => {
      pdfCurrentPositionY = hook.cursor.y
    },
  })

  pdfCurrentPositionY += pdfRowHeight
  let text = 'Вышеперечисленные услуги выполнены полностью. Заказчик по объему, качеству и срокам' +
    ' оказания услуг претензий не имеет. Настоящий акт является основанием к оплате оказанных услуг.'
  pdfCurrentPositionY = textToRows(
    text, '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )
  pdfCurrentPositionY += 2
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('ИТОГО', pdfMargin, pdfCurrentPositionY)
  pdfCurrentPositionY += pdfRowHeight

  let totalText = 'Начислено:   '
  pdfFile.text(totalText, pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)

  pdfFile.text(
    mixins.methods.toFixed(totalPrice, true),
    pdfMargin + getTextWidth(pdfFile, totalText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let discountText = 'Скидка %:   '
  pdfFile.text(discountText, (pdfWidth / 4) + 4, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    purchaseOrder.calc_discount_percent ? purchaseOrder.calc_discount_percent.toString() : '0',
    (pdfWidth / 4) + 4 + getTextWidth(pdfFile, discountText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let payText = 'К оплате:   '
  pdfFile.text(payText, pdfWidth / 2, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    (salePrice > 0 && totalPrice) || purchaseOrder.calc_discount_percent === 100
      ? mixins.methods.toFixed(salePrice, true)
      : mixins.methods.toFixed(totalPrice, true),
    (pdfWidth / 2) + getTextWidth(pdfFile, payText),
    pdfCurrentPositionY
  )

  pdfCurrentPositionY += pdfRowHeight
  const doctorInitials = actFillSignerDoctorName() && purchaseOrder.doctor
    ? purchaseOrder.doctor.full_name_initials : null
  addSignerBlockToPurchaseOrderAct(
    pdfFile, pdfCurrentPositionY, pdfWidth, pdfMargin, pdfRowHeight, pdfFontBold, pdfFontNormal, doctorInitials
  )

  pdfFile.save(
    mixins.methods.getActTitle(purchaseOrder) + ' (сокращенный формат) - ' + patient.full_name + '.pdf'
  )
}

export const createActOfServicesRenderedWithMaterialsForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования акта оказанных услуг. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const patient = purchaseOrder.patient
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const pdfMargin = 8
  const pdfFontSize = 10
  const pdfRowHeight = 7
  const pdfWidth = 210
  const pdfHeight = 297
  const priceCalcSystem = mixins.methods.getMaterialPriceCalcSystem()
  const lineHeightFactor = 1.5

  let pdfCurrentPositionY = 0
  let pdfFile = new jsPDF({orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true})

  // header
  pdfFile = mixins.methods.getPdfDocumentHeader(pdfFile, dentalLaboratory, pdfFontNormal, pdfMargin)
  pdfCurrentPositionY = mixins.methods.getPositionAfterHeader(dentalLaboratory, 42, false)

  pdfFile.setFontSize(pdfFontSize)
  let patientName = patient.full_name

  pdfFile.setFont(pdfFontBold)
  mixins.methods.setActTitle(pdfFile, dentalLaboratory, purchaseOrder, pdfWidth, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  if (dentalLaboratory.city) {
    pdfCurrentPositionY += pdfRowHeight
    pdfFile.text(
      'Место составления ' + dentalLaboratory.city, pdfWidth - pdfMargin, pdfCurrentPositionY, 'right'
    )
  }

  pdfCurrentPositionY += (pdfRowHeight * 2)

  let rightDataPosition = pdfWidth / 5
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('Дата:', pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)

  let stringDate = moment(parseInt(purchaseOrder.act_creation_date)).format('L')
  pdfFile.text(stringDate, rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(
    rightDataPosition,
    pdfCurrentPositionY + 1,
    rightDataPosition + getTextWidth(pdfFile, stringDate),
    pdfCurrentPositionY + 1,
  )

  if (!actContractNumberHidden()) {
    pdfFile.setFont(pdfFontBold)
    let actText = 'Договор №:  '
    pdfFile.text(actText, pdfWidth / 2, pdfCurrentPositionY, 'center')
    pdfFile.setFont(pdfFontNormal)
    const contractNumber = mixins.methods.getActContractNumber(purchaseOrder, patient)
    const contractNumberWidth = getTextWidth(pdfFile, contractNumber)
    const actTextWidth = getTextWidth(pdfFile, actText)
    pdfFile.text(contractNumber, (pdfWidth / 2) + (actTextWidth / 2) + 1, pdfCurrentPositionY)
    pdfFile.line(
      (pdfWidth / 2) + (actTextWidth / 2),
      pdfCurrentPositionY + 1,
      (pdfWidth / 2) + ((actTextWidth / 2) + contractNumberWidth + 2),
      pdfCurrentPositionY + 1
    )
  }

  pdfFile.setFont(pdfFontBold)
  pdfCurrentPositionY += pdfRowHeight

  pdfFile.text('Заказчик:', pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(patientName, rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)

  pdfFile.setFont(pdfFontBold)
  pdfCurrentPositionY += pdfRowHeight

  pdfFile.text('Адрес:', pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(patient.address ? patient.address : '', rightDataPosition, pdfCurrentPositionY)
  pdfFile.line(
    rightDataPosition,
    pdfCurrentPositionY + 1,
    pdfWidth - pdfMargin,
    pdfCurrentPositionY + 1
  )
  pdfCurrentPositionY += pdfRowHeight

  if (purchaseOrder.doctor) {
    pdfFile.setFont(pdfFontBold)
    pdfFile.text('Ф.И.О врача:', pdfMargin, pdfCurrentPositionY)
    pdfFile.setFont(pdfFontNormal)
    pdfFile.text(purchaseOrder.doctor.full_name + '', rightDataPosition, pdfCurrentPositionY)
    pdfFile.line(rightDataPosition, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)
    pdfCurrentPositionY += pdfRowHeight
  }

  pdfCurrentPositionY += pdfRowHeight

  // Table services title
  const title = getServicesTableTitleForActOfServicesRendered(dentalLaboratory.name)
  pdfCurrentPositionY = textToRows(
    title, null, pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )
  pdfFile.setLineHeightFactor(1)

  let rows = [
    [
      {content: 'Номер', styles: {halign: 'center'}},
      {content: 'Наименование', styles: {halign: 'center'}},
      {content: 'Кол', styles: {halign: 'center'}},
      {content: 'Цена', styles: {halign: 'center'}},
      {content: 'Сумма', styles: {halign: 'center'}}
    ]
  ]

  let totalMaterialsPrice = 0
  let totalMaterialsNds10Price = 0
  let totalMaterialsNds20Price = 0
  let totalMaterialsPriceWithNds = 0
  const materials = {}
  purchaseOrder.services_history.forEach(s => {
    s.materials_history.map(m => {
      const totalPrice = m.price * s.count * m.part_rate
      let ndsPrice
      if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
        ndsPrice = (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
      } else {
        ndsPrice = m.nds ? (totalPrice / 100) * m.nds : null
      }
      const totalPriceWithNds = ndsPrice ? totalPrice + ndsPrice : totalPrice

      if (materials[m.id]) {
        materials[m.id].count += s.count * m.part_rate
      } else {
        materials[m.id] = {
          name: m.name,
          sku: m.sku,
          count: s.count * m.part_rate,
          price: m.price,
          price_per_unit: m.price_per_unit,
          price_per_unit_without_nds: m.price_per_unit_without_nds,
          nds: m.nds,
          unitType: m.unit_type,
        }
      }

      if (m.nds === 10) {
        totalMaterialsNds10Price += ndsPrice
      }
      if (m.nds === 20) {
        totalMaterialsNds20Price += ndsPrice
      }
      totalMaterialsPrice += totalPrice
      totalMaterialsPriceWithNds += totalPriceWithNds
    })
    totalMaterialsNds10Price = parseFloat(
      mixins.methods.formatPrice(totalMaterialsNds10Price, false)
    )
    totalMaterialsNds20Price = parseFloat(
      mixins.methods.formatPrice(totalMaterialsNds20Price, false)
    )
    totalMaterialsPriceWithNds = parseFloat(
      mixins.methods.formatPrice(totalMaterialsPriceWithNds, false)
    )
    rows.push([
      {content: s.number, styles: {halign: 'center'}},
      {content: s.name},
      {content: s.count, styles: {halign: 'center'}},
      {content: mixins.methods.formatPrice(s.total_price, true), styles: {halign: 'center'}},
      {
        content: mixins.methods.formatPrice(s.total_price * parseFloat(s.count), true),
        styles: {halign: 'center'}
      }
    ])
  })
  let totalPrice = purchaseOrder.services_history.reduce(
    (total, s) => total + parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false)), 0
  )
  totalPrice = parseFloat(mixins.methods.formatPrice(totalPrice, false))
  const discountAmount = purchaseOrder.services_history.length
    ? getPaymentDiscountAmountByTreatmentDiaryServices(
      purchaseOrder.services_history, purchaseOrder.calc_discount_percent
    )
    : (purchaseOrder.calc_discount_percent / 100) * totalPrice
  let salePrice = purchaseOrder.calc_discount_percent ? (totalPrice - discountAmount) : totalPrice
  salePrice = salePrice < 0 ? 0 : salePrice
  rows.push([
    {content: 'Итого', styles: {halign: 'left'}, colSpan: 4},
    {content: mixins.methods.formatPrice(totalPrice, true), styles: { halign: 'center' }}
  ])
  pdfFile.autoTable({
    theme: 'grid',
    startY: pdfCurrentPositionY,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: pdfFontSize,
    },
    didParseCell: hook => {
      if (hook.row.index === 0) {
        hook.cell.styles.font = pdfFontBold
      }
    },
    didDrawPage: hook => {
      pdfCurrentPositionY = (hook.cursor.y + pdfRowHeight)
    },
  })

  if (Object.keys(materials).length) {
    pdfFile.text('Использованные материалы:', pdfMargin, pdfCurrentPositionY)
    rows = [
      [
        {content: 'Артикул', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Наименование', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Стоимость без НДС', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Количество', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Ед. измерения', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Итого без НДС', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Ставка НДС', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Сумма НДС', styles: {halign: 'center', valign: 'middle'}},
        {content: 'Итого с НДС', styles: {halign: 'center', valign: 'middle'}}
      ]
    ]

    Object.keys(materials).map(key => {
      const material = materials[key]
      const nds = material.nds ? material.nds : null
      const totalPrice = material.price * material.count
      let ndsPrice
      if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
        ndsPrice = (material.price_per_unit - material.price_per_unit_without_nds) * material.count
      } else {
        ndsPrice = nds ? (totalPrice / 100) * nds : null
      }
      const totalPriceWithNds = ndsPrice ? totalPrice + ndsPrice : totalPrice
      let row = [
        {content: material.sku, styles: {halign: 'center', valign: 'middle'}},
        {content: material.name, styles: {halign: 'center', valign: 'middle'}},
        {
          content: mixins.methods.formatPrice(material.price, true),
          styles: {halign: 'center', valign: 'middle'}
        },
        {content: material.count, styles: {halign: 'center', valign: 'middle'}},
        {content: material.unitType ? material.unitType : '', styles: {halign: 'center', valign: 'middle'}},
        {
          content: mixins.methods.formatPrice(totalPrice, true),
          styles: {halign: 'center', valign: 'middle'}
        },
        {content: nds ? `${nds}%` : '0%', styles: {halign: 'center', valign: 'middle'}},
        {content: mixins.methods.formatPrice(ndsPrice, true), styles: {halign: 'center', valign: 'middle'}},
        {
          content: mixins.methods.formatPrice(totalPriceWithNds, true),
          styles: {halign: 'center', valign: 'middle'}
        },
      ]
      rows.push(row)
    })

    pdfFile.autoTable({
      theme: 'grid',
      startY: pdfCurrentPositionY + (pdfRowHeight / 2),
      margin: pdfMargin,
      body: rows,
      bodyStyles: {
        cellPadding: 1,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      styles: {
        font: pdfFontNormal,
        fontSize: pdfFontSize,
      },
      didParseCell: hook => {
        if (hook.row.index === 0) {
          hook.cell.styles.font = pdfFontBold
        }
      },
      didDrawPage: hook => {
        pdfCurrentPositionY = (hook.cursor.y + pdfRowHeight)
      },
    })
  }

  if (pdfHeight - (pdfRowHeight * 2) < pdfCurrentPositionY) {
    pdfFile.addPage()
    pdfCurrentPositionY = pdfRowHeight * 2
  }

  if (Object.keys(materials).length) {
    pdfCurrentPositionY = textToRows(
      'Стоимость материалов:', '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontBold,
      true, lineHeightFactor
    )
    pdfCurrentPositionY = textToRows(
      `Итого сумма без НДС: ${ mixins.methods.formatPrice(totalMaterialsPrice, true) }`,
      '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
    )
    pdfCurrentPositionY = textToRows(
      `в т.ч. сумма НДС 10%: ${ mixins.methods.formatPrice(totalMaterialsNds10Price, true) }`,
      '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
    )
    pdfCurrentPositionY = textToRows(
      `в т.ч. сумма НДС 20%: ${ mixins.methods.formatPrice(totalMaterialsNds20Price, true) }`,
      '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
    )
    pdfCurrentPositionY = textToRows(
      `Итого сумма с учетом НДС: ${ mixins.methods.formatPrice(totalMaterialsPriceWithNds, true) }`,
      '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
    )
  }

  let text = 'Вышеперечисленные услуги выполнены полностью. Заказчик по объему, качеству и срокам' +
    ' оказания услуг претензий не имеет. Настоящий акт является основанием к оплате оказанных услуг.'
  pdfCurrentPositionY = textToRows(
    text, '', pdfFile, pdfMargin, pdfCurrentPositionY, pdfFontNormal, true, lineHeightFactor
  )
  pdfCurrentPositionY += pdfRowHeight
  if (pdfHeight - pdfRowHeight < pdfCurrentPositionY) {
    pdfFile.addPage()
    pdfCurrentPositionY = 10
  }
  pdfFile.setFont(pdfFontBold)
  pdfFile.text('ИТОГО', pdfMargin, pdfCurrentPositionY)
  pdfCurrentPositionY += pdfRowHeight

  let totalText = 'Начислено:   '
  pdfFile.text(totalText, pdfMargin, pdfCurrentPositionY)

  pdfFile.setFont(pdfFontNormal)

  pdfFile.text(
    mixins.methods.toFixed(totalPrice, true),
    pdfMargin + getTextWidth(pdfFile, totalText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let discountText = 'Скидка %:   '
  pdfFile.text(discountText, (pdfWidth / 4) + 4, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    purchaseOrder.calc_discount_percent ? purchaseOrder.calc_discount_percent.toString() : '0',
    (pdfWidth / 4) + 4 + getTextWidth(pdfFile, discountText),
    pdfCurrentPositionY
  )

  pdfFile.setFont(pdfFontBold)
  let payText = 'К оплате:   '
  pdfFile.text(payText, pdfWidth / 2, pdfCurrentPositionY)
  pdfFile.setFont(pdfFontNormal)
  pdfFile.text(
    (salePrice > 0 && totalPrice) || purchaseOrder.calc_discount_percent === 100
      ? mixins.methods.toFixed(salePrice, true)
      : mixins.methods.toFixed(totalPrice, true),
    (pdfWidth / 2) + getTextWidth(pdfFile, payText),
    pdfCurrentPositionY
  )

  pdfCurrentPositionY += (pdfRowHeight * 2)
  const doctorInitials = actFillSignerDoctorName() && purchaseOrder.doctor
    ? purchaseOrder.doctor.full_name_initials : null
  addSignerBlockToPurchaseOrderAct(
    pdfFile, pdfCurrentPositionY, pdfWidth, pdfMargin, pdfRowHeight, pdfFontBold, pdfFontNormal, doctorInitials
  )

  pdfFile.save(
    mixins.methods.getActTitle(purchaseOrder) + ' (с материалами) - ' + patient.full_name + '.pdf'
  )
}

export const createStatementOfWorkPerformedForMedicalServicesRenderedForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования акта выполненных работ по оказанным медицинским услугам.' +
      ' Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const priceCalcSystem = mixins.methods.getMaterialPriceCalcSystem()
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const patient = purchaseOrder.patient
  const pdfFont = 'Times-Roman'
  const pdfFontBold = 'bold'
  const pdfFontNormal = 'normal'
  const pdfLeftMargin = 16
  const pdfRightMargin = pdfLeftMargin / 2
  const pdfFontSize = 12
  const pdfRowHeight = 7
  const pdfWidth = 210

  let pdfCurrentPositionY = 0
  let pdfFile = new jsPDF({orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true})

  // header
  pdfFile = mixins.methods.getPdfDocumentHeader(pdfFile, dentalLaboratory, pdfFont, pdfLeftMargin)
  pdfCurrentPositionY = mixins.methods.getPositionAfterHeader(dentalLaboratory, 42, false)

  pdfFile.setFont(pdfFont, pdfFontNormal)
  pdfFile.setFontSize(pdfFontSize)
  pdfFile.text(
    'Акт выполненных работ по оказанным медицинским услугам',
    pdfWidth / 2,
    pdfCurrentPositionY,
    'center'
  )
  pdfCurrentPositionY += pdfRowHeight * 1.5
  pdfFile.text(dentalLaboratory.city ? dentalLaboratory.city : '', pdfLeftMargin, pdfCurrentPositionY)
  const date = moment(parseInt(purchaseOrder.act_creation_date)).format('ll')
  pdfFile.text(date, pdfWidth - pdfRightMargin - getTextWidth(pdfFile, date), pdfCurrentPositionY)

  pdfCurrentPositionY += pdfRowHeight * 1.5
  const patientInfo = [patient.full_name]
  if (patient.address) {
    patientInfo.push(patient.address)
  }

  if (
    patient.document_series
    && patient.document_number
    && patient.document_issued_by
    && patient.document_type === 'passport'
  ) {
    const passportInfo = [`${patient.document_series}${patient.document_number}`, patient.document_issued_by]
    patientInfo.push(`№ ${passportInfo.join(', ')}`)
  }
  let text = `${dentalLaboratory.name}, в соответствии с договором возмездного оказания медицинских услуг от `
    + `${moment(parseInt(purchaseOrder.act_creation_date)).format('L')} г., `
    + `были оказаны медицинские услуги пациенту ${patientInfo.join(', ')}, наименование медицинских услуг:`
  const splittedText = pdfFile.splitTextToSize(text, pdfWidth - (pdfLeftMargin + pdfRightMargin))
  const splittedTextItemsCount = splittedText.length
  pdfFile.text(splittedText, pdfLeftMargin, pdfCurrentPositionY)
  pdfCurrentPositionY += (pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor) * splittedTextItemsCount

  text = purchaseOrder.services_history.map(service => service.name).join(', ')
  text = mixins.methods.capitalizeFirstLetter(text)
  if (getTextWidth(pdfFile, text) > pdfWidth - (pdfLeftMargin + pdfRightMargin)) {
    const splittedText = pdfFile.splitTextToSize(text, pdfWidth - (pdfLeftMargin + pdfRightMargin))
    const splittedTextItemsCount = splittedText.length
    pdfFile.text(splittedText, pdfLeftMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += (pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor) * (splittedTextItemsCount - 1)
  } else {
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
  }
  pdfCurrentPositionY += pdfRowHeight * 1.5

  pdfFile.setFont(pdfFont, pdfFontBold)
  let totalPrice = purchaseOrder.services_history.reduce(
    (totalPrice, service) =>
      totalPrice + parseFloat(mixins.methods.formatPrice(service.total_price * service.count, false)),
    0
  )
  totalPrice = parseFloat(mixins.methods.formatPrice(totalPrice, false))
  const discountAmount = purchaseOrder.services_history.length
    ? getPaymentDiscountAmountByTreatmentDiaryServices(
      purchaseOrder.services_history, purchaseOrder.calc_discount_percent
    )
    : (purchaseOrder.calc_discount_percent / 100) * totalPrice
  let salePrice = purchaseOrder.calc_discount_percent ? (totalPrice - discountAmount) : totalPrice
  salePrice = salePrice < 0 ? 0 : salePrice
  text = `Выполнено работ на сумму с НДС: ${mixins.methods.toFixed(salePrice, false)}`
  pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
  let endTextPosition = pdfLeftMargin + getTextWidth(pdfFile, text)
  text = `(${mixins.methods.amountToHumanReadableAmount(salePrice)})`
  pdfFile.setFont(pdfFont, pdfFontNormal)
  let startTextPosition = endTextPosition + 1
  pdfFile.text(text, startTextPosition, pdfCurrentPositionY)
  pdfCurrentPositionY += 1
  pdfFile.line(pdfLeftMargin, pdfCurrentPositionY, pdfWidth - pdfRightMargin, pdfCurrentPositionY)

  pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
  text = 'Услуги без НДС (согласно ст.118 подп. 1.2  п.1 НК РБ)'
  pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)

  let totalServicesNds10Price = 0
  let totalServicesNds20Price = 0
  let totalMaterialsNds10Price = 0
  let totalMaterialsNds20Price = 0
  purchaseOrder.services_history.forEach(s => {
    const totalPrice = s.service_price * s.count
    const ndsPrice = s.nds ? (totalPrice / 100) * s.nds : null
    if (s.nds === 10) {
      totalServicesNds10Price += ndsPrice
    }
    if (s.nds === 20) {
      totalServicesNds20Price += ndsPrice
    }
    s.materials_history.map(m => {
      const totalPrice = m.price * s.count * m.part_rate
      const ndsPrice = m.nds ? (totalPrice / 100) * m.nds : null
      if (m.nds === 10) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds10Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
        } else {
          totalMaterialsNds10Price += ndsPrice
        }
      }
      if (m.nds === 20) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds20Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
        } else {
          totalMaterialsNds20Price += ndsPrice
        }
      }
    })
    totalMaterialsNds10Price = parseFloat(mixins.methods.formatPrice(totalMaterialsNds10Price, false))
    totalMaterialsNds20Price = parseFloat(mixins.methods.formatPrice(totalMaterialsNds20Price, false))
  })

  if (totalMaterialsNds10Price || totalMaterialsNds20Price || totalServicesNds10Price || totalServicesNds20Price) {
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    text = 'В том числе НДС:'
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
  }

  if (totalMaterialsNds10Price || totalMaterialsNds20Price) {
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    text = 'на материалы'
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
  }

  if (totalMaterialsNds10Price) {
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    totalMaterialsNds10Price = totalMaterialsNds10Price.toFixed(2)
    text = `НДС 10%: ${totalMaterialsNds10Price} (${mixins.methods.amountToHumanReadableAmount(totalMaterialsNds10Price)})`
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfLeftMargin, pdfCurrentPositionY, pdfLeftMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
  }

  if (totalMaterialsNds20Price) {
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    totalMaterialsNds20Price = totalMaterialsNds20Price.toFixed(2)
    text = `НДС 20%: ${totalMaterialsNds20Price} (${mixins.methods.amountToHumanReadableAmount(totalMaterialsNds20Price)})`
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfLeftMargin, pdfCurrentPositionY, pdfLeftMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
  }

  if (totalServicesNds10Price || totalServicesNds20Price) {
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    text = 'на тариф'
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
  }

  if (totalServicesNds10Price) {
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    totalServicesNds10Price = totalServicesNds10Price.toFixed(2)
    text = `НДС 10%: ${totalServicesNds10Price} (${mixins.methods.amountToHumanReadableAmount(totalServicesNds10Price)})`
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfLeftMargin, pdfCurrentPositionY, pdfLeftMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
  }

  if (totalServicesNds20Price) {
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    totalServicesNds20Price = totalServicesNds20Price.toFixed(2)
    text = `НДС 20%: ${totalServicesNds20Price} (${mixins.methods.amountToHumanReadableAmount(totalServicesNds20Price)})`
    pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfLeftMargin, pdfCurrentPositionY, pdfLeftMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
  }

  pdfCurrentPositionY += pdfRowHeight * 1.5
  const doctor = purchaseOrder.doctor ? purchaseOrder.doctor.full_name_initials : ''
  text = `Врач оказавший услуги: ${doctor}`
  pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)
  pdfCurrentPositionY += 1
  const offset = purchaseOrder.doctor
    ? pdfLeftMargin + getTextWidth(pdfFile, text)
    : pdfLeftMargin + (getTextWidth(pdfFile, text) * 2)
  pdfFile.line(pdfLeftMargin, pdfCurrentPositionY, offset, pdfCurrentPositionY)

  pdfCurrentPositionY += pdfRowHeight * 1.5
  text = 'Качество оказанных услуг соответствует предъявленным требованиям, стороны претензий друг к'
  pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)

  pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
  text = 'другу не имеют.'
  pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)

  const pageCenter = pdfWidth / 2
  pdfCurrentPositionY += pdfRowHeight * 1.5
  text = 'Исполнитель'
  pdfFile.text(text, pdfLeftMargin, pdfCurrentPositionY)

  pdfCurrentPositionY += 1
  pdfFile.line(
    pdfLeftMargin + getTextWidth(pdfFile, text) + 10, pdfCurrentPositionY, pageCenter - 10, pdfCurrentPositionY
  )
  pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
  text = '(подпись)'
  pdfFile.text(text, pdfLeftMargin + getTextWidth(pdfFile, text) + 30, pdfCurrentPositionY)

  if (actMedicalServiceRenderedShowPatientSignature()) {
    pdfCurrentPositionY -= pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    pdfCurrentPositionY -= 1
    text = 'Заказчик'
    pdfFile.text(text, pageCenter, pdfCurrentPositionY)

    pdfCurrentPositionY += 1
    pdfFile.line(
      pageCenter + getTextWidth(pdfFile, text) + 10, pdfCurrentPositionY, pdfWidth - pdfLeftMargin - 10, pdfCurrentPositionY
    )
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    text = '(подпись)'
    pdfFile.text(text, pageCenter + getTextWidth(pdfFile, text) + 26, pdfCurrentPositionY)
  }

  pdfFile.save(
    'Акт выполненных работ по оказанным медицинским услугам - ' + patient.full_name + '.pdf'
  )
}

export const createPurchaseOrderMedicalServiceDocumentForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования документа. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const priceCalcSystem = mixins.methods.getMaterialPriceCalcSystem()
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const patient = purchaseOrder.patient
  let pdfFont = 'Times-Roman'
  const pdfFontBold = 'bold'
  const pdfFontNormal = 'normal'
  const pdfMargin = 8
  const pdfFontSize = 11
  const pdfRowHeight = 7
  const date = moment(parseInt(purchaseOrder.act_creation_date)).format('LL')
  let pdfFile = new jsPDF({orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true})
  const pdfHeight = pdfFile.internal.pageSize.height
  const pdfWidth = pdfFile.internal.pageSize.width
  let text = ''

  // header
  pdfFile = mixins.methods.getPdfDocumentHeader(pdfFile, dentalLaboratory, pdfFont, pdfMargin)
  let pdfCurrentPositionY = mixins.methods.getPositionAfterHeader(
    dentalLaboratory, 42, false
  )

  if (purchaseOrderShowServicesText()) {
    text = 'Медицинская услуга оказана на территории РБ'
    pdfFile.setFontSize(pdfFontSize)
    pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += pdfRowHeight * 1.5
  }

  pdfFont = 'Microsoft-Sans-Serif'
  pdfFile.setFont(pdfFont, pdfFontBold)
  pdfFile.setFontSize(13.5)
  pdfFile.text('Заказ-наряд', pdfWidth / 2, pdfCurrentPositionY, 'center')

  pdfFont = 'Times-Roman'
  pdfFile.setFont(pdfFont, pdfFontBold)
  pdfFile.setFontSize(pdfFontSize)

  pdfCurrentPositionY += pdfRowHeight * 1.5
  text = 'Дата:'
  pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFont, pdfFontNormal)
  pdfFile.text(date, pdfMargin + getTextWidth(pdfFile, text) + 2, pdfCurrentPositionY)

  pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
  pdfFile.setFont(pdfFont, pdfFontBold)
  text = 'Пациент:'
  pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
  pdfFile.setFont(pdfFont, pdfFontNormal)
  let patientInfo = [patient.full_name]
  if (patient.address) {
    patientInfo.push(patient.address)
  }

  if (
    patient.document_series
    && patient.document_number
    && patient.document_issued_by
    && patient.document_type === 'passport'
  ) {
    const passportInfo = [`${patient.document_series}${patient.document_number}`, patient.document_issued_by]
    patientInfo.push(`№ ${passportInfo.join(', ')}`)
  }
  patientInfo = patientInfo.join(', ')
  if (getTextWidth(pdfFile, patientInfo) > pdfWidth - getTextWidth(pdfFile, text) - pdfMargin * 2) {
    const splittedText = pdfFile.splitTextToSize(
      patientInfo, pdfWidth - getTextWidth(pdfFile, text) - pdfMargin * 2
    )
    const splittedTextItemsCount = splittedText.length
    pdfFile.text(splittedText[0], pdfMargin + getTextWidth(pdfFile, text) + 2, pdfCurrentPositionY)
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    pdfFile.text(splittedText.slice(1), pdfMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += (pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor) * (splittedTextItemsCount - 1)
  } else {
    pdfFile.text(patientInfo, pdfMargin + getTextWidth(pdfFile, text) + 2, pdfCurrentPositionY)
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
  }
  let rows = [
    [
      {content: 'Наименование услуги', styles: {cellWidth: 50, halign: 'center', valign: 'middle'}, rowSpan: 2},
      {content: 'Количество', styles: {cellWidth: 14, halign: 'center', valign: 'middle'}, rowSpan: 2},
      {content: 'Тариф услуги', styles: {halign: 'center', valign: 'middle'}, colSpan: 2},
      {content: 'Стоимость материалов', styles: {halign: 'center', valign: 'middle'}, colSpan: 5},
      {content: 'Всего', styles: {halign: 'center', valign: 'middle'}, rowSpan: 2},
    ],
    [
      {content: 'Без НДС', styles: {halign: 'center', valign: 'middle'}},
      {content: 'НДС', styles: {halign: 'center', valign: 'middle'}},
      {content: 'Освобожденных от НДС', styles: {halign: 'center', valign: 'middle'}},
      {content: 'Облагаемых НДС без учета НДС 10%', styles: {halign: 'center', valign: 'middle'}},
      {content: 'Облагаемых НДС без учета НДС 20%', styles: {halign: 'center', valign: 'middle'}},
      {content: 'Сумма НДС по ставке 10%', styles: {halign: 'center', valign: 'middle'}},
      {content: 'Сумма НДС по ставке 20%', styles: {halign: 'center', valign: 'middle'}},
    ],
  ]

  let totalServicesCount = 0
  let totalServicesPriceWithoutNds = 0
  let totalNdsPrice = 0
  let totalMaterialsPriceWithoutNds = 0
  let commonTotalMaterialsNds10Price = 0
  let commonTotalMaterialsNds20Price = 0
  let commonTotalMaterialsWithNds10Price = 0
  let commonTotalMaterialsWithNds20Price = 0
  let commonTotalPrice = 0
  let totalServicesNds10Price = 0
  let totalServicesNds20Price = 0
  purchaseOrder.services_history.forEach(s => {
    let ndsPrice = 0
    const totalPrice =
      parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false))
    if (s.nds) {
      const nds = s.nds
      ndsPrice = nds ? ((s.service_price * s.count / 100) * nds) : 0
      if (nds === 10) {
        totalServicesNds10Price += ndsPrice
      }
      if (nds === 20) {
        totalServicesNds20Price += ndsPrice
      }
    }

    let materialPriceWithoutNds = 0
    let totalMaterialsNds10Price = 0
    let totalMaterialsNds20Price = 0
    let totalMaterialsWithNds10Price = 0
    let totalMaterialsWithNds20Price = 0
    s.materials_history.map(m => {
      const nds = m.nds
      const pricePerUnit = m.price
      let materialPrice = pricePerUnit * s.count * m.part_rate
      let materialNdsPrice = nds ? (nds / 100) * materialPrice : 0
      materialPriceWithoutNds += !nds ? pricePerUnit * s.count * m.part_rate : 0
      if (nds === 10) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds10Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
          totalMaterialsWithNds10Price += materialPrice
        } else {
          totalMaterialsNds10Price += materialNdsPrice
          totalMaterialsWithNds10Price += materialPrice
        }
      }
      if (nds === 20) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds20Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
          totalMaterialsWithNds20Price += materialPrice
        } else {
          totalMaterialsNds20Price += materialNdsPrice
          totalMaterialsWithNds20Price += materialPrice
        }
      }
    })
    materialPriceWithoutNds = materialPriceWithoutNds ? materialPriceWithoutNds : 0

    let servicePriceWithoutNds = s.service_price * s.count
    servicePriceWithoutNds = servicePriceWithoutNds ? servicePriceWithoutNds : 0
    rows.push([
      {content: `${s.number} ${s.name}`, styles: {halign: 'left', valign: 'middle'}},
      {content: s.count, styles: {halign: 'center', valign: 'middle'}},
      {
        content: mixins.methods.formatPrice(servicePriceWithoutNds, false),
        styles: {halign: 'center', valign: 'middle'}
      },
      {
        content: mixins.methods.formatPrice(ndsPrice, false),
        styles: {halign: 'center', valign: 'middle'}
      },
      {
        content: mixins.methods.formatPrice(materialPriceWithoutNds, false),
        styles: {halign: 'center', valign: 'middle'}
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsWithNds10Price, false),
        styles: {halign: 'center', valign: 'middle'}
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsWithNds20Price, false),
        styles: {halign: 'center', valign: 'middle'}
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsNds10Price, false),
        styles: {halign: 'center', valign: 'middle'}
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsNds20Price, false),
        styles: {halign: 'center', valign: 'middle'}
      },
      {
        content: mixins.methods.formatPrice(totalPrice, false),
        styles: {halign: 'center', valign: 'middle'}
      }
    ])

    totalServicesCount += s.count
    totalServicesPriceWithoutNds += parseFloat(servicePriceWithoutNds)
    totalNdsPrice += ndsPrice
    totalMaterialsPriceWithoutNds += parseFloat(materialPriceWithoutNds)
    commonTotalMaterialsNds10Price += parseFloat(mixins.methods.formatPrice(totalMaterialsNds10Price, false))
    commonTotalMaterialsNds20Price += parseFloat(mixins.methods.formatPrice(totalMaterialsNds20Price, false))
    commonTotalMaterialsWithNds10Price += parseFloat(mixins.methods.formatPrice(totalMaterialsWithNds10Price, false))
    commonTotalMaterialsWithNds20Price += parseFloat(mixins.methods.formatPrice(totalMaterialsWithNds20Price, false))
    commonTotalPrice += parseFloat(totalPrice)
    commonTotalMaterialsNds10Price = parseFloat(
      mixins.methods.formatPrice(commonTotalMaterialsNds10Price, false)
    )
    commonTotalMaterialsNds20Price = parseFloat(
      mixins.methods.formatPrice(commonTotalMaterialsNds20Price, false)
    )
  })

  totalServicesPriceWithoutNds = totalServicesPriceWithoutNds ? totalServicesPriceWithoutNds : 0
  totalNdsPrice = totalNdsPrice ? totalNdsPrice : 0
  totalMaterialsPriceWithoutNds = totalMaterialsPriceWithoutNds ? totalMaterialsPriceWithoutNds : 0
  commonTotalPrice = commonTotalPrice ? commonTotalPrice : 0
  rows.push([
    {content: 'ИТОГО', styles: {halign: 'left', valign: 'middle'}},
    {content: totalServicesCount, styles: {halign: 'center', valign: 'middle'}},
    {
      content: mixins.methods.formatPrice(totalServicesPriceWithoutNds, false),
      styles: {halign: 'center', valign: 'middle'}
    },
    {
      content: mixins.methods.formatPrice(totalNdsPrice, false),
      styles: {halign: 'center', valign: 'middle'}
    },
    {
      content: mixins.methods.formatPrice(totalMaterialsPriceWithoutNds, false),
      styles: {halign: 'center', valign: 'middle'}
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsWithNds10Price, false),
      styles: {halign: 'center', valign: 'middle'}
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsWithNds20Price, false),
      styles: {halign: 'center', valign: 'middle'}},
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsNds10Price, false),
      styles: {halign: 'center', valign: 'middle'}
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsNds20Price, false),
      styles: {halign: 'center', valign: 'middle'}
    },
    {
      content: mixins.methods.formatPrice(commonTotalPrice, false),
      styles: {halign: 'center', valign: 'middle'}
    }
  ])

  pdfFile.autoTable({
    theme: 'grid',
    startY: pdfCurrentPositionY,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0]
    },
    styles: {
      font: 'Microsoft-Sans-Serif',
      fontSize: 9,
    },
    didParseCell: hook => {
      hook.cell.styles.fontSize = hook.row.index > 1 && hook.column.index === 0 ? 7.5 : hook.cell.styles.fontSize
      hook.cell.styles.font = hook.row.index > 1 && hook.column.index === 0
        ? 'Microsoft-Sans-Serif' : hook.cell.styles.font
    },
    didDrawPage: hook => {
      pdfCurrentPositionY = (hook.cursor.y + pdfRowHeight)
    }
  })

  if (pdfHeight - 10 < pdfCurrentPositionY) {
    pdfFile.addPage()
    pdfCurrentPositionY = 10
  }

  pdfFile.setDrawColor(0,0,0,1)
  pdfFile.setFont(pdfFont, pdfFontBold)
  pdfFile.setFontSize(11)
  const mainText = `Выполнено работ на сумму с НДС: ${mixins.methods.toFixed(commonTotalPrice, false)}`
  pdfFile.text(mainText, pdfMargin, pdfCurrentPositionY)
  text = `(${mixins.methods.priceToText(commonTotalPrice)})`
  pdfFile.setFont(pdfFont, pdfFontNormal)

  if (getTextWidth(pdfFile, text) > pdfWidth - getTextWidth(pdfFile, mainText) - pdfMargin * 2) {
    const splittedText = pdfFile.splitTextToSize(
      text, pdfWidth - getTextWidth(pdfFile, mainText) - pdfMargin * 2
    )
    pdfFile.text(splittedText[0], pdfMargin + getTextWidth(pdfFile, mainText) + 4, pdfCurrentPositionY)
    pdfFile.line(pdfMargin, pdfCurrentPositionY + 1, pdfWidth - pdfMargin, pdfCurrentPositionY + 1)
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    pdfFile.text(splittedText.slice(1), pdfMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfWidth - pdfMargin, pdfCurrentPositionY)
  } else {
    pdfFile.text(text, pdfMargin + getTextWidth(pdfFile, mainText) + 4, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfWidth - pdfMargin, pdfCurrentPositionY)
  }

  if (
    commonTotalMaterialsNds20Price || commonTotalMaterialsNds10Price
    || totalServicesNds10Price || totalServicesNds20Price
  ) {
    if (pdfHeight - 10 < pdfCurrentPositionY) {
      pdfFile.addPage()
      pdfCurrentPositionY = 10
    }
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    text = 'В том числе НДС:'
    pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
  }

  if (commonTotalMaterialsNds20Price || commonTotalMaterialsNds10Price) {
    if (pdfHeight - 10 < pdfCurrentPositionY) {
      pdfFile.addPage()
      pdfCurrentPositionY = 10
    }
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    text = 'на материалы'
    pdfFile.text(text, pdfMargin, pdfCurrentPositionY)

    if (commonTotalMaterialsNds10Price) {
      if (pdfHeight - 10 < pdfCurrentPositionY) {
        pdfFile.addPage()
        pdfCurrentPositionY = 10
      }
      pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
      commonTotalMaterialsNds10Price = commonTotalMaterialsNds10Price.toFixed(2)
      text = `НДС 10%: ${commonTotalMaterialsNds10Price} (${mixins.methods.priceToText(commonTotalMaterialsNds10Price)})`
      pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
      pdfCurrentPositionY += 1
      pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
    }

    if (commonTotalMaterialsNds20Price) {
      if (pdfHeight - 10 < pdfCurrentPositionY) {
        pdfFile.addPage()
        pdfCurrentPositionY = 10
      }
      pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
      commonTotalMaterialsNds20Price = commonTotalMaterialsNds20Price.toFixed(2)
      text = `НДС 20%: ${commonTotalMaterialsNds20Price} (${mixins.methods.priceToText(commonTotalMaterialsNds20Price)})`
      pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
      pdfCurrentPositionY += 1
      pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
    }
  }

  if (totalServicesNds10Price || totalServicesNds20Price) {
    if (pdfHeight - 10 < pdfCurrentPositionY) {
      pdfFile.addPage()
      pdfCurrentPositionY = 10
    }
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    text = 'на тариф'
    pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
  }

  if (totalServicesNds10Price) {
    if (pdfHeight - 10 < pdfCurrentPositionY) {
      pdfFile.addPage()
      pdfCurrentPositionY = 10
    }
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    totalServicesNds10Price = totalServicesNds10Price.toFixed(2)
    text = `НДС 10%: ${totalServicesNds10Price} (${mixins.methods.priceToText(totalServicesNds10Price)})`
    pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
  }

  if (totalServicesNds20Price) {
    if (pdfHeight - 10 < pdfCurrentPositionY) {
      pdfFile.addPage()
      pdfCurrentPositionY = 10
    }
    pdfCurrentPositionY += pdfFile.getLineHeight(text) / pdfFile.internal.scaleFactor
    totalServicesNds20Price = totalServicesNds20Price.toFixed(2)
    text = `НДС 20%: ${totalServicesNds20Price} (${mixins.methods.priceToText(totalServicesNds20Price)})`
    pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfMargin + getTextWidth(pdfFile, text), pdfCurrentPositionY)
  }

  pdfCurrentPositionY += pdfRowHeight * 1.5

  if (pdfHeight - 10 < pdfCurrentPositionY) {
    pdfFile.addPage()
    pdfCurrentPositionY = 10
  }

  text = `${purchaseOrderExecutorLabel()}:`
  pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
  if (purchaseOrderFillExecutorDoctorName() && purchaseOrder.doctor) {
    pdfFile.text(
      purchaseOrder.doctor.full_name_initials,
      pdfMargin + 1 + getTextWidth(pdfFile, text),
      pdfCurrentPositionY
    )
  }
  pdfCurrentPositionY += 1
  pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfWidth - pdfMargin, pdfCurrentPositionY)

  pdfCurrentPositionY += pdfRowHeight * 1.5
  if (pdfHeight - 10 < pdfCurrentPositionY) {
    pdfFile.addPage()
    pdfCurrentPositionY = 10
  }

  if (!purchaseOrderAdministratorHidden()) {
    text = 'Администратор:'
    pdfFile.text(text, pdfMargin, pdfCurrentPositionY)
    pdfCurrentPositionY += 1
    pdfFile.line(pdfMargin, pdfCurrentPositionY, pdfWidth - pdfMargin, pdfCurrentPositionY)
  }

  pdfFile.save(`Заказ-наряд по оказанным мед. услугам, ${patient.full_name}, ${date}.pdf`)
}

export const createAcceptanceActOfWorkPerformedForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования документа. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const priceCalcSystem = mixins.methods.getMaterialPriceCalcSystem()
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const client = purchaseOrder.client
  const patient = purchaseOrder.patient
  let pdfFont = 'Microsoft-Sans-Serif'
  const pdfFontBold = 'bold'
  const pdfFontNormal = 'normal'
  const pdfMargin = 10
  const pdfRowHeight = 7
  const date = moment(parseInt(purchaseOrder.act_creation_date)).format('LL')
  let doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true })
  const scaleFactor = doc.internal.scaleFactor
  const pdfHeight = doc.internal.pageSize.height
  const pdfWidth = doc.internal.pageSize.width
  const name = dentalLaboratory && dentalLaboratory.name ? dentalLaboratory.name : null
  const phone = dentalLaboratory && dentalLaboratory.phone ? dentalLaboratory.phone : null
  const unp = dentalLaboratory && dentalLaboratory.phone ? dentalLaboratory.unp : null
  const address = dentalLaboratory && dentalLaboratory.address ? dentalLaboratory.address : null
  const currentAccount = dentalLaboratory && dentalLaboratory.current_account
    ? dentalLaboratory.current_account : null
  const bank = dentalLaboratory && dentalLaboratory.bank ? dentalLaboratory.bank : null
  const bic = dentalLaboratory && dentalLaboratory.bic ? dentalLaboratory.bic : null
  const clientName = client && client.name ? client.name : null
  const clientPhone = client && client.phone ? client.phone : null
  const clientUnp = client && client.phone ? client.unp : null
  const clientAddress = client && client.address ? client.address : null
  const clientCurrentAccount = client && client.current_account ? client.current_account : null
  const clientBank = client && client.bank ? client.bank : null
  const clientBic = client && client.bic ? client.bic : null
  const clientAgreement = client && client.agreement ? client.agreement : null
  let text = address
  let y = pdfMargin

  doc.setFont(pdfFont, pdfFontNormal)
  if (text) {
    doc.setFontSize(9)
    doc.text(text, pdfMargin, pdfMargin)
    doc.setFont(pdfFont, pdfFontBold)
    doc.setFontSize(13)
    y += (doc.getLineHeight(text) / scaleFactor) * 1.5
  } else {
    doc.setFont(pdfFont, pdfFontBold)
    doc.setFontSize(13)
  }

  text = 'АКТ'
  doc.text(text, pdfWidth / 2, y, 'center')
  doc.setFontSize(12)
  y += doc.getLineHeight(text) / scaleFactor
  text = purchaseOrder.order_number
    ? `сдачи-приемки выполненных работ №${purchaseOrder.order_number}`
    : 'сдачи-приемки выполненных работ'
  doc.text(text, pdfWidth / 2, y, 'center')
  y += (doc.getLineHeight(text) / scaleFactor) * 1.5

  doc.setFont(pdfFont, pdfFontNormal)
  doc.setFontSize(10)
  text = `составлен ${moment().format('DD MMMM YYYY')} г.`
  const dateTextOffset = pdfMargin + getTextWidth(doc, text)
  doc.text(text, pdfMargin, y)

  text = clientAgreement ? `Договор продажи ${clientAgreement}` : ''
  doc.text(text, pdfWidth - pdfMargin, y, 'right')
  y += (doc.getLineHeight(text) / scaleFactor) * 2

  doc.setFont(pdfFont, pdfFontBold)
  text = 'Исполнитель:'
  doc.text(text, pdfMargin, y)
  doc.line(pdfMargin, y + 1, pdfMargin + getTextWidth(doc, text), y + 1)

  let credentials = []
  doc.setFont(pdfFont, pdfFontNormal)
  if (name) {
    credentials.push(name)
  }
  if (address || phone) {
    text = address ? address : ''
    if (phone) {
      text = text ? `${text}, +${phone}` : `+${phone}`
    }
    credentials.push(text)
  }
  if (unp) {
    credentials.push(`УНП ${unp}`)
  }
  if (currentAccount || bank || bic) {
    text = currentAccount ? `Р/с №${currentAccount.replaceAll(' ', '')}` : ''
    if (bank) {
      text = text ? `${text}, Банк: ${bank}` : `Банк: ${bank}`
    }
    if (bic) {
      text = text ? `${text}, БИК ${bic}` : `БИК ${bic}`
    }
    credentials.push(text)
  }
  if (credentials) {
    y = textToLines(
      credentials.join('\n'), null, doc, dateTextOffset, y, pdfFont, true, 1,
      true, pdfMargin
    )
  }

  y += (doc.getLineHeight(text) / scaleFactor) / 2
  doc.setFont(pdfFont, pdfFontBold)
  text = 'Заказчик:'
  doc.text(text, pdfMargin, y)
  doc.line(pdfMargin, y + 1, pdfMargin + getTextWidth(doc, text), y + 1)

  credentials = []
  doc.setFont(pdfFont, pdfFontNormal)
  if (clientName) {
    credentials.push(clientName)
  }
  if (clientAddress || clientPhone) {
    text = clientAddress ? clientAddress : ''
    if (clientPhone) {
      text = text ? `${text}, +${clientPhone}` : `+${clientPhone}`
    }
    credentials.push(text)
  }
  if (clientUnp) {
    credentials.push(`УНП ${clientUnp}`)
  }
  if (clientCurrentAccount || clientBank || clientBic) {
    text = clientCurrentAccount ? `Р/с №${clientCurrentAccount.replaceAll(' ', '')}` : ''
    if (clientBank) {
      text = text ? `${text}, Банк: ${clientBank}` : `Банк: ${clientBank}`
    }
    if (clientBic) {
      text = text ? `${text}, БИК ${clientBic}` : `БИК ${clientBic}`
    }
    credentials.push(text)
  }
  if (credentials) {
    y = textToLines(
      credentials.join('\n'), null, doc, dateTextOffset, y, pdfFont, true, 1,
      true, pdfMargin
    )
  }
  
  y += (doc.getLineHeight(text) / scaleFactor) / 2
  doc.setFont(pdfFont, pdfFontBold)
  text = 'Пациент:'
  doc.text(text, pdfMargin, y)
  doc.line(pdfMargin, y + 1, pdfMargin + getTextWidth(doc, text), y + 1)
  doc.setFont(pdfFont, pdfFontNormal)
  y = textToLines(
    patient.full_name, null, doc, dateTextOffset, y, pdfFont, true, 1,
    true, pdfMargin
  )

  y += doc.getLineHeight(text) / scaleFactor
  text = `Настоящий акт составлен, с одной стороны, ${name}, именуемым в дальнейшем Исполнитель, и с другой стороны ${clientName}, именуемым в дальнейшем Заказчик, в том, что Исполнитель выполнил в срок и сдал следующие работы`
  y = textToLines(
    text, null, doc, pdfMargin, y, pdfFont, true, 1, false, pdfMargin
  )

  let rows = [
    [
      { content: 'Наименование услуги', styles: { cellWidth: 50, halign: 'center', valign: 'middle' }, rowSpan: 2 },
      { content: 'Количество', styles: { cellWidth: 14, halign: 'center', valign: 'middle' }, rowSpan: 2 },
      { content: 'Тариф услуги', styles: { halign: 'center', valign: 'middle' }, colSpan: 2 },
      { content: 'Стоимость материалов', styles: { halign: 'center', valign: 'middle' }, colSpan: 5 },
      { content: 'Всего', styles: { halign: 'center', valign: 'middle' }, rowSpan: 2 },
    ],
    [
      { content: 'Без НДС', styles: { halign: 'center', valign: 'middle' } },
      { content: 'НДС', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Освобожденных от НДС', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Облагаемых НДС без учета НДС 10%', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Облагаемых НДС без учета НДС 20%', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Сумма НДС по ставке 10%', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Сумма НДС по ставке 20%', styles: { halign: 'center', valign: 'middle' } },
    ],
  ]

  let totalServicesCount = 0
  let totalServicesPriceWithoutNds = 0
  let totalNdsPrice = 0
  let totalMaterialsPriceWithoutNds = 0
  let commonTotalMaterialsNds10Price = 0
  let commonTotalMaterialsNds20Price = 0
  let commonTotalMaterialsWithNds10Price = 0
  let commonTotalMaterialsWithNds20Price = 0
  let commonTotalPrice = 0
  let totalServicesNds10Price = 0
  let totalServicesNds20Price = 0
  const tableLineHeightFactor = 1.2
  purchaseOrder.services_history.forEach(s => {
    let ndsPrice = 0
    const totalPrice =
      parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false))
    if (s.nds) {
      const nds = s.nds
      ndsPrice = nds ? ((s.service_price * s.count / 100) * nds) : 0
      if (nds === 10) {
        totalServicesNds10Price += ndsPrice
      }
      if (nds === 20) {
        totalServicesNds20Price += ndsPrice
      }
    }

    let materialPriceWithoutNds = 0
    let totalMaterialsNds10Price = 0
    let totalMaterialsNds20Price = 0
    let totalMaterialsWithNds10Price = 0
    let totalMaterialsWithNds20Price = 0
    s.materials_history.map(m => {
      const nds = m.nds
      const pricePerUnit = m.price
      let materialPrice = pricePerUnit * s.count * m.part_rate
      let materialNdsPrice = nds ? (nds / 100) * materialPrice : 0
      materialPriceWithoutNds += !nds ? pricePerUnit * s.count * m.part_rate : 0
      if (nds === 10) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds10Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
          totalMaterialsWithNds10Price += materialPrice
        } else {
          totalMaterialsNds10Price += materialNdsPrice
          totalMaterialsWithNds10Price += materialPrice
        }
      }
      if (nds === 20) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds20Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
          totalMaterialsWithNds20Price += materialPrice
        } else {
          totalMaterialsNds20Price += materialNdsPrice
          totalMaterialsWithNds20Price += materialPrice
        }
      }
    })
    materialPriceWithoutNds = materialPriceWithoutNds ? materialPriceWithoutNds : 0

    let servicePriceWithoutNds = s.service_price * s.count
    servicePriceWithoutNds = servicePriceWithoutNds ? servicePriceWithoutNds : 0
    rows.push([
      { content: `${s.number} ${s.name}`, styles: { halign: 'left', valign: 'middle' } },
      { content: s.count, styles: { halign: 'center', valign: 'middle' } },
      {
        content: mixins.methods.formatPrice(servicePriceWithoutNds, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(ndsPrice, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(materialPriceWithoutNds, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsWithNds10Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsWithNds20Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsNds10Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsNds20Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalPrice, false),
        styles: { halign: 'center', valign: 'middle' }
      }
    ])

    totalServicesCount += s.count
    totalServicesPriceWithoutNds += parseFloat(servicePriceWithoutNds)
    totalNdsPrice += ndsPrice
    totalMaterialsPriceWithoutNds += parseFloat(materialPriceWithoutNds)
    commonTotalMaterialsNds10Price += parseFloat(mixins.methods.formatPrice(totalMaterialsNds10Price, false))
    commonTotalMaterialsNds20Price += parseFloat(mixins.methods.formatPrice(totalMaterialsNds20Price, false))
    commonTotalMaterialsWithNds10Price += parseFloat(mixins.methods.formatPrice(totalMaterialsWithNds10Price, false))
    commonTotalMaterialsWithNds20Price += parseFloat(mixins.methods.formatPrice(totalMaterialsWithNds20Price, false))
    commonTotalPrice += parseFloat(totalPrice)
    commonTotalMaterialsNds10Price = parseFloat(
      mixins.methods.formatPrice(commonTotalMaterialsNds10Price, false)
    )
    commonTotalMaterialsNds20Price = parseFloat(
      mixins.methods.formatPrice(commonTotalMaterialsNds20Price, false)
    )
  })

  totalServicesPriceWithoutNds = totalServicesPriceWithoutNds ? totalServicesPriceWithoutNds : 0
  totalNdsPrice = totalNdsPrice ? totalNdsPrice : 0
  totalMaterialsPriceWithoutNds = totalMaterialsPriceWithoutNds ? totalMaterialsPriceWithoutNds : 0
  commonTotalPrice = commonTotalPrice ? commonTotalPrice : 0
  rows.push([
    { content: 'ИТОГО', styles: { halign: 'left', valign: 'middle' } },
    { content: totalServicesCount, styles: { halign: 'center', valign: 'middle' } },
    {
      content: mixins.methods.formatPrice(totalServicesPriceWithoutNds, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(totalNdsPrice, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(totalMaterialsPriceWithoutNds, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsWithNds10Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsWithNds20Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsNds10Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsNds20Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalPrice, false),
      styles: { halign: 'center', valign: 'middle' }
    }
  ])

  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: y,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0]
    },
    styles: {
      font: 'Microsoft-Sans-Serif',
      fontSize: 9,
    },
    didParseCell: hook => {
      hook.cell.styles.fontSize = hook.row.index > 1 && hook.column.index === 0 ? 7.5 : hook.cell.styles.fontSize
      hook.cell.styles.font = hook.row.index > 1 && hook.column.index === 0
        ? 'Microsoft-Sans-Serif' : hook.cell.styles.font
    },
    didDrawPage: hook => {
      y = (hook.cursor.y + pdfRowHeight)
    }
  })

  if (pdfHeight - 10 < y) {
    doc.addPage()
    y = 10
  }

  doc.setDrawColor(0,0,0,1)
  doc.setFont(pdfFont, pdfFontBold)
  doc.setFontSize(10)
  const mainText = `Выполнено работ на сумму с НДС: ${mixins.methods.toFixed(commonTotalPrice, false)}`
  const mainTextOffset = getTextWidth(doc, mainText) + 1
  doc.text(mainText, pdfMargin, y)
  doc.line(pdfMargin, y + 1, pdfMargin + mainTextOffset, y + 1)
  text = `(${mixins.methods.priceToText(commonTotalPrice)})`
  doc.setFont(pdfFont, pdfFontNormal)

  y = textToLines(
    text, null, doc, pdfMargin, y, pdfFont, false, 1, false, pdfMargin,
    mainTextOffset
  )
  doc.setLineHeightFactor(tableLineHeightFactor)
  y -= (doc.getLineHeight(text) / scaleFactor) - 1

  if (
    commonTotalMaterialsNds20Price || commonTotalMaterialsNds10Price
    || totalServicesNds10Price || totalServicesNds20Price
  ) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    text = 'В том числе НДС:'
    doc.text(text, pdfMargin, y)
  }

  if (commonTotalMaterialsNds20Price || commonTotalMaterialsNds10Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    text = 'на материалы'
    doc.text(text, pdfMargin, y)

    if (commonTotalMaterialsNds10Price) {
      if (pdfHeight - 10 < y) {
        doc.addPage()
        y = 10
      }
      y += doc.getLineHeight(text) / doc.internal.scaleFactor
      commonTotalMaterialsNds10Price = commonTotalMaterialsNds10Price.toFixed(2)
      text = `НДС 10%: ${commonTotalMaterialsNds10Price} (${mixins.methods.priceToText(commonTotalMaterialsNds10Price)})`
      doc.text(text, pdfMargin, y)
      y += 1
      doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
    }

    if (commonTotalMaterialsNds20Price) {
      if (pdfHeight - 10 < y) {
        doc.addPage()
        y = 10
      }
      y += doc.getLineHeight(text) / doc.internal.scaleFactor
      commonTotalMaterialsNds20Price = commonTotalMaterialsNds20Price.toFixed(2)
      text = `НДС 20%: ${commonTotalMaterialsNds20Price} (${mixins.methods.priceToText(commonTotalMaterialsNds20Price)})`
      doc.text(text, pdfMargin, y)
      y += 1
      doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
    }
  }

  if (totalServicesNds10Price || totalServicesNds20Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    text = 'на тариф'
    doc.text(text, pdfMargin, y)
  }

  if (totalServicesNds10Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    totalServicesNds10Price = totalServicesNds10Price.toFixed(2)
    text = `НДС 10%: ${totalServicesNds10Price} (${mixins.methods.priceToText(totalServicesNds10Price)})`
    doc.text(text, pdfMargin, y)
    y += 1
    doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
  }

  if (totalServicesNds20Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    totalServicesNds20Price = totalServicesNds20Price.toFixed(2)
    text = `НДС 20%: ${totalServicesNds20Price} (${mixins.methods.priceToText(totalServicesNds20Price)})`
    doc.text(text, pdfMargin, y)
    y += 1
    doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
  }

  y += pdfRowHeight * 1.5

  if (pdfHeight - 10 < y) {
    doc.addPage()
    y = 10
  }

  text = `${purchaseOrderExecutorLabel()}:`
  doc.text(text, pdfMargin, y)
  if (purchaseOrderFillExecutorDoctorName() && purchaseOrder.doctor) {
    doc.text(
      purchaseOrder.doctor.full_name_initials,
      pdfMargin + 1 + getTextWidth(doc, text),
      y
    )
  }
  y += 1
  doc.line(pdfMargin, y, pdfWidth - pdfMargin, y)

  y += pdfRowHeight * 1.5
  if (pdfHeight - 10 < y) {
    doc.addPage()
    y = 10
  }

  if (!purchaseOrderAdministratorHidden()) {
    text = 'Администратор:'
    doc.text(text, pdfMargin, y)
    y += 1
    doc.line(pdfMargin, y, pdfWidth - pdfMargin, y)
  }

  doc.save(`Акт сдачи-приемки выполненных работ, ${patient.full_name}, ${date}.pdf`)
}

export const invoiceOfRenderedMedicalServicesForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования документа. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }
  const priceCalcSystem = mixins.methods.getMaterialPriceCalcSystem()
  const dentalLaboratory = purchaseOrder.dental_laboratory
  const client = purchaseOrder.client
  const patient = purchaseOrder.patient
  let pdfFont = 'Microsoft-Sans-Serif'
  const pdfFontBold = 'bold'
  const pdfFontNormal = 'normal'
  const pdfMargin = 10
  const pdfRowHeight = 7
  const date = moment(parseInt(purchaseOrder.act_creation_date)).format('LL')
  let doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true })
  const scaleFactor = doc.internal.scaleFactor
  const pdfHeight = doc.internal.pageSize.height
  const pdfWidth = doc.internal.pageSize.width
  const name = dentalLaboratory && dentalLaboratory.name ? dentalLaboratory.name : null
  const phone = dentalLaboratory && dentalLaboratory.phone ? dentalLaboratory.phone : null
  const unp = dentalLaboratory && dentalLaboratory.phone ? dentalLaboratory.unp : null
  const address = dentalLaboratory && dentalLaboratory.address ? dentalLaboratory.address : ''
  const currentAccount = dentalLaboratory && dentalLaboratory.current_account
    ? dentalLaboratory.current_account : null
  const bank = dentalLaboratory && dentalLaboratory.bank ? dentalLaboratory.bank : null
  const bic = dentalLaboratory && dentalLaboratory.bic ? dentalLaboratory.bic : null
  const licenseNumber = dentalLaboratory && dentalLaboratory.license_number ? dentalLaboratory.license_number : null
  const clientName = client && client.name ? client.name : null
  let clientPhone = client && client.phone ? client.phone : null
  const clientUnp = client && client.unp ? client.unp : null
  const clientAddress = client && client.address ? client.address : null
  const clientCurrentAccount = client && client.current_account ? client.current_account : null
  const clientBank = client && client.bank ? client.bank : null
  const clientBic = client && client.bic ? client.bic : null
  const clientAgreement = client && client.agreement ? client.agreement : ''
  const fontStyleBold = 'bold'
  let text = address
  let y = pdfMargin

  const credentials = []
  if (address) {
    credentials.push(address)
  }
  if (currentAccount || bank || bic) {
    text = currentAccount ? `Р/с №${currentAccount.replaceAll(' ', '')}` : ''
    if (bank) {
      text = text ? `${text}, Банк: ${bank}` : `Банк: ${bank}`
    }
    if (bic) {
      text = text ? `${text}, БИК ${bic}` : `БИК ${bic}`
    }
    credentials.push(text)
  }
  if (phone) {
    credentials.push(`тел. +${phone}`)
  }
  if (unp) {
    credentials.push(`УНП ${unp}`)
  }
  if (licenseNumber) {
    credentials.push(`Лицензия ${licenseNumber}`)
  }
  
  const clientCredentials = []
  if (clientAddress) {
    clientCredentials.push(clientAddress)
  }
  if (clientCurrentAccount || clientBank || clientBic) {
    text = clientCurrentAccount ? `Р/с №${clientCurrentAccount.replaceAll(' ', '')}` : ''
    if (clientBank) {
      text = text ? `${text}, Банк: ${clientBank}` : `Банк: ${clientBank}`
    }
    if (clientBic) {
      text = text ? `${text}, БИК ${clientBic}` : `БИК ${clientBic}`
    }
    clientCredentials.push(text)
  }
  if (clientPhone) {
    clientCredentials.push(`тел. +${clientPhone}`)
  }
  if (clientUnp) {
    clientCredentials.push(`УНП ${clientUnp}`)
  }

  const orderNumber = purchaseOrder.order_number ? purchaseOrder.order_number : '_'
  let rows = [
    [
      { content: 'Поставщик и его адрес:', styles: { cellWidth: 23, fontSize: 8, fontStyle: fontStyleBold } },
      { content: name, styles: { fontSize: 9, cellWidth: 80 } },
      {
        content: 'СЧЕТ-ПРОТОКОЛ', styles: { halign: 'center', valign: 'middle', fontSize: 12, fontStyle: fontStyleBold }
      },
    ],
    [
      { content: '' },
      { content: credentials.length ? credentials.join('\n') : '', styles: { fontSize: 9 } },
      {
        content:
          `согласования свободных договорных цен на услуги №${orderNumber} от ${moment().format('DD MMMM YYYY')} г.`,
        rowSpan: 2, styles: { halign: 'center', valign: 'top', fontSize: 9, fontStyle: fontStyleBold }
      }
    ],
    [
      { content: 'Покупатель и его адрес:', styles: { fontSize: 8, fontStyle: fontStyleBold } },
      { content: clientName, colSpan: 2, styles: { fontSize: 9 } },
    ],
    [
      { content: '' },
      { content: clientCredentials.length ? clientCredentials.join('\n') : '', styles: { fontSize: 9 }, colSpan: 2 }
    ],
    [
      {
        content: `Основание: ${clientAgreement ? `Договор продажи ${clientAgreement}` : ''}`,
        colSpan: 3, styles: { fontSize: 8, fontStyle: fontStyleBold }
      },
    ],
  ]
  doc.autoTable({
    theme: 'grid',
    startY: y,
    margin: { top: pdfMargin, right: pdfMargin, bottom: 0, left: pdfMargin },
    body: rows,
    bodyStyles: { lineColor: [0, 0, 0], textColor: [0, 0, 0] },
    styles: { font: 'Microsoft-Sans-Serif', fontSize: 9 },
    didParseCell: hook => {
      if ([0, 2].includes(hook.row.index)) {
        hook.cell.styles.lineWidth = { top: 0.1, right: 0.1, bottom: 0, left: 0.1 }
      } else if ([1, 3].includes(hook.row.index)) {
        hook.cell.styles.lineWidth = { top: 0, right: 0.1, bottom: 0, left: 0.1 }
      }
      hook.cell.styles.cellPadding = {
        top: ![0, 2, 4].includes(hook.row.index) ? 0 : 0.8,
        right: 0.8,
        bottom: ![0, 2, 4].includes(hook.row.index) ? 0 : 0.4,
        left: 0.8
      }
    },
    didDrawPage: hook => y = hook.cursor.y,
  })

  y += doc.getLineHeight(text) / doc.internal.scaleFactor

  rows = [
    [
      { content: 'Наименование услуги', styles: { cellWidth: 50, halign: 'center', valign: 'middle' }, rowSpan: 2 },
      { content: 'Количество', styles: { cellWidth: 14, halign: 'center', valign: 'middle' }, rowSpan: 2 },
      { content: 'Тариф услуги', styles: { halign: 'center', valign: 'middle' }, colSpan: 2 },
      { content: 'Стоимость материалов', styles: { halign: 'center', valign: 'middle' }, colSpan: 5 },
      { content: 'Всего', styles: { halign: 'center', valign: 'middle' }, rowSpan: 2 },
    ],
    [
      { content: 'Без НДС', styles: { halign: 'center', valign: 'middle' } },
      { content: 'НДС', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Освобожденных от НДС', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Облагаемых НДС без учета НДС 10%', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Облагаемых НДС без учета НДС 20%', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Сумма НДС по ставке 10%', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Сумма НДС по ставке 20%', styles: { halign: 'center', valign: 'middle' } },
    ],
  ]

  let totalServicesCount = 0
  let totalServicesPriceWithoutNds = 0
  let totalNdsPrice = 0
  let totalMaterialsPriceWithoutNds = 0
  let commonTotalMaterialsNds10Price = 0
  let commonTotalMaterialsNds20Price = 0
  let commonTotalMaterialsWithNds10Price = 0
  let commonTotalMaterialsWithNds20Price = 0
  let commonTotalPrice = 0
  let totalServicesNds10Price = 0
  let totalServicesNds20Price = 0
  const tableLineHeightFactor = 1.2
  purchaseOrder.services_history.forEach(s => {
    let ndsPrice = 0
    const totalPrice =
      parseFloat(mixins.methods.formatPrice(s.total_price * s.count, false))
    if (s.nds) {
      const nds = s.nds
      ndsPrice = nds ? ((s.service_price * s.count / 100) * nds) : 0
      if (nds === 10) {
        totalServicesNds10Price += ndsPrice
      }
      if (nds === 20) {
        totalServicesNds20Price += ndsPrice
      }
    }

    let materialPriceWithoutNds = 0
    let totalMaterialsNds10Price = 0
    let totalMaterialsNds20Price = 0
    let totalMaterialsWithNds10Price = 0
    let totalMaterialsWithNds20Price = 0
    s.materials_history.map(m => {
      const nds = m.nds
      const pricePerUnit = m.price
      let materialPrice = pricePerUnit * s.count * m.part_rate
      let materialNdsPrice = nds ? (nds / 100) * materialPrice : 0
      materialPriceWithoutNds += !nds ? pricePerUnit * s.count * m.part_rate : 0
      if (nds === 10) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds10Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
          totalMaterialsWithNds10Price += materialPrice
        } else {
          totalMaterialsNds10Price += materialNdsPrice
          totalMaterialsWithNds10Price += materialPrice
        }
      }
      if (nds === 20) {
        if (appConstants.material.materialPriceCalcSystem.USN === priceCalcSystem) {
          totalMaterialsNds20Price += (m.price_per_unit - m.price_per_unit_without_nds) * s.count * m.part_rate
          totalMaterialsWithNds20Price += materialPrice
        } else {
          totalMaterialsNds20Price += materialNdsPrice
          totalMaterialsWithNds20Price += materialPrice
        }
      }
    })
    materialPriceWithoutNds = materialPriceWithoutNds ? materialPriceWithoutNds : 0

    let servicePriceWithoutNds = s.service_price * s.count
    servicePriceWithoutNds = servicePriceWithoutNds ? servicePriceWithoutNds : 0
    rows.push([
      { content: `${s.number} ${s.name}`, styles: { halign: 'left', valign: 'middle' } },
      { content: s.count, styles: { halign: 'center', valign: 'middle' } },
      {
        content: mixins.methods.formatPrice(servicePriceWithoutNds, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(ndsPrice, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(materialPriceWithoutNds, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsWithNds10Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsWithNds20Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsNds10Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalMaterialsNds20Price, false),
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: mixins.methods.formatPrice(totalPrice, false),
        styles: { halign: 'center', valign: 'middle' }
      }
    ])

    totalServicesCount += s.count
    totalServicesPriceWithoutNds += parseFloat(servicePriceWithoutNds)
    totalNdsPrice += ndsPrice
    totalMaterialsPriceWithoutNds += parseFloat(materialPriceWithoutNds)
    commonTotalMaterialsNds10Price += parseFloat(mixins.methods.formatPrice(totalMaterialsNds10Price, false))
    commonTotalMaterialsNds20Price += parseFloat(mixins.methods.formatPrice(totalMaterialsNds20Price, false))
    commonTotalMaterialsWithNds10Price += parseFloat(mixins.methods.formatPrice(totalMaterialsWithNds10Price, false))
    commonTotalMaterialsWithNds20Price += parseFloat(mixins.methods.formatPrice(totalMaterialsWithNds20Price, false))
    commonTotalPrice += parseFloat(totalPrice)
    commonTotalMaterialsNds10Price = parseFloat(
      mixins.methods.formatPrice(commonTotalMaterialsNds10Price, false)
    )
    commonTotalMaterialsNds20Price = parseFloat(
      mixins.methods.formatPrice(commonTotalMaterialsNds20Price, false)
    )
  })

  totalServicesPriceWithoutNds = totalServicesPriceWithoutNds ? totalServicesPriceWithoutNds : 0
  totalNdsPrice = totalNdsPrice ? totalNdsPrice : 0
  totalMaterialsPriceWithoutNds = totalMaterialsPriceWithoutNds ? totalMaterialsPriceWithoutNds : 0
  commonTotalPrice = commonTotalPrice ? commonTotalPrice : 0
  rows.push([
    { content: 'ИТОГО', styles: { halign: 'left', valign: 'middle' } },
    { content: totalServicesCount, styles: { halign: 'center', valign: 'middle' } },
    {
      content: mixins.methods.formatPrice(totalServicesPriceWithoutNds, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(totalNdsPrice, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(totalMaterialsPriceWithoutNds, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsWithNds10Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsWithNds20Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsNds10Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalMaterialsNds20Price, false),
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: mixins.methods.formatPrice(commonTotalPrice, false),
      styles: { halign: 'center', valign: 'middle' }
    }
  ])

  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: y,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0]
    },
    styles: {
      font: 'Microsoft-Sans-Serif',
      fontSize: 9,
    },
    didParseCell: hook => {
      hook.cell.styles.fontSize = hook.row.index > 1 && hook.column.index === 0 ? 7.5 : hook.cell.styles.fontSize
      hook.cell.styles.font = hook.row.index > 1 && hook.column.index === 0
        ? 'Microsoft-Sans-Serif' : hook.cell.styles.font
    },
    didDrawPage: hook => {
      y = (hook.cursor.y + pdfRowHeight)
    }
  })

  if (pdfHeight - 10 < y) {
    doc.addPage()
    y = 10
  }

  doc.setDrawColor(0,0,0,1)
  doc.setFont(pdfFont, pdfFontBold)
  doc.setFontSize(10)
  const mainText = `Выполнено работ на сумму с НДС: ${mixins.methods.toFixed(commonTotalPrice, false)}`
  const mainTextOffset = getTextWidth(doc, mainText) + 1
  doc.text(mainText, pdfMargin, y)
  doc.line(pdfMargin, y + 1, pdfMargin + mainTextOffset, y + 1)
  text = `(${mixins.methods.priceToText(commonTotalPrice)})`
  doc.setFont(pdfFont, pdfFontNormal)

  y = textToLines(
    text, null, doc, pdfMargin, y, pdfFont, false, 1, false, pdfMargin,
    mainTextOffset
  )
  doc.setLineHeightFactor(tableLineHeightFactor)
  y -= (doc.getLineHeight(text) / scaleFactor) - 1

  if (
    commonTotalMaterialsNds20Price || commonTotalMaterialsNds10Price
    || totalServicesNds10Price || totalServicesNds20Price
  ) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    text = 'В том числе НДС:'
    doc.text(text, pdfMargin, y)
  }

  if (commonTotalMaterialsNds20Price || commonTotalMaterialsNds10Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    text = 'на материалы'
    doc.text(text, pdfMargin, y)

    if (commonTotalMaterialsNds10Price) {
      if (pdfHeight - 10 < y) {
        doc.addPage()
        y = 10
      }
      y += doc.getLineHeight(text) / doc.internal.scaleFactor
      commonTotalMaterialsNds10Price = commonTotalMaterialsNds10Price.toFixed(2)
      text = `НДС 10%: ${commonTotalMaterialsNds10Price} (${mixins.methods.priceToText(commonTotalMaterialsNds10Price)})`
      doc.text(text, pdfMargin, y)
      y += 1
      doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
    }

    if (commonTotalMaterialsNds20Price) {
      if (pdfHeight - 10 < y) {
        doc.addPage()
        y = 10
      }
      y += doc.getLineHeight(text) / doc.internal.scaleFactor
      commonTotalMaterialsNds20Price = commonTotalMaterialsNds20Price.toFixed(2)
      text = `НДС 20%: ${commonTotalMaterialsNds20Price} (${mixins.methods.priceToText(commonTotalMaterialsNds20Price)})`
      doc.text(text, pdfMargin, y)
      y += 1
      doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
    }
  }

  if (totalServicesNds10Price || totalServicesNds20Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    text = 'на тариф'
    doc.text(text, pdfMargin, y)
  }

  if (totalServicesNds10Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    totalServicesNds10Price = totalServicesNds10Price.toFixed(2)
    text = `НДС 10%: ${totalServicesNds10Price} (${mixins.methods.priceToText(totalServicesNds10Price)})`
    doc.text(text, pdfMargin, y)
    y += 1
    doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
  }

  if (totalServicesNds20Price) {
    if (pdfHeight - 10 < y) {
      doc.addPage()
      y = 10
    }
    y += doc.getLineHeight(text) / doc.internal.scaleFactor
    totalServicesNds20Price = totalServicesNds20Price.toFixed(2)
    text = `НДС 20%: ${totalServicesNds20Price} (${mixins.methods.priceToText(totalServicesNds20Price)})`
    doc.text(text, pdfMargin, y)
    y += 1
    doc.line(pdfMargin, y, pdfMargin + getTextWidth(doc, text), y)
  }

  y += pdfRowHeight * 1.5

  if (pdfHeight - 10 < y) {
    doc.addPage()
    y = 10
  }

  text = `${purchaseOrderExecutorLabel()}:`
  doc.text(text, pdfMargin, y)
  if (purchaseOrderFillExecutorDoctorName() && purchaseOrder.doctor) {
    doc.text(
      purchaseOrder.doctor.full_name_initials,
      pdfMargin + 1 + getTextWidth(doc, text),
      y
    )
  }
  y += 1
  doc.line(pdfMargin, y, pdfWidth - pdfMargin, y)

  y += pdfRowHeight * 1.5
  if (pdfHeight - 10 < y) {
    doc.addPage()
    y = 10
  }

  if (!purchaseOrderAdministratorHidden()) {
    text = 'Администратор:'
    doc.text(text, pdfMargin, y)
    y += 1
    doc.line(pdfMargin, y, pdfWidth - pdfMargin, y)
  }

  doc.save(`Счет-протокол по оказанным мед. услугам, ${patient.full_name}, ${date}.pdf`)
}

export const createPurchaseOrderDentalLabWorkDocumentForPurchaseOrder = async purchaseOrderId => {
  let purchaseOrder = null
  await getPurchaseOrderInfo(purchaseOrderId).then(({ data }) => purchaseOrder = data)
  if (!purchaseOrder) {
    showFailedOperationToast(
      'Ошибка формирования документа. Пожалуйста, обратитесь в службу технической поддержки'
    )
    return
  }

  const dentalLaboratory = purchaseOrder.dental_laboratory
  const patient = purchaseOrder.patient
  const doctor = purchaseOrder.doctor
  const orderNumber = purchaseOrder.order_number ? purchaseOrder.order_number : ''
  let pdfFont = 'Times-Roman'
  const pdfFontBold = 'bold'
  const pdfFontNormal = 'normal'
  const pdfMargin = 8
  const pdfFontSize = 11
  const pdfRowHeight = 7
  const { formattedTeethList } = useTeeth()
  const { getScaledByHeightImage } = useFormatter()
  const date = moment(parseInt(purchaseOrder.filling_date)).format('LL')
  let pdfFile = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true })
  const pdfWidth = pdfFile.internal.pageSize.width
  const scaleFactor = pdfFile.internal.scaleFactor - 2
  let text = ''

  pdfFile = mixins.methods.getPdfDocumentHeader(pdfFile, dentalLaboratory, pdfFont, pdfMargin)
  let y = mixins.methods.getPositionAfterHeader(
    dentalLaboratory, 42, false
  )

  pdfFont = 'Times-Roman'
  pdfFile.setFont(pdfFont, pdfFontBold)
  pdfFile.setFontSize(13.5)
  y += pdfRowHeight * 1.5
  pdfFile.text('Заказ-наряд на выполнение зуботехнических работ', pdfWidth / 2, y, 'center')

  pdfFile.setFont(pdfFont, pdfFontBold)
  pdfFile.setFontSize(pdfFontSize)

  y += pdfRowHeight * 1.5
  text = 'Дата:'
  pdfFile.text(text, pdfMargin, y)
  let offsetX = pdfMargin + getTextWidth(pdfFile, text) + 2
  pdfFile.setFont(pdfFont, pdfFontNormal)
  pdfFile.text(date, offsetX, y)
  y += pdfFile.getTextDimensions(text).h + scaleFactor

  pdfFile.setFont(pdfFont, pdfFontBold)
  text = 'Номер заказа:'
  pdfFile.text(text, pdfMargin, y)
  offsetX = pdfMargin + getTextWidth(pdfFile, text) + 2
  pdfFile.setFont(pdfFont, pdfFontNormal)
  pdfFile.text(orderNumber, offsetX, y)
  y += pdfFile.getTextDimensions(text).h + scaleFactor

  pdfFile.setFont(pdfFont, pdfFontBold)
  text = 'Заказчик:'
  pdfFile.text(text, pdfMargin, y)
  offsetX = pdfMargin + getTextWidth(pdfFile, text) + 2
  pdfFile.setFont(pdfFont, pdfFontNormal)
  const clientName = purchaseOrder.client ? purchaseOrder.client.name : ''
  pdfFile.text(clientName, offsetX, y)
  y += pdfFile.getTextDimensions(text).h + scaleFactor

  pdfFile.setFont(pdfFont, pdfFontBold)
  text = 'Врач:'
  pdfFile.text(text, pdfMargin, y)
  offsetX = pdfMargin + getTextWidth(pdfFile, text) + 2
  pdfFile.setFont(pdfFont, pdfFontNormal)
  const doctorFIO = doctor ? doctor.full_name : ''
  pdfFile.text(doctorFIO, offsetX, y)
  y += pdfFile.getTextDimensions(text).h + scaleFactor

  pdfFile.setFont(pdfFont, pdfFontBold)
  text = 'Пациент:'
  pdfFile.text(text, pdfMargin, y)
  offsetX = pdfMargin + getTextWidth(pdfFile, text) + 2
  pdfFile.setFont(pdfFont, pdfFontNormal)
  const patientFIO = patient ? patient.full_name : ''
  pdfFile.text(patientFIO, offsetX, y)
  y += pdfFile.getTextDimensions(text).h + scaleFactor
  
  pdfFile.setFont(pdfFont, pdfFontBold)
  text = 'Желаемая дата получения:'
  pdfFile.text(text, pdfMargin, y)
  offsetX = pdfMargin + getTextWidth(pdfFile, text) + 2
  pdfFile.setFont(pdfFont, pdfFontNormal)
  const desiredReceiptDate = purchaseOrder.desired_receipt_date
    ? moment(parseInt(purchaseOrder.desired_receipt_date)).format('LL') : ''
  pdfFile.text(desiredReceiptDate, offsetX, y)
  y += pdfRowHeight * 1.5

  if (purchaseOrder.schema) {
    text = 'Схема протезирования'
    pdfFile.text(text, pdfMargin, y)
    y += pdfRowHeight * 1.5
    const width = pdfWidth - (pdfMargin * 4)
    const scaling_x = width / purchaseOrder.schema.width
    const height = purchaseOrder.schema.height * scaling_x
    pdfFile.setFontSize(10)
    pdfFile.addImage(purchaseOrder.schema.src, 'png', pdfMargin * 2, y, width, height)
    y += height + pdfRowHeight * 1.5
  }

  pdfFile.setFontSize(pdfFontSize)
  text = 'Индивидуальные сведения о работе'
  pdfFile.text(text, pdfMargin, y)
  y += 3

  let rows = [
    [
      { content: '№ зуба, группа зубов', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Виды работ', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Тип материала', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Вид работ на имплантатах', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Система имплантатов', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Дополнительная информация', styles: { halign: 'center', valign: 'middle' } },
    ]
  ]

  if (purchaseOrder.individual_information.length) {
    purchaseOrder.individual_information.forEach(ii => {
      const teeth = ii.teeth ? ii.teeth.length > 1 ? formattedTeethList(ii.teeth).join(', ') : ii.teeth[0] : ''
      const workTypes = ii.work_types && ii.work_types.length ? ii.work_types.map(i => i.name).join(', ') : ''
      const materialTypes = ii.material_types && ii.material_types.length
        ? ii.material_types.map(i => i.name).join(', ') : ''
      const implantWorkTypes = ii.implant_work_types && ii.implant_work_types.length
        ? ii.implant_work_types.map(i => i.name).join(', ') : ''
      const implantSystems = ii.implant_systems && ii.implant_systems.length
        ? ii.implant_systems.map(i => i.name).join(', ') : ''
      const additionalInfo = ii.additional_info ? ii.additional_info : ''
      rows.push([
        { content: teeth, styles: { halign: 'center', valign: 'middle' } },
        { content: workTypes, styles: { halign: 'center', valign: 'middle' } },
        { content: materialTypes, styles: { halign: 'center', valign: 'middle' } },
        { content: implantWorkTypes, styles: { halign: 'center', valign: 'middle' } },
        { content: implantSystems, styles: { halign: 'center', valign: 'middle' } },
        { content: additionalInfo },
      ])
    })
  } else {
    rows.push([{ content: 'Список сведений пуст', styles: { halign: 'center', valign: 'middle' }, colSpan: 6 }])
  }

  pdfFile.autoTable({
    theme: 'grid',
    startY: y,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: { font: 'OpenSans-Regular', fontSize: 9 },
    didParseCell: e => e.cell.styles.font = e.row.index > 0 ? e.cell.styles.font : 'OpenSans-Bold',
    didDrawPage: e => y = (e.cursor.y + pdfRowHeight),
  })

  text = 'Цвет'
  pdfFile.text(text, pdfMargin, y)
  y += 3

  rows = [
    [
      { content: 'Цвет реставрации', styles: { halign: 'center', valign: 'middle' }, colSpan: 2 },
      { content: 'Цвет культи и коронки', styles: { halign: 'center', valign: 'middle' }, colSpan: 2 },
    ],
    [
      { content: 'Окрашивать фиссуры', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Окрашивать шейки', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Цвет культи', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Цвет коронки', styles: { halign: 'center', valign: 'middle' } },
    ],
    [
      { content: purchaseOrder.fissure_staining ? 'Да' : 'Нет', styles: { halign: 'center', valign: 'middle' } },
      { content: purchaseOrder.necks_painting ? 'Да' : 'Нет', styles: { halign: 'center', valign: 'middle' } },
      {
        content: purchaseOrder.stump_color ? purchaseOrder.stump_color.name : '-',
        styles: { halign: 'center', valign: 'middle' }
      },
      {
        content: purchaseOrder.stump_material ? purchaseOrder.stump_material.name : '-',
        styles: { halign: 'center', valign: 'middle' }
      },
    ]
  ]

  pdfFile.autoTable({
    theme: 'grid',
    startY: y,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: { font: 'OpenSans-Regular', fontSize: 9 },
    didParseCell: e => e.cell.styles.font = e.row.index > 0 ? e.cell.styles.font : 'OpenSans-Bold',
    didDrawPage: e => y = (e.cursor.y + pdfRowHeight),
  })

  text = 'Дополнительно'
  pdfFile.text(text, pdfMargin, y)
  y += 3

  const gingivalSuppleness = {
    low: 'Низкая',
    medium: 'Средняя',
    high: 'Высокая'
  }
  rows = [
    [
      { content: 'Дизайн промежуточной части', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Податливость десны', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Изготовление Wax-Up', styles: { halign: 'center', valign: 'middle' } },
    ],
    [
      {
        content: purchaseOrder.intermediate_part_design ? purchaseOrder.intermediate_part_design.toString() : '',
        styles: { halign: 'center', valign: 'middle', minCellHeight: 12 }
      },
      {
        content: purchaseOrder.gingival_suppleness ? gingivalSuppleness[purchaseOrder.gingival_suppleness] : '',
        styles: { halign: 'center', valign: 'middle' }
      },
      { content: purchaseOrder.wax_up ? 'Да' : 'Нет', styles: { halign: 'center', valign: 'middle' } },
    ]
  ]

  const images = {
    1: toothVariant1Src,
    2: toothVariant2Src,
    3: toothVariant3Src,
    4: toothVariant4Src,
    5: toothVariant5Src,
  }
  pdfFile.autoTable({
    theme: 'grid',
    startY: y,
    margin: pdfMargin,
    body: rows,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: { font: 'OpenSans-Regular', fontSize: 9 },
    didParseCell: e => e.cell.styles.font = e.row.index > 0 ? e.cell.styles.font : 'OpenSans-Bold',
    didDrawCell: data => {
      if (data.row.index === 1 && data.column.index === 0 && data.cell.raw.content) {
        const imgSrc = images[parseInt(data.cell.raw.content)]
        const imgInCellSize = 8
        const [w, h] = getScaledByHeightImage(26, 36, imgInCellSize)
        pdfFile.addImage(
          imgSrc, data.cell.x + (data.cell.width - w) / 2, data.cell.y + (data.cell.height - h) / 2, w, h
        )
      }
    },
    didDrawPage: e => y = (e.cursor.y + pdfRowHeight),
  })

  const comment = purchaseOrder.comment ? purchaseOrder.comment : ''
  text = 'Комментарий к работе:'
  pdfFile.text(text, pdfMargin, y)
  y = textToLines(comment, text, pdfFile, pdfMargin, y, pdfFont, false, scaleFactor, true)

  const parts = ['Заказ-наряд на выполнение зуботех. работ']
  if (orderNumber) {
    parts.push(orderNumber)
  }
  parts.push(date)
  pdfFile.save(`${parts.join(', ')}.pdf`)
}

export const createInvoiceForPurchaseOrder = async purchaseOrderId => {
  await store.dispatch(
    'dentalLaboratory/getPurchaseOrderActInvoiceExcelFile', {purchaseOrderId}
  )
    .then(({ data }) => {
      const fileName = `Счет (зуботехнические работы).xlsx`
      saveExcelFileFromResponse(data, fileName)
    })
    .catch(() => showFailedOperationToast('Не удалось сформировать счет'))
}

export const createWaybillForPurchaseOrder = async purchaseOrderId => {
  await store.dispatch(
    'dentalLaboratory/getPurchaseOrderActWaybillExcelFile', {purchaseOrderId}
  )
    .then(({ data }) => {
      const fileName = `Накладная (зуботехнические работы).xlsx`
      saveExcelFileFromResponse(data, fileName)
    })
    .catch(() => showFailedOperationToast('Не удалось сформировать накладную'))
}

export const exportIntegratorSubscriptionInvoice = async invoiceId => {
  await store.dispatch('integrator/getSubscriptionInvoice', invoiceId)
    .then(({ data }) => {
      if (!data || !data.integrator) {
        showFailedOperationToast(
          'Ошибка формирования счет-протокола для продления подписки.' +
          ' Пожалуйста, обратитесь в службу технической поддержки'
        )
        return
      }
      const description = `Предоставление доступа к ПО "ПрофманХэлс"`
      makeIntegratorInvoice(
        'продление подписки', description, data.integrator, data.created_at, data.account_number,
        data.subscription_amount, 3
      )
    })
}

export const exportIntegratorSmsBalanceInvoice = async invoiceId => {
  await store.dispatch('integrator/getSmsBalanceInvoice', invoiceId)
    .then(({ data }) => {
      if (!data || !data.integrator) {
        showFailedOperationToast(
          'Ошибка формирования счет-протокола для пополнения sms-баланса.' +
          ' Пожалуйста, обратитесь в службу технической поддержки'
        )
        return
      }
      const description =
        `Пополнение внутреннего баланса системы ${getProgramVersionName()} для отправки уведомлений (${data.point_count} баллов)`
      makeIntegratorInvoice(
        'пополнение SMS-баланса', description, data.integrator, data.created_at, data.account_number, data.amount,
        5
      )
    })
}

const makeIntegratorInvoice = async (
  title, description, integrator, createdAt, accountNumber, amount, expireDays
) => {
  const createdDate = moment(parseInt(createdAt))
  const date = createdDate.format('LL')
  let pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true })
  const docWidth = pdf.internal.pageSize.width
  const scaleFactor = pdf.internal.scaleFactor
  const marginTop = 13.5
  const marginLeft = 11
  const marginRight = 6.5
  const fontStyleBold = 'bold'
  const fontNormal = 'OpenSans-Regular'
  const fontBold = 'OpenSans-Bold'
  const fontBoldItalic = 'OpenSans-BoldItalic'
  const fontSize = 10
  const fontSizeLarge = 12
  const fontSizeSmall = 8
  const bankInfo = []
  let positionY = marginTop

  if (integrator.current_account) {
    bankInfo.push(`р/с № ${integrator.current_account.trim()}`)
  }
  if (integrator.bank) {
    bankInfo.push(integrator.bank.trim())
  }
  if (integrator.bic) {
    bankInfo.push(`БИК ${integrator.bic.trim()}`)
  }
  let rows = [
    [
      { content: 'Поставщик и его адрес:', styles: { cellWidth: 19, fontStyle: fontStyleBold, fontSize: 7 } },
      {
        content: 'Частное предприятие "Люди Создают"', styles: { fontStyle: fontStyleBold, fontSize: 9, cellWidth: 80 }
      },
      {
        content: 'СЧЕТ-ПРОТОКОЛ', styles: { halign: 'center', valign: 'middle', fontStyle: fontStyleBold, fontSize: 12 }
      },
    ],
    [
      { content: '' },
      { content: '220055 г. Минск,уп.Каменногорская 47/42', styles: { fontSize: 9 } },
      {
        content:
          `согласования свободных договорных цен на услуги ${accountNumber} от ${createdDate.format('LL')}`,
        rowSpan: 2,
        styles: { halign: 'center', valign: 'middle', fontStyle: fontStyleBold, fontSize: 9 }
      }
    ],
    [{ content: '' }, { content: '№ BY06ALFA30122757640010270000, Банк: ЗАО', styles: { fontSize: 9 } }],
    [{ content: '' }, { content: '"Альфа-Банк•. Отдепение •на Притыцкого" в г.', styles: { fontSize: 9 } }],
    [{ content: '' }, { content: 'Минске г. Минск, уп. Притыцкого, 39 БИК ALFAВY2X', styles: { fontSize: 9 } }],
    [{ content: '' }, { content: 'тел.+375 (44) 781-50-04', styles: { fontSize: 9 } }],
    [{ content: '' }, { content: 'УНП 193497888', styles: { fontSize: 9 } }],
    [
      { content: 'Покупатель и его адрес:', styles: { fontStyle: fontStyleBold, fontSize: 7 } },
      { content: integrator.name, colSpan: 2, styles: { fontStyle: fontStyleBold, fontSize: 9 } },
    ],
    [{ content: '' }, { content: integrator.address ? integrator.address : '', colSpan: 2, styles: { fontSize: 9 } }],
    [{ content: '' }, { content: bankInfo.length ? bankInfo.join(', ') : '', colSpan: 2, styles: { fontSize: 9 } }],
    [{ content: '' }, { content: integrator.unp ? `УНП ${integrator.unp}` : '', colSpan: 2, styles: { fontSize: 9 } }],
    [
      {
        content: `Основание: ${integrator.software_usage_agreement
          ? `Договор Пользования Программным Обеспечением ${integrator.software_usage_agreement}` : ''}`,
        colSpan: 3,
        styles: { fontStyle: fontStyleBold, fontSize: 7 }
      },
    ],
  ]
  pdf.autoTable({
    theme: 'grid',
    startY: positionY,
    margin: { top: marginTop, right: marginRight, bottom: 0, left: marginLeft },
    body: rows,
    bodyStyles: { lineColor: [0, 0, 0], textColor: [0, 0, 0] },
    styles: { font: 'Microsoft-Sans-Serif', fontSize: 9 },
    didParseCell: hook => {
      if (!hook.row.index) {
        hook.cell.styles.lineWidth = { top: 0.1, right: 0.1, bottom: 0, left: 0.1 }
      } else if (hook.row.index >= 1 && hook.row.index <= 6) {
        hook.cell.styles.lineWidth = { top: 0, right: 0.1, bottom: hook.row.index === 6 ? 0.1 : 0, left: 0.1 }
      } else if (hook.row.index >= 7 && hook.row.index <= 10) {
        hook.cell.styles.lineWidth = {
          top: hook.row.index === 7 ? 0.1 : 0, right: 0.1, bottom: hook.row.index === 10 ? 0.1 : 0, left: 0.1
        }
      }
      hook.cell.styles.cellPadding = {
        top: ![0, 7, 11].includes(hook.row.index) ? 0 : 0.8,
        right: 0.8,
        bottom: ![0, 7, 11].includes(hook.row.index) ? 0 : 0.4,
        left: 0.8
      }
    },
    didDrawPage: hook => positionY = hook.cursor.y,
  })
  amount = amount ? amount : ''
  rows = [
    [
      { content: 'Предмет счета', styles: { halign: 'center', valign: 'middle', cellWidth: 99 }},
      {
        content: 'Стоимость работ (услуг) без НДС, BYN', styles: { halign: 'center', valign: 'middle', cellWidth: 29 }
      },
      { content: 'Ставка НДС, %', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Сумма НДС, BYN', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Сумма с НДС, BYN', styles: { halign: 'center', valign: 'middle' } },
    ],
    [
      { content: description, styles: {} },
      { content: mixins.methods.formatPrice(amount, false, true), styles: { halign: 'right' } },
      { content: 'Без НДС', styles: { halign: 'center' } },
      { content: '' },
      {
        content: mixins.methods.formatPrice(amount, false, true), styles: { halign: 'right' }
      },
    ],
    [
      { content: 'Итого:', styles: { font: fontBold } },
      {
        content: mixins.methods.formatPrice(amount, false, true),
        styles: { halign: 'right', font: fontBold }
      },
      { content: 'x', styles: { halign: 'center', font: fontBold } },
      { content: '' },
      {
        content: mixins.methods.formatPrice(amount, false, true),
        styles: { halign: 'right', font: fontBold }
      },
    ]
  ]
  pdf.autoTable({
    theme: 'grid',
    startY: positionY + 5,
    margin: { top: 0, right: marginRight, bottom: 0, left: marginLeft },
    body: rows,
    bodyStyles: {
      cellPadding: { top: 0.8, right: 0.8, bottom: 0.4, left: 0.8 }, lineColor: [0, 0, 0], textColor: [0, 0, 0]
    },
    styles: { font: 'Microsoft-Sans-Serif', fontSize: 9 },
    didDrawPage: hook => positionY = hook.cursor.y,
  })

  let text = 'Сумма НДС:'
  positionY += pdf.getTextDimensions(text).h + scaleFactor
  let textWidth = getTextWidth(pdf, text)
  let offset = marginLeft * 3 + textWidth
  pdf.setFont(fontBold)
  pdf.setFontSize(fontSize)
  pdf.text(text, marginLeft, positionY)
  pdf.text('Ноль белорусских рублей ноль копеек', offset, positionY)
  positionY += 1
  pdf.line(offset - 1, positionY, docWidth - marginRight, positionY)

  positionY += (pdf.getTextDimensions(text).h + scaleFactor) * 1.5
  text = 'Всего:'
  pdf.text(text, marginLeft, positionY)
  pdf.text(mixins.methods.capitalizeFirstLetter(mixins.methods.priceToText(amount)), offset, positionY)
  positionY += 1
  pdf.line(offset - 1, positionY, docWidth - marginRight, positionY)

  positionY += (pdf.getTextDimensions(text).h + scaleFactor) * 1.5
  pdf.setFont(fontBoldItalic)
  pdf.setFontSize(fontSizeLarge)
  pdf.text('ВНИМАНИЕ!!!', marginLeft, positionY)

  pdf.setFont(fontNormal)
  pdf.setFontSize(fontSize)
  pdf.text(
    `Счет действителен до: ${createdDate.add(expireDays, 'days').format('LL')}`, offset, positionY
  )

  positionY += (pdf.getTextDimensions(text).h + scaleFactor) * 1.5
  pdf.setFont(fontBold)
  pdf.text('1. Иметь при себе подписанный экземпляр счета-протокола', offset, positionY)

  positionY += (pdf.getTextDimensions(text).h + scaleFactor) / 1.5
  pdf.text('2. Знать УНП', offset, positionY)

  positionY += 10
  const scaling_y = 59 / 240
  const width = 286 * scaling_y
  pdf.addImage(stampSrc, 'png', marginLeft, positionY, width, 59)

  pdf.setFontSize(fontSizeSmall)
  positionY += 2.3
  offset = (docWidth / 2) + ((docWidth / 2) - width - marginLeft)
  pdf.text('Покупатель', offset + ((docWidth - marginRight - offset) / 2), positionY, 'center')
  positionY += 7.7
  pdf.setLineWidth(0.4)
  pdf.line(offset, positionY, docWidth - marginRight, positionY)
  positionY += 6.3
  pdf.line(offset, positionY, docWidth - marginRight, positionY)

  pdf.save(`Счет-протокол, ${title}, ${date}.pdf`)
}

export const createTreatmentDiaryDocument = async (
  treatmentDiary, patient, fileName, isLastVisiting = false
) => {
  const integratorId = getIntegratorId()
  if (integratorId) {
    await store.dispatch('info/getIntegrator', integratorId)
      .then(response => {
        const integrator = response.data
        if (!treatmentDiary || !patient || !integrator) {
          showCommonToast(
            `Ошибка формирования ${isLastVisiting ? 'последнего посещения' : 'дневника лечения'}`,
            'Пожалуйста, обратитесь в службу технической поддержки',
            'danger'
          )
          return false
        }

        let pdfRowHeight = 7
        let pdfMargin = 8
        let pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', putOnlyUsedFonts: true })
        let pdfCurrentPositionY = 10
        const pdfFontBold = 'OpenSans-Bold'
        const pdfFontNormal = 'OpenSans-Regular'
        const pdfHeight = pdf.internal.pageSize.height
        const pdfWidth = pdf.internal.pageSize.width
        const savedPdfMargin = pdfMargin
        const diaryPaddingX = 2
        const diaryContentRowHeight = pdfRowHeight - 2
        const treatmentMedicalHistory = treatmentDiary.treatment_medical_history
          ? treatmentDiary.treatment_medical_history : ''
        const showEmptyFields = isLastVisiting ? lastVisitShowEmptyField() : treatmentDiaryShowEmptyField()
        const showCredentials = isLastVisiting ? lastVisitShowCredentials() : treatmentDiaryShowCredentials()
        const hideDoctorColumn = isLastVisiting
          ? lastVisitHideDoctorColumn() : treatmentDiaryHideDoctorColumn()
        const treatmentDate = treatmentDiary.date
          ? moment(parseInt(treatmentDiary.date)).format('DD.MM.YYYY') : null

        // header
        if (showCredentials) {
          pdf = mixins.methods.getPdfDocumentHeader(pdf, integrator, pdfFontNormal, pdfMargin)
          pdfCurrentPositionY = mixins.methods.getPositionAfterHeader(integrator, 42, false)
        } else {
          pdf.setFontSize(8)
        }

        pdf.setFont(pdfFontBold)
        pdf.text('Дневник посещения', pdfWidth / 2, pdfCurrentPositionY, 'center')
        pdfCurrentPositionY += pdfRowHeight

        // Patient info
        pdf.setFont(pdfFontBold)
        let label = 'Пациент:'
        pdf.text(label, pdfMargin, pdfCurrentPositionY)
        const left = pdfMixins.methods.getTextWidth(pdf, label) + pdfMargin + 1
        pdf.setFont(pdfFontNormal)
        pdf.text(patient.full_name, left, pdfCurrentPositionY)
        pdfCurrentPositionY += pdfRowHeight / 2

        // Treatment diary
        pdfMargin = savedPdfMargin
        const complaint = treatmentDiary.complaint ? treatmentDiary.complaint : ''
        const anamnesis = treatmentDiary.anamnesis ? treatmentDiary.anamnesis : ''
        const clinicalPicture = treatmentDiary.clinical_picture ? treatmentDiary.clinical_picture : ''
        const researchResult = treatmentDiary.research_result ? treatmentDiary.research_result : ''
        const recommendations = treatmentDiary.recommendations ? treatmentDiary.recommendations : ''
        const diaryIsDental = treatmentDiary.type === 'DENTAL'
        let teethNumbers = []
        treatmentDiary.status_teeth.map(t => t.teeth ? teethNumbers.push(t.teeth.number) : null)
        teethNumbers = teethNumbers.join(', ')
        const diagnosisCustomName = getDiagnosisCustomName(treatmentDiary) + ''
        const descriptionDiagnosis = treatmentDiary.description_diagnosis
          ? treatmentDiary.description_diagnosis : ''

        // Treatment diary
        const rightBlockWidth = hideDoctorColumn ? 0 : 50
        pdf.setDrawColor(0, 0, 0, 1)
        pdf.line(pdfMargin, pdfCurrentPositionY, pdfWidth - pdfMargin, pdfCurrentPositionY)
        pdfCurrentPositionY += pdfRowHeight

        let text = diaryIsDental
          ? 'Дата, жалобы, клиническая картина, результаты исследований, диагноз, план лечения, лечение'
          : 'Дата, диагноз, план лечения, лечение'
        let textMaxWidth = pdfWidth - ((pdfMargin * 2) + rightBlockWidth)
        let textLines = pdf.splitTextToSize(text, textMaxWidth)
        pdf.text(
          textLines, hideDoctorColumn ? pdfWidth / 2 : (pdfWidth / 1.6) - rightBlockWidth,
          pdfCurrentPositionY - 1.4, 'center'
        )

        if (!hideDoctorColumn) {
          let rightBlockText = 'Фамилия, имя, отчество врача, подпись'
          let rightBlockLines = pdf.splitTextToSize(rightBlockText, rightBlockWidth - 5)
          pdf.text(
            rightBlockLines, pdfWidth - (pdfMargin + (rightBlockWidth / 2)), pdfCurrentPositionY - 2.5,
            'center'
          )
        }

        let textDimension = pdf.getTextDimensions(text, { maxWidth: textMaxWidth })
        let diaryContentLineTop = (pdfCurrentPositionY - pdfRowHeight)

        pdfRowHeight -= 3
        pdfCurrentPositionY += textDimension.h
        pdf.line(pdfMargin, pdfCurrentPositionY, pdfWidth - pdfMargin, pdfCurrentPositionY)
        const secondHorizontalLineY = pdfCurrentPositionY

        // Diary content
        pdf.setFont(pdfFontNormal)
        pdfCurrentPositionY += diaryContentRowHeight
        pdf.text(treatmentDate ? treatmentDate : '', pdfMargin + diaryPaddingX, pdfCurrentPositionY + 1)
        pdfCurrentPositionY += diaryContentRowHeight
        if (hideDoctorColumn) {
          pdf.line(pdfMargin, pdfCurrentPositionY, pdfWidth - pdfMargin, pdfCurrentPositionY)
        } else {
          pdf.line(pdfMargin, pdfCurrentPositionY, pdfWidth - (pdfMargin + rightBlockWidth), pdfCurrentPositionY)
        }
        pdfCurrentPositionY += diaryContentRowHeight
        pdfMargin += diaryPaddingX
        if (diaryIsDental) {
          if (showEmptyFields || complaint) {
            pdfCurrentPositionY = addTextWithMultiRows(
              pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Жалобы:', complaint, rightBlockWidth,
              false, true
            )
          }
          if (showEmptyFields || anamnesis) {
            pdfCurrentPositionY = addTextWithMultiRows(
              pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Анамнез:', anamnesis, rightBlockWidth,
              false, true
            )
          }
          if (showEmptyFields || clinicalPicture) {
            pdfCurrentPositionY = addTextWithMultiRows(
              pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Клиническая картина:', clinicalPicture,
              rightBlockWidth, false, true
            )
          }
          if (showEmptyFields || researchResult) {
            pdfCurrentPositionY = addTextWithMultiRows(
              pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Результаты исследования:', researchResult,
              rightBlockWidth, false, true
            )
          }
        }

        if (diaryIsDental) {
          pdfCurrentPositionY = addTextWithMultiRows(
            pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Зуб (-ы):', teethNumbers, rightBlockWidth,
            false, true
          )
        }

        pdfCurrentPositionY = addTextWithMultiRows(
          pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Диагноз:', diagnosisCustomName, rightBlockWidth,
          false, true
        )

        if (diaryIsDental) {
          if (showEmptyFields || descriptionDiagnosis) {
            pdfCurrentPositionY = addTextWithMultiRows(
              pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Описание диагноза:', descriptionDiagnosis,
              rightBlockWidth, false, true
            )
          }
        }

        let treatmentMedicalHistoryOne = treatmentMedicalHistory
        let treatmentMedicalHistoryTwo = ''
        const maxLen = hideDoctorColumn
          ? pdfWidth - (pdfMargin * 4) : pdfWidth - (pdfMargin * 3.1 + rightBlockWidth)
        const splitHistory = treatmentMedicalHistoryOne.split(' ')
        const rows = []
        let row = ''
        splitHistory.forEach((i, idx) => {
          const tempStr = idx ? `${row} ${i}` : i
          if (pdf.getTextWidth(tempStr) <= (idx ? maxLen : maxLen - 24.5)) {
            row += row ? ` ${i}` : i
          } else {
            rows.push(row)
            row = i
          }
        })
        if (row) {
          rows.push(row)
        }
        const rowsCount = rows.length + 1
        const rowsHeight = rowsCount * pdfRowHeight
        let diff = pdfHeight - (rowsHeight + pdfCurrentPositionY + pdfRowHeight + 10)
        if (diff < 0) {
          diff = Math.abs(diff)
          const val = Math.ceil(diff / pdfRowHeight)
          const resultHistoryOne = rows.slice(0, rowsCount - val)
          const resultHistoryTwo = rows.slice(-val + 1)
          treatmentMedicalHistoryOne = resultHistoryOne.join(' ')
          treatmentMedicalHistoryTwo = resultHistoryTwo.join(' ')
        }

        pdfCurrentPositionY = addTextWithMultiRows(
          pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Лечение:', treatmentMedicalHistoryOne,
          rightBlockWidth, false, true
        )

        if (diaryIsDental) {
          if (showEmptyFields || recommendations) {
            pdfCurrentPositionY = addTextWithMultiRows(
              pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, 'Рекомендации:', recommendations, rightBlockWidth,
              false, true
            )
          }
        }

        let maxTableY = pdfCurrentPositionY - 1
        pdf.line(savedPdfMargin, diaryContentLineTop, savedPdfMargin, maxTableY)
        if (!hideDoctorColumn) {
          pdf.line(
            pdfWidth - (savedPdfMargin + rightBlockWidth), diaryContentLineTop,
            pdfWidth - (savedPdfMargin + rightBlockWidth), maxTableY
          )
        }
        pdf.line(pdfWidth - savedPdfMargin, diaryContentLineTop, pdfWidth - savedPdfMargin, maxTableY)
        pdf.line(savedPdfMargin, maxTableY, pdfWidth - savedPdfMargin, maxTableY)

        // Right block Doctor name
        let doctorFullName = ''
        if (!hideDoctorColumn) {
          pdf.setFont(pdfFontNormal)
          doctorFullName = treatmentDiary.doctor
            ? treatmentDiary.doctor.full_name.replace(' ', '\n') : ''
          pdf.text(
            pdf.splitTextToSize(doctorFullName, rightBlockWidth - diaryPaddingX),
            pdfWidth - (savedPdfMargin + (rightBlockWidth / 2)),
            secondHorizontalLineY + ((maxTableY - secondHorizontalLineY) / 3.2),
            'center'
          )
        }

        if (treatmentMedicalHistoryTwo) {
          pdf.addPage()
          let startPositionY = 10
          pdfCurrentPositionY = startPositionY +  pdfRowHeight
          pdfCurrentPositionY = addTextWithMultiRows(
            pdf, pdfCurrentPositionY, pdfRowHeight, pdfMargin, '',
            treatmentMedicalHistoryTwo, rightBlockWidth, false, true
          )
          const maxTableY = pdfCurrentPositionY - 1
          pdf.line(savedPdfMargin, startPositionY, pdfWidth - savedPdfMargin, startPositionY)
          pdf.line(savedPdfMargin, startPositionY, savedPdfMargin, maxTableY)
          pdf.line(savedPdfMargin, startPositionY, savedPdfMargin, maxTableY)
          if (!hideDoctorColumn) {
            pdf.line(
              pdfWidth - (savedPdfMargin + rightBlockWidth), startPositionY,
              pdfWidth - (savedPdfMargin + rightBlockWidth), maxTableY
            )
          }
          pdf.line(pdfWidth - savedPdfMargin, startPositionY, pdfWidth - savedPdfMargin, maxTableY)
          pdf.line(savedPdfMargin, maxTableY, pdfWidth - savedPdfMargin, maxTableY)
          if (!hideDoctorColumn) {
            pdf.text(
              pdf.splitTextToSize(doctorFullName, rightBlockWidth - diaryPaddingX),
              pdfWidth - (savedPdfMargin + (rightBlockWidth / 2)),
              startPositionY + ((maxTableY - startPositionY) / 3.2),
              'center'
            )
          }
        }

        pdf.save(`${fileName} - ` + patient.full_name + `${treatmentDate ? ` (${treatmentDate})` : ''}.pdf`)
      })
  }
}

export const createAppointmentTalon = async appointment => {
  let items = []
  if (appointment.service_time_selected_complexes && appointment.service_time_selected_complexes.length) {
    items = appointment.service_time_selected_complexes.map(i => ({...i, count: 1}))
  }
  if (appointment.service_time_selected_services && appointment.service_time_selected_services.length) {
    items = appointment.service_time_selected_services.map(i => ({...i, count: 1}))
  }
  const { getPatientDiscountCardByCardId } = usePatient()
  const discountCard = appointment.patient.discount_card_id
    ? getPatientDiscountCardByCardId(appointment.patient.discount_card_id) : null
  const discountPercent = discountCard ? discountCard.percent : 0
  const errorTitle = 'Ошибка формирования талона'
  const { getCurrentIntegrator } = useIntegrator()
  const integrator = await getCurrentIntegrator().then(({ data }) => data)
  await createCashReceipt(
    appointment.id, 'Талон ', appointment.start, appointment.patient, appointment.doctor, integrator, items,
    discountPercent, [], errorTitle
  )
}

export const createTreatmentDiaryCashReceipt = async (
  treatmentDiary, patient, discountPercent, paymentMethods = []
) => {
  const { getTreatmentDiaryInfo } = useTreatmentDiary()
  const info = await getTreatmentDiaryInfo(treatmentDiary.id).then(({ data }) => data)
  const errorTitle = 'Ошибка формирования кассового чека'
  if (!info) {
    showCommonToast(errorTitle, 'Пожалуйста, обратитесь в службу технической поддержки', 'danger')
  }
  let items = []
  if (info.dental_services_history && info.dental_services_history.length) {
    items = info.dental_services_history.map(s => ({
      name: s.name,
      price: s.total_price,
      count: s.count
    }))
  }
  await createCashReceipt(
    info.act_number, 'Кассовый чек по акту №', info.date, info.patient, treatmentDiary.doctor,
    info.patient.integrator, items, discountPercent, paymentMethods, errorTitle
  )
}

const createCashReceipt = async (
  docNumber, docLabel, date, patient, doctor, integrator, positions, discountPercent, paymentMethods, errorTitle
) => {
  if (!docNumber || !patient || !doctor || !integrator) {
    showCommonToast(errorTitle, 'Пожалуйста, обратитесь в службу технической поддержки', 'danger')
    return
  }
  let marginTop = 10
  let margin = 6
  let doc = new jsPDF({
    orientation: 'portrait', unit: 'mm', format: [58, 297], putOnlyUsedFonts: true
  })
  let y = marginTop
  const pdfFontBold = 'OpenSans-Bold'
  const pdfFontNormal = 'OpenSans-Regular'
  const width = doc.internal.pageSize.width
  const height = doc.internal.pageSize.height
  const scaleFactor = doc.internal.scaleFactor
  const offset = 2
  const lineHeightFactor = doc.getLineHeightFactor()

  let text = integrator.name
  let textMaxWidth = width - ((margin * 2))
  doc.setFont(pdfFontBold)
  doc.setFontSize(10)
  let textLines = doc.splitTextToSize(text, textMaxWidth)
  doc.text(textLines, (width / 2), y, 'center')

  doc.setFont(pdfFontNormal)
  doc.setFontSize(8.8)
  y += (doc.getLineHeight(textLines[0]) / scaleFactor) * textLines.length
  doc.setLineDashPattern([1, 1], 0)
  doc.line(margin, y, width, y, 'DF')

  y += (lineHeightFactor * 2) + offset
  text = 'Адрес:'
  doc.text(text, margin + 1, y)
  textMaxWidth = width / 2 - 2
  const address = integrator.address ? integrator.address : ''
  textLines = doc.splitTextToSize(address, textMaxWidth)
  doc.text(textLines, width - 2, y, { align: 'right' })

  y += (doc.getLineHeight(textLines[0]) / scaleFactor) * textLines.length
  text = 'Документ:'
  doc.text(text, margin + 1, y)
  doc.text(docNumber.toString(), width - 2, y, { align: 'right' })

  y += doc.getLineHeight(text) / scaleFactor
  text = 'Дата визита:'
  doc.text(text, margin + 1, y)
  doc.text(mixins.methods.formatDateAsDayMonthYearTime(date), width - 2, y, { align: 'right' })

  y += doc.getLineHeight(text) / scaleFactor / 2
  doc.setLineDashPattern([1, 1], 0)
  doc.line(margin, y, width, y, 'DF')

  y += (lineHeightFactor * 2) + offset
  text = 'Клиент:'
  doc.text(text, margin + 1, y)
  const patientFullName = patient.full_name ? patient.full_name : ''
  textLines = doc.splitTextToSize(patientFullName, textMaxWidth)
  doc.text(textLines, width - 2, y, { align: 'right' })

  y += (doc.getLineHeight(textLines[0]) / scaleFactor) * textLines.length
  text = 'Телефон:'
  doc.text(text, margin + 1, y)
  const patientPhone = patient.full_phone ? patient.full_phone.replace(' ', '') : ''
  doc.text(patientPhone, width - 2, y, { align: 'right' })

  y += doc.getLineHeight(text) / scaleFactor/ 2
  doc.setLineDashPattern([1, 1], 0)
  doc.line(margin, y, width, y, 'DF')

  y += (lineHeightFactor * 2) + offset
  text = 'Сотрудник:'
  doc.text(text, margin + 1, y)
  const doctorFullName = doctor.full_name ? doctor.full_name : ''
  textLines = doc.splitTextToSize(doctorFullName, textMaxWidth)
  doc.text(textLines, width - 2, y, { align: 'right' })

  y += (doc.getLineHeight(textLines[0]) / scaleFactor) * textLines.length - (doc.getLineHeight(textLines[0]) / scaleFactor / 2)
  doc.setLineDashPattern([1, 1], 0)
  doc.line(margin, y, width, y, 'DF')

  y += (lineHeightFactor * 2) + offset
  text = 'Дата:'
  doc.text(text, margin + 1, y)
  doc.text(moment().format('DD.MM.YYYY HH:mm'), width - 2, y, { align: 'right' })

  let totalCount = 0
  if (positions.length) {
    y += doc.getLineHeight(text) / scaleFactor/ 2
    doc.setLineDashPattern([1, 1], 0)
    doc.line(margin, y, width, y, 'DF')

    y += (lineHeightFactor * 2) + offset
    let sum = 0, discountSum = 0, totalSum = 0
    positions.forEach(i => {
      const name = i.name ? i.name : ''
      const price = i.price ? i.price : 0
      const count = i.count ? i.count : 0
      const totalServicePrice = count * i.price
      const discountPrice = totalServicePrice && discountPercent
        ? parseFloat(mixins.methods.formatPrice(((totalServicePrice / 100) * discountPercent), false))
        : 0
      const totalPrice = parseFloat(
        mixins.methods.formatPrice(totalServicePrice - discountPrice, false)
      )
      const iRows = [
        {label: name, value: null, font: pdfFontBold},
        {label: 'Цена:', value: mixins.methods.formatPrice(price, true), font: pdfFontNormal},
        {label: 'Количество:', value: count.toString(), font: pdfFontNormal},
        {
          label: 'Скидка:', value: mixins.methods.formatPrice(discountPrice, true),
          font: pdfFontNormal
        },
        {label: 'Итого:', value: mixins.methods.formatPrice(totalPrice, true), font: pdfFontNormal},
      ]
      iRows.forEach((r, idx) => {
        const rItemStep = (lineHeightFactor * 2) + offset
        let rTitleStep = 0
        doc.setFont(r.font)
        if (!idx) {
          textMaxWidth = width - margin - offset - 1
          textLines = doc.splitTextToSize(r.label, textMaxWidth)
          rTitleStep = (doc.getLineHeight(textLines[0]) / scaleFactor) * textLines.length
          const blockHeight = (4 * rItemStep) + rTitleStep
          if (y + blockHeight > height - marginTop) {
            doc.addPage()
            y = marginTop
          }
        }
        if (idx) {
          doc.text(r.label, margin + 1, y)
          doc.text(r.value, width - 2, y, { align: 'right' })
          y += rItemStep
        } else {
          doc.text(textLines, margin + 1, y)
          y += rTitleStep
        }
      })

      sum += price
      discountSum += discountPrice
      totalSum += totalPrice
      totalCount += count
    })

    const tRows = [
      {label: 'Итого', value: null, font: pdfFontBold},
      {label: 'Цена:', value: mixins.methods.formatPrice(sum, true), font: pdfFontNormal},
      {label: 'Количество:', value: totalCount.toString(), font: pdfFontNormal},
      {
        label: 'Скидка:', value: mixins.methods.formatPrice(discountSum, true),
        font: pdfFontNormal
      },
      {label: 'Итого:', value: mixins.methods.formatPrice(totalSum, true), font: pdfFontNormal},
    ]

    tRows.forEach((r, idx) => {
      if (!idx) {
        const rStep = (lineHeightFactor * 2) + offset
        const blockHeight = 5 * rStep
        if (y + blockHeight > height - marginTop) {
          doc.addPage()
          y = marginTop
        }
      }
      doc.setFont(r.font)
      doc.text(r.label, margin + 1, y)
      if (r.value !== null) {
        doc.text(r.value, width - 2, y, { align: 'right' })
      }
      y += (lineHeightFactor * 2) + offset
    })
  }

  if (paymentMethods && paymentMethods.length) {
    y -= doc.getLineHeight(text) / scaleFactor/ 2
    doc.setLineDashPattern([1, 1], 0)
    doc.line(margin, y, width, y, 'DF')

    y += (lineHeightFactor * 2) + offset
    text = 'Способы оплаты:'
    textMaxWidth = width - margin - offset - 1
    doc.setFont(pdfFontBold)
    textLines = doc.splitTextToSize(text, textMaxWidth)
    let rTitleStep = (doc.getLineHeight(textLines[0]) / scaleFactor) * textLines.length
    let blockHeight = (lineHeightFactor * 2) + offset + rTitleStep
    if (y + blockHeight > height - marginTop) {
      doc.addPage()
      y = marginTop
    }
    doc.text(text, margin + 1, y)

    doc.setFont(pdfFontNormal)
    y += (lineHeightFactor * 2) + offset
    let sum = 0, discountSum = 0, totalSum = 0
    paymentMethods.forEach(i => {
      const name = i.payment_method.name
      const amount = i.paid_amount
      const rItemStep = (lineHeightFactor * 2) + offset
      let rTitleStep = 0
      textMaxWidth = width - margin - offset - 1
      textLines = doc.splitTextToSize(`${name}:`, textMaxWidth)
      rTitleStep = (doc.getLineHeight(textLines[0]) / scaleFactor) * textLines.length
      const blockHeight = rItemStep + rTitleStep
      if (y + blockHeight > height - marginTop) {
        doc.addPage()
        y = marginTop
      }
      doc.text(textLines, margin + 1, y)
      doc.text(mixins.methods.formatPrice(amount, true), width - 2, y, { align: 'right' })
      y += rTitleStep
      sum += amount
    })

    text = 'Итого:'
    rTitleStep = doc.getLineHeight(text) / scaleFactor / 2
    blockHeight = (lineHeightFactor * 2) + offset + rTitleStep
    if (y + blockHeight > height - marginTop) {
      doc.addPage()
      y = marginTop
    }
    doc.text(text, margin + 1, y)
    doc.text(mixins.methods.formatPrice(sum, true), width - 2, y, { align: 'right' })
  }
  doc.save(`${docLabel}${docNumber}.pdf`)
}

export const createGeneralMedicalCard = (
  cardNumber, fullName, birthday, gender, documentIdentifier, residencePlace, address, workPlace, phone, integratorName,
  integratorAddress, medicalExamination = null
) => {
  const docFormat = generalMedicalCardPrintFormat()
  let doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: docFormat, putOnlyUsedFonts: true })
  const pdfFont = 'Times-Roman'
  const pdfFontBold = 'bold'
  const pdfFontNormal = 'normal'
  const docWidth = doc.internal.pageSize.width
  const docHeight = doc.internal.pageSize.height
  const docMarginLeft = 25
  const docMarginRight = 20

  let y = 10
  doc.setFont(pdfFont, pdfFontNormal)
  doc.setFontSize(10)
  let textItems = ['(наименование организации здравоохранения)', '(структурного (обособленного) подразделения)']
  let textWidth = doc.getTextWidth(integratorName)
  let minWidth = doc.getTextWidth(textItems[1])
  let offset = (textWidth >= minWidth ? 0 : minWidth - textWidth) / 2
  let scaleFactor = doc.internal.scaleFactor
  const maxTextXPosition = docMarginLeft + (minWidth > textWidth ? minWidth : textWidth)
  doc.text(integratorName, docMarginLeft + offset, y)
  doc.line(docMarginLeft, y + 1, maxTextXPosition, y + 1)
  y += doc.getLineHeight(integratorName) / scaleFactor
  
  let itemHeight = 0
  textItems.forEach(item => {
    const width = doc.getTextWidth(item)
    offset = (textWidth >= minWidth ? textWidth - width : minWidth - width) / 2
    doc.text(item, docMarginLeft + offset, y)
    itemHeight = doc.getLineHeight(item) / scaleFactor
    y += itemHeight
  })

  y = 10
  doc.setFontSize(11)
  let textRightOffset = 53
  textItems = [
    'Приложение', 'к постановлению', 'Министерства здравоохранения', 'Республики Беларусь', '05.12.2023 № 188'
  ]
  textItems.forEach(item => {
    doc.text(item, docWidth - docMarginRight - textRightOffset, y)
    itemHeight = doc.getLineHeight(item) / scaleFactor
    y += itemHeight
  })

  y += itemHeight
  let text = 'Форма 025/у-23'
  textRightOffset = 26
  doc.text(text, docWidth - docMarginRight - textRightOffset, y)
  y += doc.getLineHeight(text) / scaleFactor

  doc.setFont(pdfFont, pdfFontBold)
  doc.setFontSize(12)
  textItems = ['МЕДИЦИНСКАЯ КАРТА ПАЦИЕНТА', 'ДЛЯ АМБУЛАТОРНО-ПОЛИКЛИНИЧЕСКИХ ОРГАНИЗАЦИЙ']
  textItems.forEach(item => {
    doc.text(item, (docWidth / 2) + ((docMarginLeft - docMarginRight) / 2), y, 'center')
    itemHeight = doc.getLineHeight(item) / scaleFactor
    y += itemHeight
  })

  doc.setFont(pdfFont, pdfFontNormal)
  const centerX = (docWidth / 2) + ((docMarginLeft - docMarginRight) / 2)
  const cardNumberWidth = doc.getTextWidth(cardNumber)
  text = '№'
  doc.text(text, centerX, y)
  const cardNumberX = centerX + doc.getTextWidth(text) + 1 + ((25 - cardNumberWidth) / 2)
  doc.text(cardNumber, cardNumberX, y)
  doc.line(
    centerX + doc.getTextWidth(text) + 1, y + 1, centerX + doc.getTextWidth(text) + 25, y + 1
  )
  y += (doc.getLineHeight(text) / scaleFactor) * 2

  let prevText = 'Фамилия, собственное имя, отчество (если таковое имеется)'
  doc.text(prevText, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(prevText) + 1
  const fullNameWidth = doc.getTextWidth(fullName)
  if (fullNameWidth > docWidth - docMarginRight - textWidth) {
    const splitText = doc.splitTextToSize(fullName, docWidth - docMarginRight - textWidth)
    doc.text(splitText[0], textWidth + 1, y)
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
    y += doc.getLineHeight(text) / scaleFactor
    doc.text(splitText.slice(1), docMarginLeft + 1, y)
    doc.line(docMarginLeft, y + 1, docWidth - docMarginRight, y + 1)
  } else {
    doc.text(
      fullName, textWidth + ((docWidth - docMarginRight - textWidth - doc.getTextWidth(fullName)) / 2), y
    )
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  }
  y += doc.getLineHeight(text) / scaleFactor

  const birthdayParts = birthday ? moment(birthday).format('LL').split(' ') : []
  text = 'Число, месяц, год рождения'
  doc.text(text, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(text) + 1
  text = birthdayParts.length ? birthdayParts[0] : ''
  doc.text(text, textWidth + ((7 - doc.getTextWidth(text)) / 2), y)
  doc.line(textWidth, y + 1, textWidth + 7, y + 1)
  textWidth = textWidth + 8
  text = birthdayParts.length ? birthdayParts[1] : ''
  doc.text(text, textWidth + ((30 - doc.getTextWidth(text)) / 2), y)
  doc.line(textWidth, y + 1, textWidth + 30, y + 1)
  textWidth = textWidth + 31
  text = birthdayParts.length ? birthdayParts[2] : ''
  doc.text(text, textWidth + ((9 - doc.getTextWidth(text)) / 2), y)
  doc.line(textWidth, y + 1, textWidth + 9, y + 1)
  textWidth = textWidth + 10
  text = 'г.'
  doc.text(text, textWidth, y)
  y += doc.getLineHeight(text) / scaleFactor

  text = 'Идентификационный номер пациента:'
  doc.text(text, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(text) + 1
  documentIdentifier = documentIdentifier ? documentIdentifier : ''
  doc.text(
    documentIdentifier,
    textWidth + ((docWidth - docMarginRight - textWidth - doc.getTextWidth(documentIdentifier)) / 2),
    y
  )
  doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getLineHeight(text) / scaleFactor

  text = 'Пол: мужской, женский (нужное подчеркнуть).'
  if ([0, 1].includes(gender)) {
    if (gender === 0) {
      doc.line(docMarginLeft + 9.5, y + 1, docMarginLeft + 25.8, y + 1)
    } else {
      doc.line(docMarginLeft + 27.3, y + 1, docMarginLeft + 43.2, y + 1)
    }
  }
  doc.text(text, docMarginLeft, y)
  y += doc.getLineHeight(text) / scaleFactor

  if (generalMedicalCardHideResidencePlaceDetailedFields()) {
    text = 'Место жительства (место пребывания):'
    doc.text(text, docMarginLeft, y)
    textWidth = docMarginLeft + doc.getTextWidth(text) + 1
    residencePlace = residencePlace ? residencePlace : ''
    doc.text(
      residencePlace, textWidth + ((docWidth - docMarginRight - textWidth - doc.getTextWidth(residencePlace)) / 2), y
    )
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  } else {
    text = 'Место жительства (место пребывания): область'
    doc.text(text, docMarginLeft, y)
    textWidth = docMarginLeft + doc.getTextWidth(text) + 1
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
    y += doc.getLineHeight(text) / scaleFactor
    
    text = 'район'
    doc.text(text, docMarginLeft, y)
    textWidth = docMarginLeft + doc.getTextWidth(text) + 1
    doc.line(textWidth, y + 1, textWidth + 49, y + 1)
    textWidth = textWidth + 50
    text = 'населенный пункт'
    doc.text(text, textWidth, y)
    textWidth = textWidth + doc.getTextWidth(text) + 1
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
    y += doc.getLineHeight(text) / scaleFactor
    
    text = 'улица/проспект/переулок/проезд'
    doc.text(text, docMarginLeft, y)
    textWidth = docMarginLeft + doc.getTextWidth(text) + 1
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
    y += doc.getLineHeight(text) / scaleFactor
    
    text = 'дом'
    doc.text(text, docMarginLeft, y)
    textWidth = docMarginLeft + doc.getTextWidth(text) + 1
    doc.line(textWidth, y + 1, textWidth + 31, y + 1)
    textWidth = textWidth + 31
    text = ', корпус'
    doc.text(text, textWidth, y)
    textWidth = textWidth + doc.getTextWidth(text) + 1
    doc.line(textWidth, y + 1, textWidth + 31, y + 1)
    textWidth = textWidth + 31
    text = ', квартира'
    doc.text(text, textWidth, y)
    textWidth = textWidth + doc.getTextWidth(text) + 1
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  }
  y += doc.getLineHeight(text) / scaleFactor

  text = 'Адрес фактического проживания:'
  doc.text(text, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(text) + 1
  address = address ? address : ''
  doc.text(address, textWidth + ((docWidth - docMarginRight - textWidth - doc.getTextWidth(address)) / 2), y)
  doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getLineHeight(text) / scaleFactor

  text = 'Место работы (учебы), должность служащего'
  doc.text(text, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(text) + 1
  workPlace = workPlace ? workPlace : ''
  doc.text(workPlace, textWidth + ((docWidth - docMarginRight - textWidth - doc.getTextWidth(workPlace)) / 2), y)
  doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getLineHeight(text) / scaleFactor

  text = 'Телефон: домашний'
  doc.text(text, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(text) + 1
  doc.line(textWidth, y + 1, textWidth + 31, y + 1)
  textWidth = textWidth + 31
  text = ', мобильный'
  doc.text(text, textWidth, y)
  textWidth = textWidth + doc.getTextWidth(text) + 1
  phone = phone ? phone : ''
  doc.text(phone, textWidth + ((docWidth - docMarginRight - textWidth - doc.getTextWidth(phone)) / 2), y)
  doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getLineHeight(text) / scaleFactor

  text = 'Категория граждан, имеющих льготы на медицинское обслуживание (указать льготу)'
  doc.text(text, docMarginLeft, y)
  y += doc.getLineHeight(text) / scaleFactor
  doc.line(docMarginLeft, y + 1, docWidth - docMarginRight, y + 1)
  y += (doc.getLineHeight(text) / scaleFactor) * 1.5

  const currentDateParts = moment().format('LL').split(' ')
  text = currentDateParts.length ? currentDateParts[0] : ''
  doc.text(text, docMarginLeft + ((8 - doc.getTextWidth(currentDateParts[0])) / 2), y)
  doc.line(docMarginLeft, y + 1, docMarginLeft + 8, y + 1)
  textWidth = docMarginLeft + 9
  text = currentDateParts.length ? currentDateParts[1] : ''
  doc.text(text, textWidth + ((50 - doc.getTextWidth(currentDateParts[1])) / 2), y)
  doc.line(textWidth, y + 1, textWidth + 50, y + 1)
  textWidth = textWidth + 51
  text = currentDateParts.length ? currentDateParts[2] : ''
  doc.text(
    text.replace(text.substring(0, 2), `${text.substring(0, 2)} `),
    textWidth + 1 + ((6 - doc.getTextWidth(currentDateParts[2])) / 2),
    y
  )
  textWidth += doc.getTextWidth(text.substring(0, 2))
  doc.line(textWidth, y + 1, textWidth + 6, y + 1)
  textWidth = textWidth + 7
  text = 'г.'
  doc.text(text, textWidth, y)

  text = '(дата оформления медицинской карты пациента)'
  doc.setFontSize(10)
  y += doc.getLineHeight(text) / scaleFactor
  doc.text(text, docMarginLeft, y)

  // page 2
  doc.addPage()
  y = 10
  doc.setFontSize(12)
  doc.setFont(pdfFont, pdfFontBold)
  text = 'Медицинский осмотр врача-специалиста'
  doc.text(text, (docWidth / 2) + ((docMarginLeft - docMarginRight) / 2), y, 'center')
  y += doc.getLineHeight(text) / scaleFactor
  const medicalExaminationDate = medicalExamination && medicalExamination.date ? medicalExamination.date : null
  const complaints = medicalExamination && medicalExamination.complaints
    ? medicalExamination.complaints : ''
  const anamnesis = medicalExamination && medicalExamination.anamnesis ? medicalExamination.anamnesis : ''
  const objectiveExaminationData = medicalExamination && medicalExamination.objective_examination_data
    ? medicalExamination.objective_examination_data : ''
  const diagnoses = medicalExamination && medicalExamination.diagnoses ? medicalExamination.diagnoses : ''
  const conclusion = medicalExamination && medicalExamination.conclusion ? medicalExamination.conclusion : ''
  const recommendations = medicalExamination && medicalExamination.recommendations
    ? medicalExamination.recommendations : ''

  doc.setFont(pdfFont, pdfFontNormal)
  text = 'первичный, повторный, активный (нужное подчеркнуть)'
  doc.text(text, (docWidth / 2) + ((docMarginLeft - docMarginRight) / 2), y, 'center')
  y += (doc.getLineHeight(text) / scaleFactor) * 1.5

  const paragraphOffset = 10
  text = '1. Место проведения медицинского осмотра:'
  integratorAddress = integratorAddress ? integratorAddress.replaceAll('\n', '') : ''
  doc.text(text, docMarginLeft + paragraphOffset, y)
  textWidth = docMarginLeft + paragraphOffset + doc.getTextWidth(text) + 1
  const integratorAddressWidth = doc.getTextWidth(integratorAddress)
  if (integratorAddressWidth > docWidth - docMarginRight - textWidth) {
    const splitText = doc.splitTextToSize(integratorAddress, docWidth - docMarginRight - textWidth)
    doc.text(splitText[0], textWidth + 1, y)
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
    y += doc.getLineHeight(text) / scaleFactor
    doc.text(splitText.slice(1), docMarginLeft + 1, y)
    doc.line(docMarginLeft, y + 1, docWidth - docMarginRight, y + 1)
  } else {
    doc.text(
      integratorAddress,
      textWidth + ((docWidth - docMarginRight - textWidth - doc.getTextWidth(integratorAddress)) / 2),
      y
    )
    doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  }

  scaleFactor = scaleFactor - 2
  y += doc.getTextDimensions(text).h + scaleFactor

  text = '2. Дата и время проведения медицинского осмотра:'
  let day = null
  let month = null
  let year = null
  if (medicalExaminationDate) {
    const splitT = medicalExaminationDate.format('LL').split(' ')
    day = splitT[0]
    month = splitT[1]
    year = splitT[2].slice(-2)
  }

  doc.text(text, docMarginLeft + paragraphOffset, y)
  textWidth = docMarginLeft + paragraphOffset + doc.getTextWidth(text) + 1
  if (day) {
    doc.text(day, textWidth + 2, y, 'center')
  }
  doc.line(textWidth, y + 1, textWidth + 4, y + 1)
  textWidth += 5
  if (month) {
    doc.text(month, textWidth + 8, y, 'center')
  }
  doc.line(textWidth, y + 1, textWidth + 16, y + 1)
  textWidth += 17
  text = '20'
  doc.text(text, textWidth, y)
  textWidth += doc.getTextWidth(text)
  if (year) {
    doc.text(year, textWidth + 2, y, 'center')
  }
  doc.line(textWidth, y + 1, textWidth + 4, y + 1)
  textWidth += 5
  text = 'г.,'
  doc.text(text, textWidth, y)
  textWidth += doc.getTextWidth(text) + 1
  let hours = null
  let minutes = null
  if (medicalExaminationDate) {
    const splitT = medicalExaminationDate.format('HH:mm').split(':')
    hours = splitT[0]
    minutes = splitT[1]
  }
  if (hours) {
    doc.text(hours, textWidth + 2, y, 'center')
  }
  doc.line(textWidth, y + 1, textWidth + 4, y + 1)
  textWidth += 5
  text = 'час'
  doc.text(text, textWidth, y)
  textWidth += doc.getTextWidth(text) + 1
  if (minutes) {
    doc.text(minutes, textWidth + 2, y, 'center')
  }
  doc.line(textWidth, y + 1, textWidth + 4, y + 1)
  textWidth += 5
  text = 'мин.'
  doc.text(text, textWidth, y)
  y += doc.getTextDimensions(text).h + scaleFactor

  text = '3. Жалобы пациента:'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  y = textToLines(
    complaints, text, doc, docMarginLeft, y, pdfFont, false, scaleFactor, true, docMarginRight,
    paragraphOffset
  )

  text = '4. Анамнез заболевания:'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  y = textToLines(
    anamnesis, text, doc, docMarginLeft, y, pdfFont, false, scaleFactor, true, docMarginRight,
    paragraphOffset
  )

  text = '5. Данные результатов медицинского осмотра:'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  y = textToLines(
    objectiveExaminationData, text, doc, docMarginLeft, y, pdfFont, false, scaleFactor, true,
    docMarginRight, paragraphOffset, 1
  )

  text = '6. Диагноз: предварительный, заключительный клинико-функциональный (нужное'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  y += doc.getTextDimensions(text).h + scaleFactor
  text = 'подчеркнуть)'
  doc.text(text, docMarginLeft, y)
  y = textToLines(
    diagnoses, text, doc, docMarginLeft, y, pdfFont, false, scaleFactor, true,
    docMarginRight, null, 1
  )

  text = '7. Лечение:'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  y = textToLines(
    conclusion, text, doc, docMarginLeft, y, pdfFont, false, scaleFactor, true,
    docMarginRight, paragraphOffset, 1
  )
  
  text = '8. Рекомендации:'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  y = textToLines(
    recommendations, text, doc, docMarginLeft, y, pdfFont, false, scaleFactor, true,
    docMarginRight, paragraphOffset, 1
  )

  text = '9. Экспертиза временной нетрудоспособности:'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  textWidth = docMarginLeft + paragraphOffset + doc.getTextWidth(text) + 1
  doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getTextDimensions(text).h + scaleFactor
  doc.line(docMarginLeft, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getTextDimensions(text).h + scaleFactor

  text = '10. Листок нетрудоспособности (справка о временной нетрудоспособности)'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  y += doc.getTextDimensions(text).h + scaleFactor

  text = '№'
  doc.text(text, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(text) + 1
  doc.line(textWidth, y + 1, textWidth + 17, y + 1)
  textWidth += 18
  text = 'с'
  doc.text(text, textWidth, y)
  textWidth += doc.getTextWidth(text) + 1
  doc.line(textWidth, y + 1, textWidth + 17, y + 1)
  textWidth += 18
  text = 'по'
  doc.text(text, textWidth, y)
  textWidth += doc.getTextWidth(text) + 1
  doc.line(textWidth, y + 1, textWidth + 17, y + 1)
  textWidth += 18
  text = 'режим'
  doc.text(text, textWidth, y)
  textWidth += doc.getTextWidth(text) + 1
  doc.line(textWidth, y + 1, textWidth + 17, y + 1)
  y += doc.getTextDimensions(text).h + scaleFactor

  text = '11. Дата повторного медицинского осмотра:'
  doc.text(text, docMarginLeft + paragraphOffset, y)
  textWidth = docMarginLeft + paragraphOffset + doc.getTextWidth(text) + 1
  doc.line(textWidth, y + 1, textWidth + 8, y + 1)
  textWidth += 9
  doc.line(textWidth, y + 1, textWidth + 30, y + 1)
  textWidth += 31
  text = '20'
  doc.text(text, textWidth, y)
  textWidth += doc.getTextWidth(text)
  doc.line(textWidth, y + 1, textWidth + 4, y + 1)
  textWidth += 5
  text = 'г.'
  doc.text(text, textWidth, y)
  y += doc.getTextDimensions(text).h + scaleFactor
  
  if (docHeight - 10 < y) {
    doc.addPage()
    y = 10
  }
  const mainText = 'Врач-специалист'
  doc.text(mainText, docMarginLeft, y)
  textWidth = docMarginLeft + doc.getTextWidth(mainText) + 5
  doc.line(textWidth, y + 1, textWidth + 32, y + 1)
  textWidth += 37
  doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getTextDimensions(text).h + scaleFactor
  doc.setFontSize(10)
  text = '(подпись)'
  doc.text(text, 69.5, y)
  text = '(инициалы (инициал собственного имени), фамилия)'
  doc.text(text, 104, y)
  y += (doc.getTextDimensions(text).h + scaleFactor) * 1.5
  doc.setFontSize(12)

  if (docHeight - 10 < y) {
    doc.addPage()
    y = 10
  }
  text = 'Согласие на проведение простых медицинских вмешательств получено.'
  doc.text(text, docMarginLeft, y)
  y += doc.getTextDimensions(text).h + scaleFactor
  textWidth -= 37
  doc.line(textWidth, y + 1, textWidth + 32, y + 1)
  textWidth += 37
  doc.line(textWidth, y + 1, docWidth - docMarginRight, y + 1)
  y += doc.getTextDimensions(text).h + scaleFactor
  doc.setFontSize(10)
  text = '(подпись)'
  doc.text(text, 69.5, y)
  text = '(инициалы (инициал собственного имени), фамилия)'
  doc.text(text, 104, y)

  doc.save(`Общая медицинская карта.pdf`)
}

export const createDentistInsertForm1M = (fullName, form1M, form1MResults) => {
  let doc = new jsPDF({
    orientation: 'landscape', unit: 'mm', format: appConstants.setting.printFormat.APP_A5, putOnlyUsedFonts: true
  })
  const pdfFontSize = 11
  const smallFontSize = pdfFontSize - 2
  const pdfFontBold = 'OpenSans-Bold'
  const pdfFontNormal = 'OpenSans-Regular'
  const docWidth = doc.internal.pageSize.width
  const docHeight = doc.internal.pageSize.height
  const scaleFactor = doc.internal.scaleFactor
  const textLineHeightFactor = 1.4
  const tableLineHeightFactor = 1.2
  const pdfMargin = 8
  const tableMargin = 8
  let positionY = 10

  let rows = [
    [
      {
        content: 'Локализация элементов поражения и количество пораженных участков', colSpan: 5,
        styles: { halign: 'center', valign: 'middle' }
      }
    ],
    [
      { content: 'Губа', styles: {halign: 'center', valign: 'middle' } },
      { content: 'Язык (боковая, нижняя поверхности, передние 2/3)', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Десна', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Ретромолярная область', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Примечания', styles: { halign: 'center', valign: 'middle' }, rowSpan: 3 }
    ]
  ]
  rows.push([
    { content: form1M && form1M.lip ? form1M.lip : '', styles: { halign: 'center', valign: 'middle' } },
    { content: form1M && form1M.tongue ? form1M.tongue : '', styles: { halign: 'center', valign: 'middle' } },
    { content: form1M && form1M.gum ? form1M.gum : '', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.retromolar_area ? form1M.retromolar_area : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    { content: '', styles: { halign: 'center', valign: 'middle' } }
  ])
  rows.push([
    { content: 'Небо', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Дно полости рта', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Щека', styles: { halign: 'center', valign: 'middle'} },
    { content: 'Другие отделы рта', styles: { halign: 'center', valign: 'middle' } }
  ])
  rows.push([
    { content: form1M && form1M.palate ? form1M.palate : '', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.oral_cavity_floor ? form1M.oral_cavity_floor : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    { content: form1M && form1M.cheek ? form1M.cheek : '', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.other_parts_of_mouth ? form1M.other_parts_of_mouth : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    { content: form1M && form1M.note ? form1M.note : '', styles: { halign: 'center', valign: 'middle' } }
  ])
  rows.push([{
    content: 'Характеристика элементов поражения', colSpan: 5, styles: { halign: 'center', valign: 'middle' }
  }])
  rows.push([
    { content: 'Размер', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Форма', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Цвет', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Возвышение над СОР', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Кровоточивость', styles: { halign: 'center', valign: 'middle' } }
  ])
  rows.push([
    { content: form1M && form1M.size ? form1M.size : '', styles: { halign: 'center', valign: 'middle' } },
    { content: form1M && form1M.form ? form1M.form : '', styles: { halign: 'center', valign: 'middle' } },
    { content: form1M && form1M.color ? form1M.color : '', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.elevation_above_sor ? form1M.elevation_above_sor : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    { content: form1M && form1M.bleeding ? form1M.bleeding : '', styles: { halign: 'center', valign: 'middle' } }
  ])
  rows.push([
    { content: 'Налет', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Изъязвление', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Признаки озлокачествления', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Пузырьки', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Другие особенности', styles: { halign: 'center', valign: 'middle'} }
  ])
  rows.push([
    { content: form1M && form1M.plaque ? form1M.plaque : '', styles: { halign: 'center', valign: 'middle' } },
    { content: form1M && form1M.ulceration ? form1M.ulceration : '', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.signs_of_malignancy ? form1M.signs_of_malignancy : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    { content: form1M && form1M.bubbles ? form1M.bubbles : '', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.other_features ? form1M.other_features : '',
      styles: { halign: 'center', valign: 'middle' }
    }
  ])

  doc.setFont(pdfFontBold)
  doc.setFontSize(pdfFontSize)
  doc.setLineHeightFactor(textLineHeightFactor)
  let text = 'Листок-вкладыш врача-стоматолога локализация и характеристика'
  doc.text(text, docWidth / 2, positionY, 'center')
  positionY += doc.getLineHeight(text) / scaleFactor
  doc.text(
    'элементов поражения предопухолевых заболеваний слизистой оболочки рта и губ', docWidth / 2, positionY,
    'center'
  )
  positionY += doc.getLineHeight(text) / scaleFactor

  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.setFont(pdfFontNormal)
  doc.autoTable({
    theme: 'grid',
    startY: positionY,
    body: rows,
    margin: tableMargin,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: smallFontSize,
    },
    didDrawPage: hook => positionY = hook.cursor.y + (doc.getLineHeight(text) / scaleFactor) * 1.5,
  })
  doc.setLineHeightFactor(textLineHeightFactor)
  doc.setFontSize(pdfFontSize)

  text = 'Примечание. Корень языка относится к ротоглотке.'
  doc.text(text, pdfMargin, positionY)

  positionY += (doc.getLineHeight(text) / scaleFactor) * 1.5
  if (docHeight - ((doc.getLineHeight(text) / scaleFactor) * 1.5) < positionY) {
    doc.addPage()
    positionY = 10
  }
  doc.text(
    'Патологические реакции на стоматологические материалы:  ДА / НЕТ.', docWidth / 2, positionY,
    'center'
  )

  if (form1M && form1M.dental_material_pathological_reaction === 'Нет') {
    doc.line(154.5, positionY - 1, 160.5, positionY - 1)
  } else {
    doc.line(163, positionY - 1.2, 171, positionY - 1.2)
  }

  rows = [
    [
      { content: 'Анамнез', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Жалобы', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Симптомы (объективно)', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Результаты диагностики', colSpan: 2, styles: { halign: 'center', valign: 'middle' } }
    ]
  ]
  rows.push([
    {
      content: form1M && form1M.anamnesis ? form1M.anamnesis : '',
      styles: { halign: 'center', valign: 'middle' }, rowSpan: 2,
    },
    {
      content: form1M && form1M.complaints ? form1M.complaints : '',
      styles: { halign: 'center', valign: 'middle' }, rowSpan: 2,
    },
    {
      content: form1M && form1M.symptoms ? form1M.symptoms : '',
      styles: { halign: 'center', valign: 'middle' }, rowSpan: 2,
    },
    { content: 'Гальваноз', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Аллергия', styles: { halign: 'center', valign: 'middle' } }
  ])
  rows.push([
    { content: form1M && form1M.galvanosis ? form1M.galvanosis : '', styles: { halign: 'center', valign: 'middle' } },
    { content: form1M && form1M.allergy ? form1M.allergy : '', styles: { halign: 'center', valign: 'middle' } },
  ])
  rows.push([
    { content: 'Лимфоузлы', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Подчелюстные', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Шейные', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Надключичные', styles: { halign: 'center', valign: 'middle' } },
    { content: 'Подмышечные', styles: { halign: 'center', valign: 'middle' } }
  ])
  rows.push([
    { content: 'Размер', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.submandibular_lymph_nodes_size ? form1M.submandibular_lymph_nodes_size : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.cervical_lymph_nodes_size ? form1M.cervical_lymph_nodes_size : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.supraclavicular_lymph_nodes_size ? form1M.supraclavicular_lymph_nodes_size : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.axillary_lymph_nodes_size ? form1M.axillary_lymph_nodes_size : '',
      styles: { halign: 'center', valign: 'middle' }
    },
  ])
  rows.push([
    { content: 'Подвижность', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.submandibular_lymph_nodes_mobility ? form1M.submandibular_lymph_nodes_mobility : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.cervical_lymph_nodes_mobility ? form1M.cervical_lymph_nodes_mobility : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.supraclavicular_lymph_nodes_mobility ? form1M.supraclavicular_lymph_nodes_mobility : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.axillary_lymph_nodes_mobility ? form1M.axillary_lymph_nodes_mobility : '',
      styles: { halign: 'center', valign: 'middle' }
    },
  ])
  rows.push([
    { content: 'Консистенция', styles: { halign: 'center', valign: 'middle' } },
    {
      content: form1M && form1M.submandibular_lymph_nodes_consistency
        ? form1M.submandibular_lymph_nodes_consistency : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.cervical_lymph_nodes_consistency ? form1M.cervical_lymph_nodes_consistency : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.supraclavicular_lymph_nodes_consistency
        ? form1M.supraclavicular_lymph_nodes_consistency : '',
      styles: { halign: 'center', valign: 'middle' }
    },
    {
      content: form1M && form1M.axillary_lymph_nodes_consistency ? form1M.axillary_lymph_nodes_consistency : '',
      styles: { halign: 'center', valign: 'middle' }
    },
  ])
  positionY += doc.getLineHeight(text) / scaleFactor / 2
  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: positionY,
    body: rows,
    margin: tableMargin,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: smallFontSize,
    },
    didDrawPage: hook => positionY = hook.cursor.y + (doc.getLineHeight(text) / scaleFactor) * 1.5,
  })
  doc.setLineHeightFactor(textLineHeightFactor)
  doc.setFontSize(pdfFontSize)

  text = 'Результаты цитологического и гистологического исследования (подчеркнуть вид исследования)'
  doc.text(text, docWidth / 2, positionY, 'center')
  positionY += doc.getLineHeight(text) / scaleFactor / 2

  rows = [
    [
      { content: 'Дата осмотра (ДД.ММ.ГГГГ)', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Нет признаков атипии', styles: { halign: 'center', valign: 'middle' } },
      {
        content: 'Атипические плоские клетки (неопределенной значимости)',
        styles: { halign: 'center', valign: 'middle' }
      },
      { content: 'Дисплазия с указанием степени', styles: { cellWidth: 20, halign: 'center', valign: 'middle' } },
      { content: 'Рак', styles: { halign: 'center', valign: 'middle' } }
    ]
  ]

  if (form1MResults && form1MResults.length) {
    form1MResults.map(i => {
      rows.push([
        { content: mixins.methods.formatAsDate(i.inspection_date), styles: { halign: 'center', valign: 'middle' } },
        { content: i.atypia_sign ? i.atypia_sign : '', styles: { halign: 'center', valign: 'middle' } },
        { content: i.atypical_flat_cells ? i.atypical_flat_cells : '', styles: { halign: 'center', valign: 'middle' } },
        { content: i.dysplasia ? i.dysplasia : '', styles: { halign: 'center', valign: 'middle' } },
        { content: i.cancer ? i.cancer : '', styles: { halign: 'center', valign: 'middle' } },
      ])
    })
  } else {
    rows.push([{ content: '' }, { content: '' }, { content: '' }, { content: '' }])
  }

  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: positionY,
    body: rows,
    margin: tableMargin,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: smallFontSize,
    },
    didDrawPage: hook => positionY = hook.cursor.y + (doc.getLineHeight(text) / scaleFactor) * 1.5,
  })
  doc.setLineHeightFactor(textLineHeightFactor)
  doc.setFontSize(pdfFontSize)

  text = 'Окончательный диагноз'
  doc.text(text, pdfMargin, positionY)
  positionY += (doc.getLineHeight(text) / scaleFactor) * 1.5

  const diagnosis = form1M && form1M.final_diagnosis
    ? mixins.methods.capitalizeFirstLetter(form1M.final_diagnosis) : ''
  if (doc.getTextWidth(diagnosis) > docWidth - pdfMargin * 2) {
    const splitText = doc.splitTextToSize(diagnosis, docWidth - pdfMargin * 2)
    doc.text(splitText[0], pdfMargin, positionY - 1)
    doc.line(
      pdfMargin,
      positionY,
      docWidth - pdfMargin,
      positionY
    )
    positionY += (doc.getLineHeight(text) / scaleFactor) * 1.5
    doc.text(splitText.slice(1), pdfMargin, positionY - 1)
    doc.line(pdfMargin, positionY, docWidth - pdfMargin, positionY)
  } else {
    doc.text(diagnosis, pdfMargin, positionY - 1)
    doc.line(pdfMargin, positionY, docWidth - pdfMargin, positionY)
  }

  rows = [
    [
      { content: 'Направлен к врачу-онкологу', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Итог (нужное подчеркнуть)', styles: { halign: 'center', valign: 'middle' } }
    ],
    [
      {
        content: form1M && form1M.referral_to_oncologist ? form1M.referral_to_oncologist : '',
        styles: { halign: 'center', valign: 'middle' }
      },
      { content: form1M && form1M.summary ? form1M.summary : '', styles: { halign: 'center', valign: 'middle' } }
    ]
  ]

  positionY += (doc.getLineHeight(text) / scaleFactor) * 1.5
  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: positionY,
    body: rows,
    margin: tableMargin,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: smallFontSize,
    },
  })

  doc.save(`Листок-вкладыш (Форма 1-м), пациент ${fullName}.pdf`)
}

export const createQuestionnaireForm1K = (fullName, form1K) => {
  let doc = new jsPDF({
    orientation: 'landscape', unit: 'mm', format: appConstants.setting.printFormat.APP_A5, putOnlyUsedFonts: true
  })

  let positionY = 10
  const pdfFontSize = 11
  const smallFontSize = pdfFontSize - 2
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const tableLineHeightFactor = 1.2
  const docWidth = doc.internal.pageSize.width
  const scaleFactor = doc.internal.scaleFactor
  const tableMargin = 8

  let rows = [
    [
      { content: 'Жалобы', styles: { halign: 'center', valign: 'middle' } },
      { content: 'Да', styles: { cellWidth: 20, halign: 'center', valign: 'middle' } },
      { content: 'Нет', styles: { cellWidth: 20, halign: 'center', valign: 'middle' } }
    ]
  ]
  const fields = [
    {
      key: 'cough_more_than_three_weeks',
      label: 'Кашель более 3 недель, не связанный с воспалительными заболеваниями верхних дыхательных путей',
    },
    { key: 'voice_changes', label: 'Изменения голоса (охриплость, осиплость, гнусавость)' },
    { key: 'presence_of_mouth_ulcers', label: 'Наличие язв слизистых оболочек ротовой полости' },
    {
      key: 'presence_of_sharp_tooth_edges',
      label: 'Наличие острых краев зубов, травмирующих язык и слизистую оболочку полости рта',
    },
    { key: 'difficulty_swallowing', label: 'Затруднение при глотании или ощущение застрявшей пищи в горле' },
    { key: 'hemoptysis', label: 'Кровохарканье' },
    { key: 'knots_in_neck_area', label: 'Уплотнения, узлы в области шеи' },
    {
      key: 'nasal_breathing_problems',
      label: 'Одностороннее или двустороннее затруднение носового дыхания длительное время',
    },
    { key: 'frequent_nasal_bleeding', label: 'Частые кровянистые выделения из носа' },
    { key: 'weight_loss', label: 'Снижение веса более 10 кг за последние 6 месяцев' },
    { key: 'sensory_disturbances', label: 'Расстройства зрения, слуха, обоняния, нарушения кожной чувствительности' }
  ]

  fields.map(f => {
    const value = form1K && form1K[f.key] ? form1K[f.key] : ''
    const valueYes = value === 'Да' ? value : ''
    const valueNo = value === 'Нет' ? value : ''
    rows.push([
      { content: f.label },
      { content: valueYes, styles: { halign: 'center', valign: 'middle' } },
      { content: valueNo, styles: { halign: 'center', valign: 'middle' } }
    ])
  })

  doc.setFont(pdfFontBold)
  doc.setFontSize(pdfFontSize)
  let text = 'Анкета-опросник по раннему выявлению злокачественных новообразований головы и шеи'
  doc.text(text, docWidth / 2, positionY, 'center')

  positionY += (doc.getLineHeight(text) / scaleFactor) * 1.5
  doc.setFont(pdfFontNormal)
  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: positionY,
    body: rows,
    margin: tableMargin,
    bodyStyles: {
      cellPadding: 1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    styles: {
      font: pdfFontNormal,
      fontSize: smallFontSize,
    },
    didParseCell: hook => {
      if (hook.row.index === 0) {
        hook.cell.styles.font = pdfFontBold
      }
    },
  })
  doc.save(`Анкета-опросник (Форма 1-к), пациент ${fullName}.pdf`)
}

export const createMedicalInterventionVoluntaryConsent = (fullName, lastName, treatmentDiaries) => {
  let doc = new jsPDF({
    orientation: 'landscape', unit: 'mm', format: appConstants.setting.printFormat.APP_A5, putOnlyUsedFonts: true
  })

  let positionY = 10
  const pdfFontSize = 11
  const smallFontSize = pdfFontSize - 2
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const tableLineHeightFactor = 1.2
  const docWidth = doc.internal.pageSize.width
  const scaleFactor = doc.internal.scaleFactor
  const tableMargin = 8

  doc.setFontSize(pdfFontSize)
  doc.setFont(pdfFontBold)
  let text = 'Добровольное согласие на медицинское вмешательство'
  doc.text(
    text, docWidth / 2, positionY, 'center'
  )
  doc.setFont(pdfFontNormal)

  let rows = [
    [
      { content: 'Дата', rowSpan: 2, styles: { halign: 'center', minCellWidth: 18, valign: 'middle' } },
      { content: 'Диагноз', rowSpan: 2, styles: { minCellWidth: 50, halign: 'center', valign: 'middle' } },
      { content: 'Предполагаемое лечение', colSpan: 3, styles: { halign: 'center', valign: 'middle' } },
      { content: 'Доктор', rowSpan: 2, styles: { minCellWidth: 20, halign: 'center', valign: 'middle' } },
      { content: 'Согласие пациента (да/нет)', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
      {
        content: 'Фамилия и подпись пациента',
        rowSpan: 2,
        styles: { minCellWidth: 20, halign: 'center', valign: 'middle' }
      }
    ],
    [
      { content: 'терапия', styles: { halign: 'center', minCellWidth: 18, valign: 'middle' } },
      { content: 'хирургия', styles: { halign: 'center', minCellWidth: 18, valign: 'middle' } },
      { content: 'ортопедия', styles: { halign: 'center', minCellWidth: 18, valign: 'middle' } }
    ]
  ]

  treatmentDiaries.forEach(diary => {
    const row = []
    const teethNumbers = []
    row.push({
      content: moment(parseInt(diary.date)).format('DD.MM.YYYY'),
      styles: { halign: 'center', valign: 'middle' }
    })
    diary.status_teeth.map(t => t.teeth ? teethNumbers.push(t.teeth.number) : null)
    const customName = diary.diagnosis_custom_name
      ? diary.diagnosis_custom_name
      : diary.diagnoses.map(d => d.mkb ? `${d.full_name} (${d.mkb})` : d.full_name).join(', ')
    let content = ''
    if (teethNumbers.length && customName) {
      content = teethNumbers.join(', ') + ` - ${customName}`
    } else if (teethNumbers.length) {
      content = teethNumbers.join(', ')
    } else if (customName) {
      content = customName
    }
    row.push({ content: content, styles: { valign: 'middle' } })
    row.push({ content: '' })
    row.push({ content: '' })
    row.push({ content: '' })
    row.push({ content: diary.doctor ? diary.doctor.full_name : '', styles: { valign: 'middle' }})
    row.push({ content: 'Да', styles: { halign: 'center', valign: 'middle' }})
    row.push({ content: lastName })
    rows.push(row)
  })

  rows = rows.concat(
    Array.from(
      { length: 15 - rows.length },
      () => [
        { content: '' },
        { content: '' },
        { content: '' },
        { content: '' },
        { content: '' },
        { content: '', styles: { halign: 'center', valign: 'middle' } },
        { content: '' }
      ]
    )
  )

  positionY += doc.getLineHeight(text) / scaleFactor
  doc.setFontSize(smallFontSize)
  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: positionY,
    body: rows,
    margin: tableMargin,
    bodyStyles: { cellPadding: 1, lineColor: [0, 0, 0], textColor: [0, 0, 0] },
    styles: { font: pdfFontNormal, fontSize: smallFontSize },
    didDrawPage: hook => positionY = hook.cursor.y + (doc.getLineHeight(text) / scaleFactor) * 1.5,
  })

  doc.save(`Добровольное согласие на медицинское вмешательство, пациент ${fullName}.pdf`)
}

export const createMedicalInterventionPreliminaryConsent = (fullName, dentalCardDate) => {
  let doc = new jsPDF({
    orientation: 'landscape', unit: 'mm', format: appConstants.setting.printFormat.APP_A5, putOnlyUsedFonts: true
  })

  let positionY = 10
  const pdfFontSize = 11
  const smallFontSize = pdfFontSize - 2
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const textLineHeightFactor = 1.4
  const docWidth = doc.internal.pageSize.width
  const scaleFactor = doc.internal.scaleFactor
  const pdfMargin = 8

  doc.setFontSize(smallFontSize)
  doc.setLineHeightFactor(textLineHeightFactor)
  doc.setFont(pdfFontBold)
  let text = 'Предварительное согласие на медицинское вмешательство'
  doc.text(text, docWidth / 2, positionY, 'center')
  doc.setFont(pdfFontNormal)

  positionY += (doc.getLineHeight(text) / scaleFactor) * 1.5

  text = '      '
  doc.text(
    text + 'В соответствии со статьями 44, 45 Закона Республики Беларусь от 20 июня 2008 года "О здравоохранении", мне,',
    pdfMargin,
    positionY
  )

  positionY += doc.getLineHeight(text) / scaleFactor
  positionY = textToRows(
    fullName, '', doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor
  )

  doc.setFontSize(7)
  doc.text(
    '(фамилия, имя, отчество пациента, либо законного представителя, либо супруга (ги), либо близкого родственника)',
    docWidth / 2,
    positionY - 2.5,
    'center'
  )
  doc.setFontSize(smallFontSize)
  positionY += 1
  text = 'в доступной и понятной для меня форме лечащим врачом '
  doc.text(text, pdfMargin, positionY)
  positionY = textToRows(
    '', text, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor
  )

  const doctorUnderlineWidth = (docWidth - (pdfMargin * 2)) - doc.getTextWidth(text) - 1
  doc.setFontSize(7)
  doc.text(
    '(фамилия, имя, отчество врача)', doctorUnderlineWidth + ((docWidth - doctorUnderlineWidth) / 2),
    positionY - 2.5, 'center'
  )
  doc.setFontSize(smallFontSize)
  positionY += 1
  doc.text(
    'предоставлена полная информация о состоянии стоматологического здоровья и цели медицинского вмешательства у',
    pdfMargin, positionY
  )
  positionY += doc.getLineHeight(text) / scaleFactor
  positionY = textToRows(
    fullName, '', doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor
  )

  doc.setFontSize(7)
  doc.text(
    '(меня, моего супруга (ги), моего близкого родственника (фамилия, имя, отчество))',
    docWidth / 2,
    positionY - 2.5,
    'center'
  )

  doc.setFontSize(smallFontSize)
  doc.text('включающая сведения:', pdfMargin, positionY)
  positionY += doc.getLineHeight(text) / scaleFactor
  text = '      '
  positionY = addTextWithMultiRows(
    doc, positionY, 5, pdfMargin, '     ',
    'о результатах стоматологического обследования, о наличии заболевания, диагнозе и прогнозе заболевания,' +
    ' необходимости медицинского стоматологического вмешательства, возможных вариантах стоматологического лечения ' +
    'и риске с ними связанном, о предполагаемом плане стоматологического лечения, о предполагаемых результатах лечения,' +
    ' о возможности осложнений и о необходимости соблюдения рекомендаций лечащего врача относительно правил и условий' +
    ' использования результатов лечения;',
    0,
    false
  )

  doc.text(text + 'о имеющемся у меня праве отказаться от медицинского вмешательства.', pdfMargin, positionY)
  positionY += doc.getLineHeight(text) / scaleFactor
  
  positionY = addTextWithMultiRows(
    doc, positionY, 5, pdfMargin, '     ',
    'Мне в доступной для меня форме даны разъяснения о возможных последствиях в случае отказа  ' +
    'от медицинского вмешательства.',
    0,
    false
  )

  text = 'Даю предварительное согласие на медицинское вмешательство:'
  doc.text(text, pdfMargin, positionY)
  positionY = textToRows(
    '', text, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor
  )
  positionY = textToRows(
    ' ', '', doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor
  )
  positionY = textToRows(
    ' ', '', doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor
  )
  positionY += doc.getLineHeight(text) / scaleFactor / 2

  text = 'Дата  '
  doc.text(text, pdfMargin, positionY)

  doc.text(dentalCardDate, doc.getTextWidth(text) + pdfMargin, positionY)
  positionY += 1
  doc.line(
    doc.getTextWidth(text) + pdfMargin,
    positionY,
    doc.getTextWidth(text) + pdfMargin + doc.getTextWidth(dentalCardDate),
    positionY
  )

  positionY += 7

  let signLineWidth = 40
  doc.line(pdfMargin, positionY, pdfMargin + signLineWidth, positionY)

  doc.setFontSize(7)
  doc.text('(подпись лечащего врача)', pdfMargin + (signLineWidth / 2), positionY + 3, 'center')

  signLineWidth = 20
  doc.line(docWidth - (pdfMargin + (signLineWidth * 3)), positionY, docWidth - pdfMargin, positionY)

  let underlineSignTextLines = doc.splitTextToSize(
    '(подпись пациента, либо законного представителя, либо супруга (ги), либо близкого родственника)',
    signLineWidth * 3
  )

  doc.text(underlineSignTextLines, (docWidth - pdfMargin) - (signLineWidth * 1.5), positionY + 3, 'center')

  doc.save(`Предварительное согласие на медицинское вмешательство, пациент ${fullName}.pdf`)
}

export const createOrthodonticInsert = (fullName, orthodonticForm) => {
  let doc = new jsPDF({
    orientation: 'landscape', unit: 'mm', format: appConstants.setting.printFormat.APP_A5, putOnlyUsedFonts: true
  })

  let positionY = 10
  const pdfFontSize = 11
  const smallFontSize = pdfFontSize - 2
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const textLineHeightFactor = 1.4
  const docWidth = doc.internal.pageSize.width
  const docHeight = doc.internal.pageSize.height
  const scaleFactor = doc.internal.scaleFactor
  const pdfMargin = 8

  doc.setFontSize(pdfFontSize)
  doc.setFont(pdfFontBold)
  let label = 'ОРТОДОНТИЧЕСКИЙ ВКЛАДЫШ'
  doc.text(label, docWidth / 2, positionY, 'center')
  positionY += doc.getLineHeight(label) / scaleFactor
  doc.text('К МЕДИЦИНСКОЙ КАРТЕ СТОМАТОЛОГИЧЕСКОГО ПАЦИЕНТА', docWidth / 2, positionY, 'center')
  doc.setFont(pdfFontNormal)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5

  label = 'Осанка, вредные привычки:'
  doc.text(label, pdfMargin, positionY)
  let text = orthodonticForm && orthodonticForm.posture_bad_habits ? orthodonticForm.posture_bad_habits : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'Состояние функций челюстно-лицевой системы:'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.maxillofacial_system_func_state
    ? orthodonticForm.maxillofacial_system_func_state : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'Внешний осмотр (эстетика лица):'
  doc.text(label, pdfMargin, positionY)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5

  label = 'анфас'
  let rectWidth = 26
  let rectHeight = 6
  doc.text(label, pdfMargin, positionY)
  let items = ['квадратное', 'прямоугольное', 'треугольное', 'ромбовидное', 'узкое', 'широкое']
  let prevPosX = pdfMargin + doc.getTextWidth(label) + 3
  doc.setFontSize(smallFontSize)
  let data = orthodonticForm && orthodonticForm.face_external_examination_data
    ? orthodonticForm.face_external_examination_data : null
  let enabled = false
  items.forEach((i, idx) => {
    if (data) {
      switch (idx) {
        case 0:
          text = data.full_face_square_text ? data.full_face_square_text : ''
          enabled = !!data.full_face_square_enabled
          break
        case 1:
          text = data.full_face_rectangular_text ? data.full_face_rectangular_text : ''
          enabled = !!data.full_face_rectangular_enabled
          break
        case 2:
          text = data.full_face_triangular_text ? data.full_face_triangular_text : ''
          enabled = !!data.full_face_triangular_enabled
          break
        case 3:
          text = data.full_face_diamond_shaped_text ? data.full_face_diamond_shaped_text : ''
          enabled = !!data.full_face_diamond_shaped_enabled
          break
        case 4:
          text = data.full_face_narrow_text ? data.full_face_narrow_text : ''
          enabled = !!data.full_face_narrow_enabled
          break
        case 5:
          text = data.full_face_wide_text ? data.full_face_wide_text : ''
          enabled = !!data.full_face_wide_enabled
          break
      }
      if (text) {
        doc.text(text, prevPosX + rectWidth / 2, positionY, 'center')
      } else {
        doc.text(enabled ? 'Да' : 'Нет', prevPosX + rectWidth / 2, positionY, 'center')
      }
    }
    doc.rect(prevPosX, positionY - 4, rectWidth, rectHeight)
    doc.text(
      i, prevPosX + rectWidth / 2, positionY + doc.getLineHeight(label) / scaleFactor + 0.5,
      'center'
    )
    prevPosX += rectWidth + 4
  })
  doc.setFontSize(pdfFontSize)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 2

  label = 'профиль'
  doc.text(label, pdfMargin, positionY)
  items = ['прямой', 'вогнутый', 'выпуклый']
  prevPosX = pdfMargin + doc.getTextWidth(label) + 3
  doc.setFontSize(smallFontSize)
  items.forEach((i, idx) => {
    if (data) {
      switch (idx) {
        case 0:
          text = data.profile_direct_text ? data.profile_direct_text : ''
          enabled = !!data.profile_direct_enabled
          break
        case 1:
          text = data.profile_concave_text ? data.profile_concave_text : ''
          enabled = !!data.profile_concave_enabled
          break
        case 2:
          text = data.profile_convex_text ? data.profile_convex_text : ''
          enabled = !!data.profile_convex_enabled
          break
      }
      if (text) {
        doc.text(text, prevPosX + rectWidth / 2, positionY, 'center')
      } else {
        doc.text(enabled ? 'Да' : 'Нет', prevPosX + rectWidth / 2, positionY, 'center')
      }
    }
    doc.rect(prevPosX, positionY - 4, rectWidth, rectHeight)
    doc.text(
      i, prevPosX + rectWidth / 2, positionY + doc.getLineHeight(label) / scaleFactor + 0.5,
      'center'
    )
    prevPosX += rectWidth + 4
  })
  doc.setFontSize(pdfFontSize)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 2

  label = 'Состояние преддверия полости рта, уздечек, языка, миндалин, свода неба, подбородочной, носогуб-'
  doc.text(label, pdfMargin, positionY)
  positionY += doc.getLineHeight(label) / scaleFactor
  label = 'ной складок:'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.lower_face_state ? orthodonticForm.lower_face_state : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'Наличие патологии несращения неба, альвеолярного отростка, губы:'
  doc.text(label, pdfMargin, positionY)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  rectWidth = 34
  items = ['отсутствует', 'губа', 'альвеолярный отросток', 'мягкое небо']
  prevPosX = pdfMargin
  doc.setFontSize(smallFontSize)
  data = orthodonticForm && orthodonticForm.pathologies_data ? orthodonticForm.pathologies_data : null
  items.forEach((i, idx) => {
    if (data) {
      switch (idx) {
        case 0:
          text = data.absent_text ? data.absent_text : ''
          enabled = !!data.absent_enabled
          break
        case 1:
          text = data.lip_text ? data.lip_text : ''
          enabled = !!data.lip_enabled
          break
        case 2:
          text = data.alveolar_process_text ? data.alveolar_process_text : ''
          enabled = !!data.alveolar_process_enabled
          break
        case 3:
          text = data.soft_palate_text ? data.soft_palate_text : ''
          enabled = !!data.soft_palate_enabled
          break
      }
      if (text) {
        doc.text(text, prevPosX + rectWidth / 2, positionY, 'center')
      } else {
        doc.text(enabled ? 'Да' : 'Нет', prevPosX + rectWidth / 2, positionY, 'center')
      }
    }
    doc.rect(prevPosX, positionY - 4, rectWidth, rectHeight)
    doc.text(
      i, prevPosX + rectWidth / 2, positionY + doc.getLineHeight(label) / scaleFactor + 0.5,
      'center'
    )
    prevPosX += rectWidth + 10
  })
  positionY += (doc.getLineHeight(label) / scaleFactor) * 2.5
  items = ['твердое небо', 'скрытые', 'односторонние расщелины', 'двусторонние']
  prevPosX = pdfMargin
  items.forEach((i, idx) => {
    if (data) {
      switch (idx) {
        case 4:
          text = data.hard_palate_text ? data.hard_palate_text : ''
          enabled = !!data.hard_palate_enabled
          break
        case 5:
          text = data.hidden_text ? data.hidden_text : ''
          enabled = !!data.hidden_enabled
          break
        case 6:
          text = data.unilateral_clefts_text ? data.unilateral_clefts_text : ''
          enabled = !!data.unilateral_clefts_enabled
          break
        case 7:
          text = data.double_sided_text ? data.double_sided_text : ''
          enabled = !!data.double_sided_enabled
          break
      }
      if (text) {
        doc.text(text, prevPosX + rectWidth / 2, positionY, 'center')
      } else {
        doc.text(enabled ? 'Да' : 'Нет', prevPosX + rectWidth / 2, positionY, 'center')
      }
    }
    doc.rect(prevPosX, positionY - 4, rectWidth, rectHeight)
    doc.text(
      i, prevPosX + rectWidth / 2, positionY + doc.getLineHeight(label) / scaleFactor + 0.5,
      'center'
    )
    prevPosX += rectWidth + 10
  })
  doc.setFontSize(pdfFontSize)
  positionY += doc.getLineHeight(label) / scaleFactor

  if (docHeight < positionY + doc.getLineHeight(label) / scaleFactor) {
    doc.addPage()
    positionY = 10
  } else {
    positionY += doc.getLineHeight(label) / scaleFactor
  }

  label = 'Прикус: сагиттальное соотношение (по Энглю): моляры R L клыки R L'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.bite_sagittal_ratio ? orthodonticForm.bite_sagittal_ratio : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'сагиттальная щель (мм)'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.bite_sagittal_fissure ? orthodonticForm.bite_sagittal_fissure : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  // label = 'вертикальное соотношение'
  // doc.text(label, pdfMargin, positionY)
  // text = orthodonticForm && orthodonticForm.bite_vertical_ratio ? orthodonticForm.bite_vertical_ratio : ''
  // positionY = textToRows(
  //   text, label, doc, pdfMargin, positionY, pdfFontNormal, false,
  //   textLineHeightFactor
  // )

  label = 'вертикальная щель (мм)'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.bite_vertical_fissure ? orthodonticForm.bite_vertical_fissure : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'прямой резцовый контакт'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.bite_direct_incisal_contact
    ? orthodonticForm.bite_direct_incisal_contact : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'резцовое перекрытие'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.bite_incisal_overlap ? orthodonticForm.bite_incisal_overlap : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)
  
  doc.addPage()
  positionY = 10

  label = 'трансверзальное соотношение:'
  doc.text(label, pdfMargin, positionY)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  items = ['не нарушено', 'буккальное', 'лингвальное', 'одностороннее', 'двустороннее', 'со смещением', 'без смещения']
  prevPosX = pdfMargin
  rectWidth = 24.5
  doc.setFontSize(smallFontSize)
  data = orthodonticForm && orthodonticForm.transversal_ratio_data ? orthodonticForm.transversal_ratio_data : null
  items.forEach((i, idx) => {
    if (data) {
      switch (idx) {
        case 0:
          text = data.not_violated_text ? data.not_violated_text : ''
          enabled = !!data.not_violated_enabled
          break
        case 1:
          text = data.buccal_text ? data.buccal_text : ''
          enabled = !!data.buccal_enabled
          break
        case 2:
          text = data.lingual_text ? data.lingual_text : ''
          enabled = !!data.lingual_enabled
          break
        case 3:
          text = data.unilateral_text ? data.unilateral_text : ''
          enabled = !!data.unilateral_enabled
          break
        case 4:
          text = data.bilateral_text ? data.bilateral_text : ''
          enabled = !!data.bilateral_enabled
          break
        case 5:
          text = data.with_offset_text ? data.with_offset_text : ''
          enabled = !!data.with_offset_enabled
          break
        case 6:
          text = data.no_offset_text ? data.no_offset_text : ''
          enabled = !!data.no_offset_enabled
          break
      }
      if (text) {
        doc.text(text, prevPosX + rectWidth / 2, positionY, 'center')
      } else {
        doc.text(enabled ? 'Да' : 'Нет', prevPosX + rectWidth / 2, positionY, 'center')
      }
    }
    doc.rect(prevPosX, positionY - 4, rectWidth, rectHeight)
    doc.text(
      i, prevPosX + rectWidth / 2, positionY + doc.getLineHeight(label) / scaleFactor + 0.5,
      'center'
    )
    prevPosX += rectWidth + 3
  })
  doc.setFontSize(pdfFontSize)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 2

  label = 'Аномалии формы зубных дуг'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.dental_arches_shape_anomalies
    ? orthodonticForm.dental_arches_shape_anomalies : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'Аномалии отдельных зубов'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.individual_teeth_anomalies ? orthodonticForm.individual_teeth_anomalies : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'Диастемы, тремы: в/челюсти'
  doc.text(label, pdfMargin, positionY)
  prevPosX = pdfMargin + doc.getTextWidth(label) + 1
  doc.line(prevPosX, positionY + 1, prevPosX + 28, positionY + 1)
  text = orthodonticForm && orthodonticForm.diastemas_tremas_upper_jaw ? orthodonticForm.diastemas_tremas_upper_jaw : ''
  doc.text(text, prevPosX + 14, positionY, 'center')
  prevPosX += 29
  label = 'величина (мм)'
  doc.text(label, prevPosX, positionY)
  prevPosX += doc.getTextWidth(label) + 1
  doc.line(prevPosX, positionY + 1, prevPosX + 28, positionY + 1)
  text = orthodonticForm && orthodonticForm.diastemas_tremas_upper_jaw_magnitude
    ? orthodonticForm.diastemas_tremas_upper_jaw_magnitude : ''
  doc.text(text, prevPosX + 14, positionY, 'center')
  prevPosX = prevPosX + 29
  label = 'н/челюсти'
  doc.text(label, prevPosX, positionY)
  prevPosX += doc.getTextWidth(label) + 1
  doc.line(prevPosX, positionY + 1, prevPosX + 26.5, positionY + 1)
  text = orthodonticForm && orthodonticForm.diastemas_tremas_lower_jaw ? orthodonticForm.diastemas_tremas_lower_jaw : ''
  doc.text(text, prevPosX + 13.25, positionY, 'center')
  positionY += doc.getLineHeight(label) / scaleFactor
  label = 'величина (мм)'
  prevPosX = pdfMargin
  doc.text(label, prevPosX, positionY)
  prevPosX += doc.getTextWidth(label) + 1
  doc.line(prevPosX, positionY + 1, prevPosX + 28, positionY + 1)
  text = orthodonticForm && orthodonticForm.diastemas_tremas_lower_jaw_magnitude
    ? orthodonticForm.diastemas_tremas_lower_jaw_magnitude : ''
  doc.text(text, prevPosX + 14, positionY, 'center')

  positionY += doc.getLineHeight(label) / scaleFactor
  label = 'Данные рентгенологического исследования'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.x_ray_examination_data ? orthodonticForm.x_ray_examination_data : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  positionY += doc.getLineHeight(label) / scaleFactor
  doc.text('Изучение диагностических моделей челюстей', docWidth / 2, positionY, 'center')
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  label = 'План лечения'
  doc.text(label, pdfMargin, positionY)
  text = orthodonticForm && orthodonticForm.treatment_plan ? orthodonticForm.treatment_plan : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  positionY += doc.getLineHeight(label) / scaleFactor
  const documentDate = orthodonticForm && orthodonticForm.document_date
    ? moment(orthodonticForm.document_date).format('DD MMMM YY').split(' ')
    : null
  let title = 'Дата'
  doc.text(title, pdfMargin, positionY)
  let dayXPosition = pdfMargin + doc.getTextWidth(title) + 1
  doc.line(dayXPosition, positionY + 1, dayXPosition + 6, positionY + 1)
  if (documentDate) {
    doc.text(documentDate[0], dayXPosition + 3, positionY, 'center')
  }
  let monthXPosition = dayXPosition + 7
  if (documentDate) {
    doc.text(documentDate[1], monthXPosition + 20, positionY, 'center')
  }
  doc.line(monthXPosition, positionY + 1, monthXPosition + 40, positionY + 1)
  doc.text('', monthXPosition, positionY, 'center')
  title = '20'
  doc.text(title, monthXPosition + 41, positionY)
  let yearXPosition = monthXPosition + doc.getTextWidth(title) + 42
  doc.line(yearXPosition, positionY + 1, yearXPosition + 5.5, positionY + 1)
  if (documentDate) {
    doc.text(documentDate[2], yearXPosition + 0.5, positionY)
  }
  doc.text('г.', yearXPosition + 5.5, positionY)

  positionY += (doc.getLineHeight(label) / scaleFactor) * 2
  doc.text('Врач-стоматолог-ортодонт', pdfMargin, positionY)
  positionY += 1

  doc.setFontSize(7)
  let lineWidth = docWidth / 4
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись)', docWidth / 2, positionY + 3, 'center')
  doc.line(docWidth - (lineWidth + pdfMargin), positionY, docWidth - pdfMargin, positionY)
  if (orthodonticForm && orthodonticForm.doctor && orthodonticForm.doctor.full_name_initials) {
    doc.setFontSize(pdfFontSize)
    doc.text(
      orthodonticForm.doctor.full_name_initials, docWidth - ((lineWidth / 2) + pdfMargin), positionY - 1,
      'center'
    )
    doc.setFontSize(7)
  }
  doc.text('(инициалы, фамилия)', docWidth - ((lineWidth / 2) + pdfMargin), positionY + 3, 'center')

  doc.setFontSize(pdfFontSize)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 2
  doc.text('С планом лечения ознакомлен(а)', pdfMargin, positionY)
  positionY += 1
  doc.setFontSize(7)
  lineWidth = docWidth / 4
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись пациента либо', docWidth / 2, positionY + 3, 'center')
  positionY += doc.getLineHeight(label) / scaleFactor / 1.5
  doc.text('законного представителя)', docWidth / 2, positionY + 3, 'center')
  doc.line(
    docWidth - (lineWidth + pdfMargin),
    positionY,
    docWidth - pdfMargin,
    positionY
  )
  doc.text('(инициалы, фамилия)', docWidth - ((lineWidth / 2) + pdfMargin), positionY + 3, 'center')

  doc.save(`Ортодонтический вкладыш к медицинской карте стоматологического пациента, ${fullName}.pdf`)
}

export const createOrthopedicInsert = (fullName, orthopedicForm) => {
  let doc = new jsPDF({
    orientation: 'landscape', unit: 'mm', format: appConstants.setting.printFormat.APP_A5, putOnlyUsedFonts: true
  })

  let positionY = 10
  const pdfFontSize = 11
  const smallFontSize = pdfFontSize - 2
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfFontBold = 'OpenSans-Bold'
  const tableLineHeightFactor = 1.2
  const textLineHeightFactor = 1.4
  const docWidth = doc.internal.pageSize.width
  const scaleFactor = doc.internal.scaleFactor
  const pdfMargin = 8
  const tableMargin = 8

  doc.setLineHeightFactor(textLineHeightFactor)
  doc.setFontSize(pdfFontSize)
  doc.setFont(pdfFontBold)
  let label = 'ОРТОПЕДИЧЕСКИЙ ВКЛАДЫШ'
  doc.text(label, docWidth / 2, positionY, 'center')
  positionY += doc.getLineHeight(label) / scaleFactor
  doc.text('К МЕДИЦИНСКОЙ КАРТЕ СТОМАТОЛОГИЧЕСКОГО ПАЦИЕНТА', docWidth / 2, positionY, 'center')
  doc.setFont(pdfFontNormal)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  doc.text('Подготовка полости рта к протезированию', docWidth / 2, positionY, 'center')
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5

  label = '1. Проведение профессиональной гигиены полости рта'
  doc.text(label, pdfMargin, positionY)
  let text = orthopedicForm && orthopedicForm.oral_hygiene ? orthopedicForm.oral_hygiene : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = '2. Снятие коронок, мостовидных протезов с зубов'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.teeth_crown_remove ? orthopedicForm.teeth_crown_remove : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'с дентальных имплантатов в области'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.implants_crown_remove ? orthopedicForm.implants_crown_remove : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = '3. Терапевтическое лечение:'
  doc.text(label, pdfMargin, positionY)
  positionY += doc.getLineHeight(label) / scaleFactor

  label = 'лечение кариеса и некариозных поражений зубов'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.caries_treatment ? orthopedicForm.caries_treatment : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'замена пломб'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.fillings_replacement ? orthopedicForm.fillings_replacement : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'эндодонтическое лечение зубов'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.endodontic_teeth_treatment ? orthopedicForm.endodontic_teeth_treatment : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'повторное эндодонтическое лечение зубов'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.repeated_endodontic_teeth_treatment
    ? orthopedicForm.repeated_endodontic_teeth_treatment : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'лечение заболеваний пародонта и слизистой оболочки рта'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.periodontal_diseases_treatment
    ? orthopedicForm.periodontal_diseases_treatment : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = '4. Хирургическое лечение:'
  doc.text(label, pdfMargin, positionY)
  positionY += doc.getLineHeight(label) / scaleFactor

  label = 'удаление зубов, корней'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.tooth_extraction ? orthopedicForm.tooth_extraction : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'хирургические операции:'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.surgical_operations ? orthopedicForm.surgical_operations : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'на мягких тканях'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.soft_tissue_surgeries ? orthopedicForm.soft_tissue_surgeries : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'на костях лицевого скелета'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.facial_skeletal_surgeries ? orthopedicForm.facial_skeletal_surgeries : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'операции дентальной имплантации'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.dental_implantation_surgeries
    ? orthopedicForm.dental_implantation_surgeries : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = 'другие'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.other_surgeries ? orthopedicForm.other_surgeries : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  label = '5. Консультации иных врачей-специалистов'
  doc.text(label, pdfMargin, positionY)
  text = orthopedicForm && orthopedicForm.other_specialist_consultations
    ? orthopedicForm.other_specialist_consultations : ''
  positionY = textToRows(text, label, doc, pdfMargin, positionY, pdfFontNormal, false, textLineHeightFactor)

  doc.addPage()
  positionY = 10

  const preparingOralCavityDate = orthopedicForm && orthopedicForm.preparing_oral_cavity_date
    ? moment(orthopedicForm.preparing_oral_cavity_date).format('DD MMMM YY').split(' ')
    : null
  let title = 'Дата составления'
  doc.text(title, pdfMargin, positionY)
  let dayXPosition = pdfMargin + doc.getTextWidth(title) + 1
  if (preparingOralCavityDate) {
    doc.text(preparingOralCavityDate[0], dayXPosition + 3, positionY, 'center')
  }
  doc.line(dayXPosition, positionY + 1, dayXPosition + 6, positionY + 1)
  let monthXPosition = dayXPosition + 7
  doc.line(monthXPosition, positionY + 1, monthXPosition + 40, positionY + 1)
  if (preparingOralCavityDate) {
    doc.text(preparingOralCavityDate[1], monthXPosition + 20, positionY, 'center')
  }
  title = '20'
  doc.text(title, monthXPosition + 41, positionY)
  let yearXPosition = monthXPosition + doc.getTextWidth(title) + 42
  doc.line(yearXPosition, positionY + 1, yearXPosition + 5.5, positionY + 1)
  if (preparingOralCavityDate) {
    doc.text(preparingOralCavityDate[2], yearXPosition + 0.5, positionY)
  }
  doc.text('г.', yearXPosition + 5.5, positionY)

  positionY += (doc.getLineHeight(label) / scaleFactor) * 2
  doc.text('Врач-стоматолог-ортопед', pdfMargin, positionY)
  positionY += 1

  doc.setFontSize(7)
  let lineWidth = docWidth / 4
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись)', docWidth / 2, positionY + 3, 'center')
  doc.line(docWidth - (lineWidth + pdfMargin), positionY, docWidth - pdfMargin, positionY)
  if (
    orthopedicForm
    && orthopedicForm.preparing_oral_cavity_doctor
    && orthopedicForm.preparing_oral_cavity_doctor.full_name_initials
  ) {
    doc.setFontSize(pdfFontSize)
    doc.text(
      orthopedicForm.preparing_oral_cavity_doctor.full_name_initials,
      docWidth - ((lineWidth / 2) + pdfMargin), positionY - 1, 'center'
    )
    doc.setFontSize(7)
  }
  doc.text(
    '(инициалы, фамилия специалиста)', docWidth - ((lineWidth / 2) + pdfMargin), positionY + 3, 'center'
  )

  doc.setFontSize(pdfFontSize)
  positionY += doc.getLineHeight(label) / scaleFactor
  doc.text('С планом подготовки', pdfMargin, positionY)
  positionY += doc.getLineHeight(label) / scaleFactor / 1.5
  doc.text('полости рта к протезированию', pdfMargin, positionY)
  positionY += doc.getLineHeight(label) / scaleFactor / 1.5
  doc.text('ознакомлен(а) и согласен(а):', pdfMargin, positionY)
  positionY += 1
  doc.setFontSize(7)
  lineWidth = docWidth / 4
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись)', docWidth / 2, positionY + 3, 'center')
  doc.line(docWidth - (lineWidth + pdfMargin), positionY, docWidth - pdfMargin, positionY)
  doc.text(
    '(инициалы, фамилия пациента или его',
    docWidth - ((lineWidth / 2) + pdfMargin),
    positionY + 3,
    'center'
  )
  positionY += doc.getLineHeight(label) / scaleFactor / 1.5
  doc.text(
    'законного представителя)', docWidth - ((lineWidth / 2) + pdfMargin), positionY + 3, 'center'
  )

  doc.addPage()
  positionY = 10
  let rows = [
    [
      { content: 'ПЛАН ОРТОПЕДИЧЕСКОГО ЛЕЧЕНИЯ', styles: { valign: 'middle' } },
      { content: 'КОРРЕКЦИЯ ПЛАНА', styles: { halign: 'center', valign: 'middle' } },
    ],
    [
      { content: '1. Изготовление штифтово-культевых вкладок на зубы', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.teeth_pin_core_inlays_manufacturing
          ? orthopedicForm.teeth_pin_core_inlays_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '2. Изготовление временных одиночных коронок:', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.temporary_single_crowns_manufacturing
          ? orthopedicForm.temporary_single_crowns_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: 'на зубы', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.teeth_temporary_single_crowns_manufacturing
          ? orthopedicForm.teeth_temporary_single_crowns_manufacturing : '',
        styles: { valign: 'middle' }
      }
    ],
    [
      { content: 'на дентальные имплантаты в области', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.implants_temporary_single_crowns_manufacturing
          ? orthopedicForm.implants_temporary_single_crowns_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '3. Изготовление временных мостовидных протезов:', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.temporary_bridge_dentures_manufacturing
          ? orthopedicForm.temporary_bridge_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: 'с опорой на зубы', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.teeth_temporary_bridge_dentures_manufacturing
          ? orthopedicForm.teeth_temporary_bridge_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      }
    ],
    [
      { content: 'с опорой на дентальные имплантаты в области', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.implants_temporary_bridge_dentures_manufacturing
          ? orthopedicForm.implants_temporary_bridge_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '4. Изготовление вкладок, виниров на зубы', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.teeth_inlays_veneers_manufacturing
          ? orthopedicForm.teeth_inlays_veneers_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '5. Изготовление одиночных коронок:', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.single_crowns_manufacturing
          ? orthopedicForm.single_crowns_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: 'на зубы', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.teeth_single_crowns_manufacturing
          ? orthopedicForm.teeth_single_crowns_manufacturing : '',
        styles: { valign: 'middle' }
      }
    ],
    [
      { content: 'с опорой на дентальные имплантаты в области', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.implants_bridge_dentures_single_crowns_manufacturing
          ? orthopedicForm.implants_bridge_dentures_single_crowns_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '6. Изготовление мостовидных протезов:', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.bridge_dentures_manufacturing
          ? orthopedicForm.bridge_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: 'с опорой на зубы', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.teeth_bridge_dentures_manufacturing
          ? orthopedicForm.teeth_bridge_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      }
    ],
    [
      { content: 'с опорой на дентальные имплантаты в области', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.implants_bridge_dentures_manufacturing
          ? orthopedicForm.implants_bridge_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '7. Изготовление частичных съемных протезов', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.partial_removable_dentures_manufacturing
          ? orthopedicForm.partial_removable_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '8. Изготовление бюгельных протезов', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.clasp_dentures_manufacturing
          ? orthopedicForm.clasp_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '9. Изготовление полных съемных протезов', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.complete_removable_dentures_manufacturing
          ? orthopedicForm.complete_removable_dentures_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '10. Изготовление индивидуальных окклюзионных шин (капп)', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.individual_occlusal_splints_manufacturing
          ? orthopedicForm.individual_occlusal_splints_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
    [
      { content: '11. Изготовление капп', styles: { valign: 'middle' } },
      {
        content: orthopedicForm && orthopedicForm.mouth_guards_manufacturing
          ? orthopedicForm.mouth_guards_manufacturing : '',
        styles: { valign: 'middle' }
      },
    ],
  ]
  doc.setLineHeightFactor(tableLineHeightFactor)
  doc.autoTable({
    theme: 'grid',
    startY: positionY,
    body: rows,
    margin: tableMargin,
    bodyStyles: { lineColor: [0, 0, 0], textColor: [0, 0, 0] },
    styles: { font: pdfFontNormal, fontSize: smallFontSize },
    didDrawPage: hook => positionY = hook.cursor.y + (doc.getLineHeight(label) / scaleFactor) * 3,
  })
  doc.setLineHeightFactor(textLineHeightFactor)
  doc.setFontSize(pdfFontSize)

  const planDate = orthopedicForm && orthopedicForm.plan_date
    ? moment(orthopedicForm.plan_date).format('DD MMMM YY').split(' ')
    : null
  const planAdjustmentDate = orthopedicForm && orthopedicForm.plan_adjustment_date
    ? moment(orthopedicForm.plan_adjustment_date).format('DD MMMM YY').split(' ')
    : null
  title = 'Дата составления'
  doc.text(title, pdfMargin, positionY)
  dayXPosition = pdfMargin + doc.getTextWidth(title) + 1
  doc.line(dayXPosition, positionY + 1, dayXPosition + 6, positionY + 1)
  if (planDate) {
    doc.text(planDate[0], dayXPosition + 3, positionY, 'center')
  }
  monthXPosition = dayXPosition + 7
  if (planDate) {
    doc.text(planDate[1], monthXPosition + 20, positionY, 'center')
  }
  doc.line(monthXPosition, positionY + 1, monthXPosition + 40, positionY + 1)
  doc.text('', monthXPosition, positionY, 'center')
  title = '20'
  doc.text(title, monthXPosition + 41, positionY)
  yearXPosition = monthXPosition + doc.getTextWidth(title) + 42
  doc.line(yearXPosition, positionY + 1, yearXPosition + 5.5, positionY + 1)
  if (planDate) {
    doc.text(planDate[2], yearXPosition + 0.5, positionY)
  }
  doc.text('г.', yearXPosition + 5.5, positionY)

  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  const planDoctor = orthopedicForm && orthopedicForm.plan_doctor ? orthopedicForm.plan_doctor : null
  const position = planDoctor && planDoctor.position ? planDoctor.position : ''
  const doctorInitials = planDoctor && planDoctor.full_name_initials ? planDoctor.full_name_initials : ''
  doc.text(position, pdfMargin + lineWidth / 2, positionY, 'center')
  positionY += 1
  doc.setFontSize(7)
  lineWidth = docWidth / 4
  doc.line(pdfMargin, positionY, pdfMargin + lineWidth, positionY)
  doc.text('(должность врача-специалиста)', pdfMargin + lineWidth / 2, positionY + 3, 'center')
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись)', docWidth / 2, positionY + 3, 'center')
  doc.line(docWidth - (lineWidth + pdfMargin), positionY, docWidth - pdfMargin, positionY)
  doc.setFontSize(pdfFontSize)
  doc.text(doctorInitials, docWidth - ((lineWidth / 2) + pdfMargin), positionY - 1, 'center')
  doc.setFontSize(7)
  doc.text(
    '(инициалы, фамилия специалиста)',
    docWidth - ((lineWidth / 2) + pdfMargin),
    positionY + 3,
    'center'
  )
  
  doc.setFontSize(pdfFontSize)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  doc.text('С планом ортопедического лечения', pdfMargin, positionY)
  positionY += doc.getLineHeight(label) / scaleFactor / 1.5
  doc.text('ознакомлен(а) и согласен(а):', pdfMargin, positionY)
  positionY += 1
  doc.setFontSize(7)
  lineWidth = docWidth / 4
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись)', docWidth / 2, positionY + 3, 'center')
  doc.line(docWidth - (lineWidth + pdfMargin), positionY, docWidth - pdfMargin, positionY)
  doc.text(
    '(инициалы, фамилия пациента или его',
    docWidth - ((lineWidth / 2) + pdfMargin),
    positionY + 3,
    'center'
  )
  positionY += doc.getLineHeight(label) / scaleFactor / 1.5
  doc.text(
    'законного представителя)', docWidth - ((lineWidth / 2) + pdfMargin), positionY + 3, 'center'
  )

  doc.setFontSize(pdfFontSize)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  title = 'Дата коррекции плана'
  doc.text(title, pdfMargin, positionY)
  dayXPosition = pdfMargin + doc.getTextWidth(title) + 1
  doc.line(dayXPosition, positionY + 1, dayXPosition + 6, positionY + 1)
  if (planAdjustmentDate) {
    doc.text(planAdjustmentDate[0], dayXPosition + 3, positionY, 'center')
  }
  monthXPosition = dayXPosition + 7
  if (planAdjustmentDate) {
    doc.text(planAdjustmentDate[1], monthXPosition + 20, positionY, 'center')
  }
  doc.line(monthXPosition, positionY + 1, monthXPosition + 40, positionY + 1)
  doc.text('', monthXPosition, positionY, 'center')
  title = '20'
  doc.text(title, monthXPosition + 41, positionY)
  yearXPosition = monthXPosition + doc.getTextWidth(title) + 42
  doc.line(yearXPosition, positionY + 1, yearXPosition + 5.5, positionY + 1)
  if (planAdjustmentDate) {
    doc.text(planAdjustmentDate[2], yearXPosition + 0.5, positionY)
  }
  doc.text('г.', yearXPosition + 5.5, positionY)

  positionY += (doc.getLineHeight(label) / scaleFactor) * 1.5
  doc.setFontSize(pdfFontSize)
  doc.text(position, pdfMargin + lineWidth / 2, positionY, 'center')
  positionY += 1
  doc.setFontSize(7)
  lineWidth = docWidth / 4
  doc.line(pdfMargin, positionY, pdfMargin + lineWidth, positionY)
  doc.text('(должность врача-специалиста)', pdfMargin + lineWidth / 2, positionY + 3, 'center')
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись)', docWidth / 2, positionY + 3, 'center')
  doc.line(docWidth - (lineWidth + pdfMargin), positionY, docWidth - pdfMargin, positionY)
  doc.setFontSize(pdfFontSize)
  doc.text(doctorInitials, docWidth - ((lineWidth / 2) + pdfMargin), positionY - 1, 'center')
  doc.setFontSize(7)
  doc.text(
    '(инициалы, фамилия специалиста)',
    docWidth - ((lineWidth / 2) + pdfMargin),
    positionY + 3,
    'center'
  )

  doc.setFontSize(pdfFontSize)
  positionY += (doc.getLineHeight(label) / scaleFactor) * 2
  doc.text('Ознакомлен(а) и согласен(а):', pdfMargin, positionY)
  positionY += 1
  doc.setFontSize(7)
  lineWidth = docWidth / 4
  doc.line((docWidth / 2) - (lineWidth / 2), positionY, (docWidth / 2) + (lineWidth / 2), positionY)
  doc.text('(подпись)', docWidth / 2, positionY + 3, 'center')
  doc.line(docWidth - (lineWidth + pdfMargin), positionY, docWidth - pdfMargin, positionY)
  doc.text(
    '(инициалы, фамилия пациента или его',
    docWidth - ((lineWidth / 2) + pdfMargin),
    positionY + 3,
    'center'
  )
  positionY += doc.getLineHeight(label) / scaleFactor / 1.5
  doc.text(
    'законного представителя)', docWidth - ((lineWidth / 2) + pdfMargin), positionY + 3, 'center'
  )

  doc.save(`Ортопедический вкладыш к медицинской карте стоматологического пациента, ${fullName}.pdf`)
}

export const addTextWithMultiRows = (
  pdf, posY, pdfRowHeight, pdfMargin, label, text = '', marginRight = 0, withLine = true,
  labelBold = false
) => {
  const pdfFontBold = 'OpenSans-Bold'
  const pdfFontNormal = 'OpenSans-Regular'
  const pdfWidth = 210
  label = label ? label + ' ' : ''
  text = text === null ? '' : text
  if (labelBold) {
    pdf.setFont(pdfFontBold)
  }
  const labelTextWidth = pdfMixins.methods.getTextWidth(pdf, label)

  pdf.text(label, pdfMargin, posY)

  if (labelBold) {
    pdf.setFont(pdfFontNormal)
  }

  let lines = pdf.splitTextToSize(
    text,
    (pdfWidth - (((pdfMargin * 2) + labelTextWidth))) - marginRight
  )
  let firstLine = ''
  if (lines.length > 0) {
    firstLine = lines[0]
  }
  text = text.slice(firstLine.length)
  lines = text === ''
    ? []
    : pdf.splitTextToSize(
      text,
      (pdfWidth - (pdfMargin * 2)) - marginRight
    )
  pdf.text(firstLine, pdfMargin + labelTextWidth, posY)
  if (withLine) {
    posY += 1
    pdf.line(pdfMargin + labelTextWidth, posY, (pdfWidth - pdfMargin) - marginRight, posY)
  }

  posY += pdfRowHeight

  lines.forEach(line => {
    pdf.text(line.trim(), pdfMargin, posY)
    if (withLine) {
      posY += 1
      pdf.line(pdfMargin, posY, (pdfWidth - pdfMargin) - marginRight, posY)
    }
    posY += pdfRowHeight
  })

  return posY
}

export const getDiagnosisCustomName = diary => diary.diagnosis_custom_name
  ? diary.diagnosis_custom_name.replaceAll('\t', ' ') : getDiagnosesNames(diary)

const getDiagnosesNames = diary =>
  diary.diagnoses.map(d => d.mkb ? `${d.full_name} (${d.mkb})` : d.full_name).join(', ')

export const fullNameToInitials = fullName => {
  if (fullName) {
    fullName = fullName ? fullName.split(' ') : ''
    if (fullName) {
      return fullName.length === 3
        ? `${mixins.methods.capitalizeFirstLetter(fullName[1][0])}.${mixins.methods.capitalizeFirstLetter(fullName[1][0])}. ${fullName[0]}`
        : fullName.join(' ')
    }
  }
  return fullName ? fullName : ''
}

export const getTextWidth = (pdf, text) => pdfMixins.methods.getTextWidth(pdf, text)

const getServicesTableTitleForActOfServicesRendered = text =>
  pdfMixins.methods.getServicesTableTitleForActOfServicesRendered(text)

export const addWarrantyToDiaryAct = diary => {
  let warrantyText = diary.without_warranty ? 'Без гарантии' : 'Не указана'
  if (diary.warranty) {
    warrantyText = `${diary.warranty.toString()} мес.`
  }
  if (diary.status_teeth && diary.status_teeth.length) {
    let teethWarranty = []
    diary.status_teeth.forEach(st => {
      if (st.warranty) {
        teethWarranty.push(`${st.teeth.number.toString()} - ${st.warranty.toLowerCase()}`)
      }
    })
    if (teethWarranty.length) {
      warrantyText = diary.warranty ? `${warrantyText}, ${teethWarranty.join(', ')}` : teethWarranty.join(', ')
    }
  }
  return warrantyText
}

export const addSignerBlockToTreatmentDiaryAct = (
  doc, y, docWidth, docMargin, rowHeight, fontBold, fontNormal, doctorInitials
) => {
  addSignerBlockToAct(doc, y, docWidth, docMargin, rowHeight, fontBold, fontNormal, doctorInitials, actPatientLabel())
}

export const addPriceBlockToTreatmentDiaryAct = (
  doc, y, fontBold, fontNormal, margin, totalPrice, salePrice, advancePercent, discountPercent
) => {
  const pdfWidth = doc.internal.pageSize.width
  const scaleFactor = doc.internal.scaleFactor
  let fontWeight = 'normal'
  if (fontBold === 'bold') {
    fontWeight = fontBold
    fontBold = fontNormal
  }

  doc.setFont(fontBold, fontWeight)
  doc.text('ИТОГО', margin, y)

  const step = 10
  let text = 'Начислено:'
  y += (doc.getLineHeight(text) / scaleFactor) * 1.35
  doc.text(text, margin, y)
  let prevTextWidth = doc.getTextWidth(text) + 1
  doc.setFont(fontNormal, 'normal')
  text = mixins.methods.toFixed(totalPrice, true)
  doc.text(text, margin + prevTextWidth, y)
  if (!discountPercent) {
    prevTextWidth = margin + prevTextWidth + doc.getTextWidth(text) + 1 + step
  }

  doc.setFont(fontBold, fontWeight)
  salePrice = (salePrice > 0 && totalPrice) || discountPercent === 100 ? salePrice : totalPrice
  if (advancePercent) {
    if (discountPercent) {
      text = 'Скидка:'
      y += doc.getLineHeight(text) / scaleFactor
      doc.text(text, margin, y)
      prevTextWidth = doc.getTextWidth(text) + 1
      doc.setFont(fontNormal, 'normal')
      doc.text(`${discountPercent ? discountPercent.toString() : 0}%`, margin + prevTextWidth, y)

      doc.setFont(fontBold, fontWeight)
      text = 'Сумма со скидкой:'
      y += doc.getLineHeight(text) / scaleFactor
      doc.text(text, margin, y)
      prevTextWidth = doc.getTextWidth(text) + 1
      doc.setFont(fontNormal, 'normal')
      doc.text(mixins.methods.toFixed(salePrice, true), margin + prevTextWidth, y)
    }

    doc.setFont(fontBold, fontWeight)
    text = 'Предоплата:'
    if (discountPercent) {
      y += doc.getLineHeight(text) / scaleFactor
    }
    let positionX = discountPercent ? margin : prevTextWidth
    doc.text(text, positionX, y)
    prevTextWidth = doc.getTextWidth(text) + 1
    positionX = discountPercent ? margin + prevTextWidth : (positionX + prevTextWidth)
    doc.setFont(fontNormal, 'normal')
    text = `${advancePercent}%`
    doc.text(text, positionX, y)
    if (!discountPercent) {
      positionX += doc.getTextWidth(text) + 1 + step
    }

    const advancePrice = salePrice ? salePrice - ((100 - advancePercent) * (salePrice / 100)) : 0
    doc.setFont(fontBold, fontWeight)
    text = 'К оплате:'
    if (discountPercent) {
      y += doc.getLineHeight(text) / scaleFactor
    }
    positionX = discountPercent ? margin : positionX
    doc.text(text, positionX, y)
    prevTextWidth = doc.getTextWidth(text) + 1
    positionX = discountPercent ? margin + prevTextWidth : (positionX + prevTextWidth)
    doc.setFont(fontNormal, 'normal')
    text = mixins.methods.toFixed(advancePrice, true)
    doc.text(text, positionX, y)
    if (!discountPercent) {
      positionX += doc.getTextWidth(text) + 1 + step
    }

    doc.setFont(fontBold, fontWeight)
    text = 'Остаток:'
    if (discountPercent) {
      y += doc.getLineHeight(text) / scaleFactor
    }
    positionX = discountPercent ? margin : positionX
    doc.text(text, positionX, y)
    prevTextWidth = doc.getTextWidth(text) + 1
    doc.setFont(fontNormal, 'normal')
    positionX = discountPercent ? margin + prevTextWidth : (positionX + prevTextWidth)
    doc.text(mixins.methods.toFixed(salePrice - advancePrice, true), positionX, y)
  } else {
    text = 'Скидка:'
    doc.text(text, (pdfWidth / 4) + 4, y)
    prevTextWidth = doc.getTextWidth(text) + 1
    doc.setFont(fontNormal, 'normal')
    doc.text(`${discountPercent ? discountPercent.toString() : 0}%`, (pdfWidth / 4) + 4 + prevTextWidth, y)

    doc.setFont(fontBold, fontWeight)
    text = 'К оплате:'
    doc.text(text, pdfWidth / 2, y)
    prevTextWidth = doc.getTextWidth(text) + 1
    doc.setFont(fontNormal, 'normal')
    doc.text(mixins.methods.toFixed(salePrice, true), (pdfWidth / 2) + prevTextWidth, y)
  }

  return y
}

export const addSignerBlockToPurchaseOrderAct = (
  doc, y, docWidth, docMargin, rowHeight, fontBold, fontNormal, doctorInitials
) => {
  addSignerBlockToAct(
    doc, y, docWidth, docMargin, rowHeight, fontBold, fontNormal, doctorInitials, 'Заказчик'
  )
}

export const addSignerBlockToAct = (
  doc, y, docWidth, docMargin, rowHeight, fontBold, fontNormal, doctorInitials, patientLabel
) => {
  doc.setFont(fontBold)
  doc.setDrawColor(0, 0, 0, 1)
  let cashierText = mixins.methods.getSignerLabelForAct()
  doc.text(cashierText, docMargin, y)
  const offset = docMargin + doc.getTextWidth(cashierText)
  if (doctorInitials) {
    doc.setFont(fontNormal)
    doc.text(doctorInitials, offset, y)
    doc.setFont(fontBold)
  }
  doc.line(offset, y + 1, offset + 60, y + 1)

  if (actShowPatientSignature()) {
    let patientTextStartPos = docWidth / 2
    let patientTextEndPos = docWidth - docMargin
    let patientText = `${patientLabel}: `
    if (patientTextStartPos < offset + 60) {
      y += rowHeight
      patientTextStartPos = docMargin
      patientTextEndPos = docMargin + doc.getTextWidth(patientText) + 60
    }
    doc.text(patientText, patientTextStartPos, y)
    doc.line(patientTextStartPos + doc.getTextWidth(patientText), y + 1, patientTextEndPos, y + 1)
  }
}

export const addConsentProcessingPersonalDataPage = (doc, y, pdfMargin, fontSize, textLineHeightFactor, data) => {
  const fontBold = 'OpenSans-Bold'
  const fontRegular = 'OpenSans-Regular'
  const fontBoldItalic = 'OpenSans-BoldItalic'
  const pdfWidth = doc.internal.pageSize.width
  const scaleFactor = doc.internal.scaleFactor
  const smallFontSize = fontSize - 2
  const endOfWidth = pdfWidth - pdfMargin
  const center = pdfWidth / 2
  const date = data.date && data.date.length === 3 ? data.date : null
  const day = date ? date[0] : ''
  const month = date ? date[1] : ''
  const year = date ? date[2] : ''
  const { getDocumentTypeName } = usePatient()

  doc.addPage()
  y = 10

  doc.setFontSize(fontSize)
  doc.setLineHeightFactor(textLineHeightFactor)
  doc.setFont(fontBold)
  let text = 'Согласие'
  doc.text(text, center, y, 'center')

  y += doc.getLineHeight(text) / scaleFactor
  doc.setFont(fontRegular)
  doc.setFontSize(fontSize - 0.19)
  text = '(отказ), отзыв согласия пациента (лиц, указанных в части второй статьи 18 Закона Республики Беларусь' +
    ' «О здравоохранении») на (от) внесение(я) и обработку(и) персональных данных пациента и информации, составляющей' +
    ' врачебную тайну'
  y = textToRows(text, '', doc, pdfMargin, y, fontRegular, true, textLineHeightFactor)
  y += doc.getLineHeight(text) / scaleFactor / 2

  text = 'Я,'
  doc.text(text, pdfMargin, y)
  let offset = doc.getTextWidth(text) + 1
  doc.line(pdfMargin + offset, y + 0.5, endOfWidth - 0.5, y + 0.5)
  text = ','
  doc.text(text, endOfWidth, y)
  offset = pdfMargin + offset + 1
  text = data.patientBirthday ? `${data.patientFIO}, ${data.patientBirthday}` : data.patientFIO
  doc.text(text, offset, y)
  y += doc.getLineHeight(text) / scaleFactor / 1.35
  text = '(фамилия, собственное имя, отчество (если таковое имеется), дата рождения пациента или лиц, указанных в' +
    ' части второй статьи 18 Закона Республики'
  doc.setFontSize(smallFontSize)
  doc.text(text, center, y, 'center')
  y += doc.getLineHeight(text) / scaleFactor
  text = 'Беларусь «О здравоохранении»)'
  doc.text(text, center, y, 'center')
  doc.setFontSize(fontSize)
  y += doc.getLineHeight(text) / scaleFactor

  let prevText = 'документ, удостоверяющий личность:'
  doc.text(prevText, pdfMargin, y)
  text = getDocumentTypeName(data.patientDocumentType)
  y = textToRows(text, prevText, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)

  text = 'серия'
  doc.text(text, pdfMargin, y)
  offset = pdfMargin + doc.getTextWidth(text) + 1
  doc.line(offset, y + 1, offset + 10, y + 1)
  if (data.patientDocumentSeries) {
    doc.text(data.patientDocumentSeries, offset + 5, y, 'center')
  }
  offset += 11
  text = 'номер'
  doc.text(text, offset, y)
  offset += doc.getTextWidth(text) + 1
  doc.line(offset, y + 1, offset + 40, y + 1)
  if (data.patientDocumentNumber) {
    doc.text(data.patientDocumentNumber, offset + 20, y, 'center')
  }
  offset += 41
  text = 'кем выдан'
  doc.text(text, offset, y)
  offset += doc.getTextWidth(text) + 1
  doc.line(offset, y + 1, endOfWidth, y + 1)
  if (data.patientDocumentIssuedBy) {
    doc.text(data.patientDocumentIssuedBy, offset + 1, y)
  }
  y += doc.getLineHeight(text) / scaleFactor + 1

  text = 'дата выдачи'
  doc.text(text, pdfMargin, y)
  offset = pdfMargin + doc.getTextWidth(text) + 1
  doc.line(offset, y + 1, offset + 30, y + 1)
  if (data.patientDocumentIssueDate) {
    doc.text(moment(data.patientDocumentIssueDate).format('DD.MM.YYYY'), offset + 15, y, 'center')
  }
  offset += 31
  text = 'г., идентификационный номер'
  doc.text(text, offset, y)
  offset += doc.getTextWidth(text) + 1
  doc.line(offset, y + 1, endOfWidth, y + 1)
  if (data.patientDocumentIdentifier) {
    doc.text(data.patientDocumentIdentifier, offset + 1, y)
  }
  y += doc.getLineHeight(text) / scaleFactor + 1

  text = 'проживающий по адресу:'
  doc.text(text, pdfMargin, y)
  y = textToRows(data.patientAddress, text, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)

  prevText = 'адрес электронной почты, контактный номер телефона:'
  doc.text(prevText, pdfMargin, y)
  if (data.patientPhone || data.patientEmail) {
    text = data.patientEmail
    if (data.patientPhone) {
      text = text ? `${text}, ${data.patientPhone}` : data.patientPhone
    }
  }
  y = textToRows(text, prevText, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)
  doc.line(pdfMargin, y + 1, endOfWidth, y + 1)
  y += doc.getLineHeight(text) / scaleFactor + 1
  doc.line(pdfMargin, y + 1, endOfWidth, y + 1)
  y += doc.getLineHeight(text) / scaleFactor + 1

  doc.setFont(fontBoldItalic)
  text = '(«даю согласие на», «отказываюсь от», «отзываю согласие на» - нужное указать)'
  doc.text(text, pdfMargin, y)
  doc.setFont(fontRegular)
  y += doc.getLineHeight(text) / scaleFactor
  doc.line(pdfMargin, y + 1, endOfWidth, y + 1)
  y += doc.getLineHeight(text) / scaleFactor + 1

  text = 'внесение(я) и обработку(и) персональных данных и информации, составляющей врачебную тайну, при формировании' +
    ' электронной медицинской карты пациента, информационных систем, информационных ресурсов, баз (банков) данных,' +
    ' реестров (регистров) в здравоохранении в отношении'
  y = textToRows(text, '', doc, pdfMargin, y, fontRegular, true, textLineHeightFactor)
  doc.line(pdfMargin, y + 1, endOfWidth, y + 1)
  y += doc.getLineHeight(text) / scaleFactor + 1
  doc.line(pdfMargin, y + 1, endOfWidth, y + 1)
  y += doc.getLineHeight(text) / scaleFactor / 1.25
  text = '(фамилия, собственное имя, отчество (если таковое имеется) пациента (заполняется в случае дачи согласия' +
    ' (отказа), отзыва согласия лицом), указанным в '
  doc.setFontSize(smallFontSize)
  doc.text(text, center, y, 'center')
  y += doc.getLineHeight(text) / scaleFactor
  text = 'части второй статьи 18 Закона Республики Беларусь «О здравоохранении»)'
  doc.text(text, center, y, 'center')
  doc.setFontSize(fontSize)
  y += (doc.getLineHeight(text) / scaleFactor) * 2

  doc.line(pdfMargin, y, center - pdfMargin, y)
  doc.line(center + pdfMargin, y, endOfWidth, y)
  y += doc.getLineHeight(text) / scaleFactor / 1.6
  doc.setFontSize(smallFontSize)
  text = '(подпись) (инициалы, фамилия пациента, или лиц, указанных в части \nвторой' +
    ' статьи 18 Закона Республики Беларусь «О здравоохранении»)'
  offset = pdfMargin + (center - pdfMargin * 2) / 2
  doc.text(text, offset, y, 'center')
  text = '(подпись) (инициалы, фамилия медицинского работника)'
  offset = center + pdfMargin + (endOfWidth - center - pdfMargin) / 2
  doc.text(text, offset, y, 'center')
  y += (doc.getLineHeight(text) / scaleFactor) * 3

  doc.setFontSize(fontSize)
  offset = pdfMargin + 10
  doc.line(pdfMargin, y + 0.5, offset, y + 0.5)
  doc.text(day, offset - 5, y, 'center')
  offset += 1
  doc.line(offset, y + 0.5, offset + 25, y + 0.5)
  doc.text(month, offset + 12.5, y, 'center')
  offset += 26
  text = '20'
  doc.text(text, offset, y)
  offset += doc.getTextWidth(text) + 0.25
  doc.line(offset, y + 0.5, offset + 6, y + 0.5)
  doc.text(year, offset + 3, y, 'center')
  offset += 7
  text = 'г.'
  doc.text(text, offset, y)
  
  return y
}

export const addDentalStatusPage = (
  doc, y, pdfMargin, fontSize, textLineHeightFactor, tableMargin, dentalStatusList, dentalStatusListLength, useA4,
  dentalStatusBody, visualInspection, maxStatusListCount, useCompact
) => {
  const fontBold = 'OpenSans-Bold'
  const fontRegular = 'OpenSans-Regular'
  const fontBoldItalic = 'OpenSans-BoldItalic'
  const pdfWidth = doc.internal.pageSize.width
  const pdfHeight = doc.internal.pageSize.height
  const hCenter = pdfWidth / 2
  const scaleFactor = doc.internal.scaleFactor
  const smallFontSize = fontSize - 1
  const tableLineHeightFactor = 1.2
  const dividerY = pdfHeight / 2

  doc.addPage()
  y = 10

  doc.setFont(fontBold)
  doc.setFontSize(fontSize)
  let title = 'Стоматологический статус'
  const titleXPosition = hCenter - doc.getTextWidth(title) - 1
  doc.text(title, titleXPosition, y)
  let dayXPosition = titleXPosition + doc.getTextWidth(title) + 1
  doc.setFont(fontRegular)
  doc.line(dayXPosition, y + 1, dayXPosition + 6, y + 1)
  let monthXPosition = dayXPosition + 7
  doc.line(monthXPosition, y + 1, monthXPosition + 40, y + 1)
  doc.text('', monthXPosition, y, 'center')
  title = '20'
  doc.text(title, monthXPosition + 41, y)
  let yearXPosition = monthXPosition + doc.getTextWidth(title) + 42
  doc.line(yearXPosition, y + 1, yearXPosition + 5.5, y + 1)
  doc.text('', yearXPosition + 0.5, y)
  doc.text('г.', yearXPosition + 5.5, y)

  y += (doc.getLineHeight(title) / scaleFactor) * (useCompact ? 1.5 : 2)
  const labelPositionY = y
  doc.setLineHeightFactor(tableLineHeightFactor)

  if (useCompact) {
    doc.setFontSize(smallFontSize)
  }
  doc.setFont(fontBold)
  let label = 'Условные обозначения:'
  doc.text(label, pdfMargin, y)
  y += doc.getLineHeight(label) / scaleFactor
  let cellWidth = 5
  const tableOffset = tableMargin * (useCompact ? 15.3 : 12.1)
  doc.setFont(fontBoldItalic)
  label = 'для временных зубов:'
  doc.text(label, pdfMargin, y)
  let text = ''
  if (useCompact) {
    let offset = pdfMargin + doc.getTextWidth(label) + 1
    doc.setFont(fontRegular)
    text = 'А – интактный зуб; В – некариозные поражения;'
    doc.text(text, offset, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = 'С – кариес, осложненный кариес, пломба с кариесом, дефектом, травма зуба;'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = 'D – пломба без кариеса, дефекта; Е – удален; F – герметизированные фиссуры;'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = 'G – коронка.'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
    doc.setFont(fontBoldItalic)
    label = 'для постоянных зубов:'
    doc.text(label, pdfMargin, y)
    offset = pdfMargin + doc.getTextWidth(label) + 1
    doc.setFont(fontRegular)
    text = '0 – интактный зуб; 1 – кариес, осложненный кариес,'
    doc.text(text, offset, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = 'травма зуба, пломба с кариесом, дефектом; 2 – некариозные поражения,'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = '3 – пломба без кариеса, дефекта; 4 – удален; 5 – дентальный имплантат;'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = '6 – штифтово-культевая вкладка; 7 – коронка, коронка на дентальном'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = 'имплантате, вкладка, винир; 8 – непрорезавшийся зуб; 9 – искусственный зуб в мостовидном протезе;' +
      ' F – герметизированные фиссуры;'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
    text = 'N – отсутствие зачатка постоянного зуба; R – корень зуба.'
    doc.text(text, pdfMargin, y)
    y += doc.getLineHeight(label) / scaleFactor
  } else {
    text = 'А – интактный зуб; В – некариозные поражения; С – кариес, осложненный кариес, пломба с' +
      ' кариесом, дефектом, травма зуба; D – пломба без кариеса, дефекта; Е – удален; F – герметизированные фиссуры;' +
      ' G – коронка.'
    y = textToRows(
      text, label, doc, pdfMargin, y, fontRegular, true, textLineHeightFactor,
      pdfWidth - tableOffset
    )
    doc.setFont(fontBoldItalic)
    label = 'для постоянных зубов:'
    doc.text(label, pdfMargin, y)
    text = '0 – интактный зуб; 1 – кариес, осложненный кариес, травма зуба, пломба с кариесом, дефектом;' +
      ' 2 – некариозные поражения, 3 – пломба без кариеса, дефекта; 4 – удален; 5 – дентальный имплантат; ' +
      '6 – штифтово-культевая вкладка; 7 – коронка, коронка на дентальном имплантате, вкладка, винир; ' +
      '8 – непрорезавшийся зуб; 9 – искусственный зуб в мостовидном протезе; F – герметизированные фиссуры; ' +
      'N – отсутствие зачатка постоянного зуба; R – корень зуба.'
    y = textToRows(
      text, label, doc, pdfMargin, y, fontRegular, true, textLineHeightFactor,
      pdfWidth - tableOffset + 0.5
    )
  }

  y = labelPositionY
  if (!useCompact) {
    doc.setFont(fontBold)
    doc.text('Дата', tableOffset + (cellWidth * 2) + 0.5, y)
    y += doc.getLineHeight(label) / scaleFactor
    const datePositionY = y
    doc.text('год', tableOffset + cellWidth + 2.5, datePositionY)
    doc.text('месяц', tableOffset + (cellWidth * 3), datePositionY)
  }
  doc.setFont(fontRegular)
  y += useCompact ? -2 : doc.getLineHeight(label) / scaleFactor / 2
  doc.autoTable({
    theme: 'grid',
    startY: y,
    body: dentalStatusBody,
    margin: useA4 ? tableOffset : { top: 0, right: 0, bottom: 0, left: tableOffset },
    styles: { font: fontRegular, fontSize: useCompact ? smallFontSize : fontSize, halign: 'center' },
    bodyStyles: {
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
      cellPadding: { top: 1, left: 0, middle: 0, bottom: 0 },
      cellWidth: cellWidth,
    },
    didParseCell: hook => {
      if (useCompact) {
        const lineWidth = { top: 0.1, right: 0.1, bottom: 0.1, left: 0.1 }
        lineWidth.bottom = hook.row.index === 2 ? 0.6 : 0.1
        lineWidth.top = hook.row.index === 3 ? 0.6 : 0.1
        lineWidth.right = hook.column.index === 7 ? 0.5 : 0.1
        lineWidth.left = hook.column.index === 8 ? 0.5 : 0.1
        hook.cell.styles.lineWidth = lineWidth
      } else {
        if (hook.column.index === 0) {
          hook.cell.styles.lineColor = [255, 255, 255]
        }
        if (
          [1, 2, 3, 4].includes(hook.column.index)
          && hook.row.index > maxStatusListCount - 1
          && hook.row.index < maxStatusListCount + 3
        ) {
          hook.cell.styles.lineColor = [255, 255, 255]
        }
      }
    },
    didDrawCell: hook => {
      if ([1, 2, 3, 4].includes(hook.column.index) && hook.row.index === maxStatusListCount) {
        doc.setDrawColor(0, 0, 0, 1)
        doc.line(hook.cell.x, hook.cell.y, hook.cell.x + hook.cell.width, hook.cell.y)
      }
    },
    didDrawPage: hook => {
      if (useCompact) {
        y = hook.cursor.y + (doc.getLineHeight(label) / scaleFactor) * 5
      } else {
        const cellHeight = hook.table.body[hook.table.body.length - 1].height
        doc.setDrawColor(0, 0, 0, 1)
        doc.line(
          hook.cursor.x - (cellWidth * 8) + 0.1,
          hook.cursor.y - (cellHeight * 10),
          hook.cursor.x - (cellWidth * 8) + 0.1,
          hook.cursor.y
        )
        doc.line(
          hook.cursor.x,
          hook.cursor.y - (cellHeight * 5) + 0.1,
          hook.cursor.x - (cellWidth * 16),
          hook.cursor.y - (cellHeight * 5) + 0.1
        )
      }
    }
  })

  if (!useCompact) {
    if (useA4) {
      doc.setLineDashPattern([3, 3], 0)
      doc.line(pdfMargin, dividerY, pdfWidth - pdfMargin, dividerY)
      doc.setLineDashPattern([], 0)
      y = dividerY
      y += (doc.getLineHeight(label) / scaleFactor) * 3
    } else {
      doc.addPage()
      y = 10
    }
  }

  doc.setFont(fontBold)
  doc.setFontSize(fontSize)
  doc.text('ИНДЕКСНАЯ ОЦЕНКА', hCenter, y, 'center')
  y += doc.getLineHeight(label) / scaleFactor
  doc.text('состояния стоматологического здоровья', hCenter, y, 'center')
  y += (doc.getLineHeight(label) / scaleFactor) * (useCompact ? 1.5 : 2)
  doc.setFont(fontRegular)

  const dentalStatus = dentalStatusListLength ? dentalStatusList[dentalStatusListLength - 1] : null
  label = '1. Интенсивность кариеса зубов:'
  doc.text(label, pdfMargin, y)
  let items = ['КПУ + кпу', 'КПУ', 'кпу']
  let prevPosX = pdfMargin + doc.getTextWidth(label) + 1
  let rectWidth = 4
  let rectHeight = 4
  items.forEach((i, idx) => {
    doc.rect(prevPosX, y - 3, rectWidth, rectHeight)
    prevPosX += rectWidth + 1
    doc.text(i, prevPosX, y)
    prevPosX += doc.getTextWidth(i) + 1
    if (!idx) {
      prevPosX += 15
    }
  })
  y += (doc.getLineHeight(label) / scaleFactor) * 1.5
  items = [
    { label: 'К =', value: dentalStatus ? `${ (dentalStatus.caries_count ? dentalStatus.caries_count : 0) }` : '' },
    {
      label: 'П =',
      value: dentalStatus ? `${ (dentalStatus.dental_filling_count ? dentalStatus.dental_filling_count : 0) }` : ''
    },
    {
      label: 'У =',
      value: dentalStatus ? `${ (dentalStatus.teeth_removed_count ? dentalStatus.teeth_removed_count : 0) }` : ''
    }
  ]
  prevPosX = pdfMargin + doc.getTextWidth(label) + 1
  items.forEach(i => {
    doc.text(i.label, prevPosX, y)
    prevPosX += doc.getTextWidth(i.label) + 1
    doc.rect(prevPosX, y - 3, rectWidth, rectHeight)
    doc.text(i.value, prevPosX + rectWidth / 2, y, 'center')
    prevPosX += rectWidth + 1
  })
  items = ['к =', 'п =', 'у =']
  prevPosX += 3.5
  items.forEach(i => {
    doc.text(i, prevPosX, y)
    prevPosX += doc.getTextWidth(i) + 1
    doc.rect(prevPosX, y - 3, rectWidth, rectHeight)
    prevPosX += rectWidth + 1
  })
  y += (doc.getLineHeight(label) / scaleFactor) * (useCompact ? 1.3 : 1.5)
  label = '2. Гигиенический статус:'
  doc.text(label, pdfMargin, y)
  y += doc.getLineHeight(label) / scaleFactor
  label = '2.1. упрощенный индекс гигиены полости рта (OHI-S):'
  doc.text(label, pdfMargin, y)
  y += (doc.getLineHeight(label) / scaleFactor) * (useCompact ? 1.3 : 1.5)
  label = 'Налет'
  doc.text(label, pdfMargin, y)
  label = 'Камень'
  doc.text(label, pdfWidth / 3, y)
  y += doc.getLineHeight(label) / scaleFactor
  let ohiArray = []
  if (dentalStatus && dentalStatus.ohis_view_map && dentalStatus.ohis_view_map.length) {
    const indexes = [0, 3, 1, 4, 2, 5, 6, 9, 7, 10, 8, 11]
    indexes.map(idx => {
      const savedIndex = dentalStatus.ohis_view_map.findIndex(i => i.index === idx)
      ohiArray.push(savedIndex > -1 ? dentalStatus.ohis_view_map[savedIndex].value.toString() : '')
    })
  } else {
    ohiArray = dentalStatus
      ? mixins.methods.findDentalIndexesByCalculation(12, 3, 2, dentalStatus.ohis)
      : Array.from({ length: 12 }, () => '')
  }

  prevPosX = pdfMargin
  rectWidth = 14
  items = { '1.7/1.6': 0, '1.1': 2, '2.6/2.7': 4 }
  Object.keys(items).forEach(i => {
    doc.text(i, prevPosX + rectWidth / 2, y, 'center')
    doc.rect(prevPosX, y + 2, rectWidth, rectHeight)
    doc.text(ohiArray[items[i]], prevPosX + rectWidth / 2, y + 5.1, 'center')
    prevPosX += rectWidth
  })
  prevPosX = pdfWidth / 3
  items = { '1.7/1.6': 6, '1.1': 8, '2.6/2.7': 10 }
  Object.keys(items).forEach(i => {
    doc.text(i, prevPosX + rectWidth / 2, y, 'center')
    doc.rect(prevPosX, y + 2, rectWidth, rectHeight)
    doc.text(ohiArray[items[i]], prevPosX + rectWidth / 2, y + 5.1, 'center')
    prevPosX += rectWidth
  })
  y += rectHeight + 2
  prevPosX = pdfMargin
  items = { '4.7/4.6': 1, '3.1': 3, '3.6/3.7': 5 }
  Object.keys(items).forEach(i => {
    doc.rect(prevPosX, y, rectWidth, rectHeight)
    doc.text(i, prevPosX + rectWidth / 2, y + rectHeight + 4, 'center')
    doc.text(ohiArray[items[i]], prevPosX + rectWidth / 2, y + 3.2, 'center')
    prevPosX += rectWidth
  })
  prevPosX = pdfWidth / 3
  items = { '4.7/4.6': 7, '3.1': 9, '3.6/3.7': 11 }
  Object.keys(items).forEach(i => {
    doc.rect(prevPosX, y, rectWidth, rectHeight)
    doc.text(i, prevPosX + rectWidth / 2, y + rectHeight + 4, 'center')
    doc.text(ohiArray[items[i]], prevPosX + rectWidth / 2, y + 3.2, 'center')
    prevPosX += rectWidth
  })
  label = 'OHI-S ='
  prevPosX += rectWidth * 2
  rectWidth = 39
  rectHeight = 5
  let labelPositionX = prevPosX
  doc.text(label, prevPosX, y - 4)
  prevPosX += doc.getTextWidth(label) + 1
  doc.rect(prevPosX, y - 7.5, pdfWidth - pdfMargin - prevPosX, rectHeight)
  const ohis = dentalStatus && dentalStatus.ohis ? dentalStatus.ohis : 0
  doc.text(
    dentalStatus && ohis ? ohis.toString() : '',
    prevPosX + (pdfWidth - pdfMargin - prevPosX) / 2,
    y - 3.9,
    'center'
  )
  y += 4
  doc.setFontSize(7)
  if (ohis) {
    doc.text(
      mixins.methods.getOhiDescription(ohis),
      labelPositionX + ((pdfWidth - pdfMargin - labelPositionX) / 2),
      y - 1,
      'center'
    )
  }
  doc.line(labelPositionX, y, pdfWidth - pdfMargin, y)
  doc.text('(интерпретация)', labelPositionX + ((pdfWidth - pdfMargin - labelPositionX) / 2), y + 3, 'center')

  y += (doc.getLineHeight(label) / scaleFactor) * 3
  doc.setFontSize(fontSize)
  label = '2.2. для детей – индекс налета (PLI):'
  doc.text(label, pdfMargin, y)
  y += (doc.getLineHeight(label) / scaleFactor) * (useCompact ? 1.5 : 2)
  prevPosX = pdfMargin
  label = 'зуб'
  const pliTeeth = dentalStatus && dentalStatus.pli_teeth && dentalStatus.pli_teeth.length
    ? dentalStatus.pli_teeth : []
  const pliTeethStatus = dentalStatus && dentalStatus.pli_teeth_status && dentalStatus.pli_teeth_status.length
    ? dentalStatus.pli_teeth_status : []
  const blocksCount = 4
  for (let i = 0; i < blocksCount; i++) {
    const maxBlockWidth = 30
    doc.text(label, (prevPosX + maxBlockWidth / 2) - 2, y - 1, 'center')
    doc.rect((prevPosX + maxBlockWidth / 2) + 2, y - 4, 4, 4)
    doc.rect(prevPosX, y + 2, 9, rectHeight * 2)
    doc.rect(prevPosX + 9, y + 2, 12, rectHeight)
    doc.rect(prevPosX + 9, y + 2 + rectHeight, 12, rectHeight)
    doc.rect(prevPosX + 21, y + 2, 9, rectHeight * 2)
    if (pliTeeth.length) {
      const tootNumber = pliTeeth[i]
      if (tootNumber) {
        doc.setFontSize(smallFontSize - 1.5)
        doc.text(tootNumber, (prevPosX + maxBlockWidth / 2) + 4, y - 1, 'center')
        doc.setFontSize(smallFontSize)
      }
    }
    if (pliTeethStatus.length) {
      const toothStatuses = pliTeethStatus.slice(i * blocksCount, i * blocksCount + blocksCount)
      const stOne = toothStatuses && toothStatuses.length ? toothStatuses[0] : null
      const stTwo = toothStatuses && toothStatuses.length ? toothStatuses[1] : null
      const stThree = toothStatuses && toothStatuses.length ? toothStatuses[2] : null
      const stFour = toothStatuses && toothStatuses.length ? toothStatuses[3] : null
      doc.text(
        stOne !== null ? stOne.toString() : '', prevPosX + 4.5,
        y + rectHeight * 1.6, 'center'
      )
      doc.text(
        stTwo !== null ? stTwo.toString() : '', (prevPosX + maxBlockWidth / 2),
        y + rectHeight + 0.5, 'center'
      )
      doc.text(
        stThree !== null ? stThree.toString() : '', (prevPosX + maxBlockWidth / 2),
        y + rectHeight * 2 + 0.5, 'center'
      )
      doc.text(
        stFour !== null ? stFour.toString() : '', prevPosX + 25.5,
        y + rectHeight * 1.6, 'center'
      )
    }
    prevPosX += maxBlockWidth + 6
  }
  label = 'PLI ='
  labelPositionX = prevPosX
  y += 3
  doc.text(label, prevPosX, y - 2)
  prevPosX += doc.getTextWidth(label) + 1
  doc.rect(prevPosX, y - 5.5, pdfWidth - pdfMargin - prevPosX, rectHeight)
  let pli = ''
  if (dentalStatus) {
    pli = dentalStatus.pli === null ? '' : dentalStatus.pli.toString()
  }
  doc.text(
    pli,
    prevPosX + (pdfWidth - pdfMargin - prevPosX) / 2,
    y - 1.9,
    'center'
  )
  y += (doc.getLineHeight(label) / scaleFactor) * 1.5
  if (pli !== '') {
    doc.setFontSize(smallFontSize)
    doc.text(
      mixins.methods.getPliDescription(parseFloat(pli)),
      labelPositionX + ((pdfWidth - pdfMargin - labelPositionX) / 2),
      y - 1,
      'center'
    )
  }
  doc.line(labelPositionX, y, pdfWidth - pdfMargin, y)
  doc.setFontSize(7)
  doc.text('(интерпретация)', labelPositionX + ((pdfWidth - pdfMargin - labelPositionX) / 2), y + 3, 'center')

  y += (doc.getLineHeight(label) / scaleFactor) * 4
  doc.line(pdfMargin, y, pdfWidth - pdfMargin, y)
  doc.text('(дополнительно указывается локализация: зуб, сторона, челюсть, иное уточнение)', hCenter, y + 3, 'center')

  y += (doc.getLineHeight(label) / scaleFactor) * 4
  doc.setFontSize(fontSize)
  doc.setLineHeightFactor(textLineHeightFactor)

  if (useCompact && useA4) {
    doc.setLineDashPattern([3, 3], 0)
    doc.line(pdfMargin, dividerY, pdfWidth - pdfMargin, dividerY)
    doc.setLineDashPattern([], 0)
    y = dividerY + 10
  } else {
    doc.addPage()
    y = 10
  }

  label = 'Прикус:'
  doc.text(label, pdfMargin, y)
  text = visualInspection && visualInspection.bite ? visualInspection.bite : ''
  y = textToLines(
    text, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor, true,
    pdfMargin, null, 2
  )

  label = 'Состояние зубов, зубных рядов, пломб, зубных протезов:'
  doc.text(label, pdfMargin, y)
  text = visualInspection && visualInspection.condition_of_hard_tissues_of_teeth
    ? visualInspection.condition_of_hard_tissues_of_teeth : ''
  y = textToLines(
    text, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor, true,
    pdfMargin, null, 5
  )

  label = 'Состояние пародонта:'
  doc.text(label, pdfMargin, y)
  text = visualInspection && visualInspection.periodontium_condition ? visualInspection.periodontium_condition : ''
  y = textToLines(
    text, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor, true,
    pdfMargin, null, 4
  )

  label = 'Состояние слизистой оболочки рта:'
  doc.text(label, pdfMargin, y)
  text = visualInspection && visualInspection.condition_of_the_oral_mucosa
    ? visualInspection.condition_of_the_oral_mucosa : ''
  y = textToLines(
    text, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor, true,
    pdfMargin, null, 4
  )

  label = 'Данные рентгеновского и других исследований:'
  doc.text(label, pdfMargin, y)
  text = visualInspection && visualInspection.x_ray_data ? visualInspection.x_ray_data : ''
  y = textToLines(
    text, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor, true,
    pdfMargin, null, 4
  )

  label = 'Диагноз (МКБ-10С):'
  doc.text(label, pdfMargin, y)
  text = visualInspection && visualInspection.preliminary_diagnosis ? visualInspection.preliminary_diagnosis : ''
  y = textToLines(
    text, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor, true,
    pdfMargin, null, 3
  )
  y -= 1
  doc.setFontSize(fontSize - 3)
  text = '* Международная классификация стоматологических болезней на основе Международной классификации болезней и проблем, связанных со здоровьем, десятого пересмотра.'
  doc.text(text, hCenter, y, 'center')

  return y
}


export const addDentalMainPage = (doc, y, data) => {
  const fontBold = 'OpenSans-Bold'
  const fontRegular = 'OpenSans-Regular'
  const pdfWidth = doc.internal.pageSize.width
  const pdfHeight = doc.internal.pageSize.height
  const hCenter = pdfWidth / 2
  const pdfMargin = data.pdfMargin
  const endWidth = pdfWidth - pdfMargin
  const scaleFactor = doc.internal.scaleFactor
  const fontSize = data.fontSize
  const textLineHeightFactor = data.textLineHeightFactor
  const smallFontSize = fontSize - 3
  const dividerY = pdfHeight / 2
  const splitDate = data.dateSplit
  const actualMainPage = data.documentUseActualMainPageFormat

  let text = ''
  if (actualMainPage) {
    doc.setFontSize(smallFontSize)
    doc.setFont(fontRegular)
    text = 'Приложение 3 к постановлению\n' +
      'Министерства здравоохранения\n' +
      'Республики Беларусь 16.12.2025 № 203\n' +
      'Форма № 043/у'
    doc.text(text, endWidth, 10, 'right')
    y += (doc.getLineHeight(text) / scaleFactor) * 2
    let lineWidth = 60
    doc.line(pdfMargin, y + 0.5, pdfMargin + lineWidth, y + 0.5)
    doc.setFontSize(fontSize)
    let counter = 0
    let integratorName = data.integrator.name
    integratorName = integratorName.length > 30
      ? integratorName.replace(/ /g, (match) => {
        counter++
        return counter === 3 ? '\n' : match
      })
      : integratorName
    const nameY = integratorName.includes('\n') ? y - doc.getLineHeight(text) / scaleFactor : y
    doc.text(integratorName, pdfMargin + (lineWidth / 2), nameY, 'center')
    doc.setFontSize(smallFontSize)

    y += doc.getLineHeight(text) / scaleFactor
    doc.text('(наименование организации здравоохранения)', pdfMargin + (lineWidth / 2), y, 'center')
    doc.setFontSize(fontSize)
    doc.setFont(fontBold)

    const logoPng = getLogoPng()
    if (logoPng) {
      const img = new Image()
      img.src = logoPng.url
      const height = 14
      const scaling_y = height / logoPng.height
      const width = logoPng.width * scaling_y
      doc.addImage(img, 'png', hCenter - (width / 2), 10, width, height)
      y += height
    } else {
      y += (doc.getLineHeight(text) / scaleFactor) * 3
    }

    doc.setLineHeightFactor(textLineHeightFactor)
    text = 'МЕДИЦИНСКАЯ КАРТА стоматологического пациента №'
    doc.text(text, hCenter, y, 'center')
    let offset = hCenter + (doc.getTextWidth(text) / 2) + 1
    doc.setFont(fontRegular)
    lineWidth = data.patientCardNumber && data.patientCardNumber.length > 4 ? 14 : 9
    doc.text(data.patientCardNumber, offset + (lineWidth / 2), y, 'center')
    doc.line(offset, y + 0.5, offset + lineWidth, y + 0.5)

    y += doc.getLineHeight(text) / scaleFactor
    doc.setFont(fontRegular)
    let dayXPosition = hCenter - 24
    doc.line(dayXPosition, y + 1, dayXPosition + 6, y + 1)
    let textPosition = dayXPosition + 3
    doc.text(splitDate[0], textPosition, y, 'center')
    let monthXPosition = hCenter
    doc.line(monthXPosition - 17, y + 1, monthXPosition + 17, y + 1)
    textPosition = hCenter
    doc.text(splitDate[1], monthXPosition, y, 'center')
    let title = '20'
    doc.text(title, hCenter + 18, y)
    let yearXPosition = hCenter + 22
    doc.line(yearXPosition, y + 1, yearXPosition + 5.5, y + 1)
    doc.text(splitDate[2], yearXPosition + 0.5, y)
    doc.text('г.', yearXPosition + 5.5, y)
    doc.setFontSize(smallFontSize)
    doc.text('(дата заполнения)', hCenter, y + 3.5, 'center')

    // Full name
    doc.setFontSize(fontSize)
    y += (doc.getLineHeight(text) / scaleFactor) * 2
    let label = 'Фамилия, собственное имя, отчество пациента (если таковое имеется)'
    doc.text(label, pdfMargin, y)
    y = textToLines(
      data.patientFIO, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor,
      true, pdfMargin, null, 1
    )
    y += 1

    // Birthdate
    label = 'Число, месяц, год рождения '
    doc.text(label, pdfMargin, y)
    offset = pdfMargin + doc.getTextWidth(label) + 1
    const splitBirthday = data.patientBirthday ? data.patientBirthday.split(' ') : null
    let day = splitBirthday ? splitBirthday[0] : null
    let month = splitBirthday ? splitBirthday[1] : null
    let year = splitBirthday ? splitBirthday[2] : null
    doc.line(offset, y + 1, offset + 6, y + 1)
    if (day) {
      doc.text(day, offset + 3, y, 'center')
    }
    offset += 7
    doc.line(offset, y + 1, offset + 34, y + 1)
    if (month) {
      doc.text(month, offset + 17, y, 'center')
    }
    offset += 35
    doc.line(offset, y + 1, offset + 11, y + 1)
    if (year) {
      doc.text(year, offset + 5.5, y, 'center')
    }
    offset += 12
    text = 'г.'
    doc.text(text, offset, y)

    // Gender
    let squareW = 20
    let squareH = 5
    let firstSquarePosition = (pdfWidth - (pdfWidth / 5) - squareW) - pdfMargin
    let secondSquarePosition = firstSquarePosition + squareW
    let male = 'М'
    let female = 'Ж'
    let genderLabel = 'Пол '
    doc.text(genderLabel, firstSquarePosition - doc.getTextWidth(genderLabel), y)
    doc.text(male, firstSquarePosition + squareW / 2, y, 'center')
    doc.text(female, secondSquarePosition + squareW / 2, y, 'center')
    y += 1
    doc.rect(firstSquarePosition, y - squareH, squareW, squareH)
    doc.rect(secondSquarePosition, y - squareH, squareW, squareH)
    doc.line(
      data.patientGender === 1 ? firstSquarePosition + (squareW / 3) : secondSquarePosition + (squareW / 3),
      y - (squareH / 2.2),
      data.patientGender === 1 ? secondSquarePosition - (squareW / 3) : secondSquarePosition + squareW - (squareW / 3),
      y - (squareH / 2.2)
    )
    doc.setFontSize(smallFontSize)
    text = '(ненужное зачеркнуть)'
    y += 3
    doc.text(text, secondSquarePosition, y, 'center')

    // Identification number
    doc.setFontSize(fontSize)
    y += doc.getLineHeight(text) / scaleFactor / 1.5
    label = 'Идентификационный номер'
    doc.text(label, pdfMargin, y)
    y = textToLines(
      data.patientDocumentIdentifier, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor,
      true
    )

    // Citizenship
    doc.setFontSize(fontSize)
    label = 'Гражданство'
    doc.text(label, pdfMargin, y)
    y = textToLines(
      data.patientCitizenship, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor,
      true
    )

    // Registration address
    label = 'Адрес места жительства (регистрации)'
    doc.text(label, pdfMargin, y)
    y = textToLines(
      data.patientResidencePlace, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor,
      true, pdfMargin, null, 1
    )

    // Current address
    label = 'Адрес места пребывания (фактического проживания)'
    doc.text(label, pdfMargin, y)
    y = textToLines(
      data.patientAddress, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor,
      true, pdfMargin, null, 1
    )

    // Mobile phone
    label = 'Номер контактного телефона'
    doc.text(label, pdfMargin, y)
    y = textToLines(
      data.patientPhone, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor,
      true
    )

    // Work place
    label = 'Место работы (должность служащего), службы (учебы)'
    doc.text(label, pdfMargin, y)
    y = textToLines(
      data.patientWorkPlace, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor,
      true, pdfMargin, null, 1
    )

    // Social benefits
    text = 'Государственные социальные льготы:'
    doc.text(text, pdfMargin, y + 0.5)
    firstSquarePosition = pdfMargin + doc.getTextWidth(text) + 4
    secondSquarePosition = firstSquarePosition + squareW
    doc.rect(firstSquarePosition, y - 3, squareW, squareH)
    doc.rect(secondSquarePosition, y - 3, squareW, squareH)
    doc.text('нет', firstSquarePosition + squareW / 2, y + 0.5, 'center')
    doc.text('да', secondSquarePosition + squareW / 2, y + 0.5, 'center')
    doc.setFontSize(smallFontSize)
    text = '(ненужное зачеркнуть)'
    y += 5
    doc.text(text, secondSquarePosition, y, 'center')

    label = 'Категория льгот:'
    doc.setFontSize(fontSize)
    y += doc.getLineHeight(text) / scaleFactor / 1.5
    doc.text(label, pdfMargin, y)
    y = textToLines('', label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor, true)

    label = 'Документ, удостоверяющий право на льготы:'
    doc.text(label, pdfMargin, y)
    let positionXStart = pdfMargin + doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, positionXStart + 24, y + 1)
    positionXStart += 25
    label = 'серия'
    doc.text(label, positionXStart, y)
    positionXStart += doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, positionXStart + 20, y + 1)
    positionXStart += 21
    label = 'номер'
    doc.text(label, positionXStart, y)
    positionXStart += doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, positionXStart + 33, y + 1)
    positionXStart += 34
    label = 'выдан'
    doc.text(label, positionXStart, y)
    y += doc.getLineHeight(label) / scaleFactor / 1.4
    positionXStart = pdfMargin
    doc.line(positionXStart, y, positionXStart + 80, y)
    positionXStart += 81
    label = 'дата выдачи'
    doc.text(label, positionXStart, y)
    positionXStart += doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, positionXStart + 25, y + 1)
    positionXStart += 26
    label = 'срок действия'
    doc.text(label, positionXStart, y)
    positionXStart += doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, endWidth, y + 1)

    y += doc.getLineHeight(label) / scaleFactor / 1.4
    label = 'С правилами внутренного распорядка для пациентов ознакомлен:'
    doc.text(label, pdfMargin, y)
    positionXStart = pdfMargin + doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, endWidth, y + 1)
    positionXStart = positionXStart + (endWidth - positionXStart) / 2
    y += 4
    doc.setFontSize(smallFontSize)
    label = '(ФИО, подпись)'
    doc.text(label, positionXStart, y, 'center')
    positionXStart += doc.getTextWidth(label) + 1

    doc.setLineDashPattern([3, 3], 0)
    doc.line(pdfMargin, dividerY, endWidth, dividerY)
    doc.setLineDashPattern([], 0)
  } else {
    doc = mixins.methods.getPdfDocumentHeader(
      doc, data.integrator, fontRegular, pdfMargin, false, true
    )
    const headerText = 'Приложение 3\n' +
      'к постановлению\n' +
      'Министерства здравоохранения\n' +
      'Республики Беларусь\n' +
      '16.12.2025 № 203'
    doc.text(headerText, pdfWidth - pdfMargin * 6.5 - 1, 10)
    text = 'Форма № 043/у'
    doc.text(text, pdfWidth - pdfMargin, 30, 'right')

    doc.setFontSize(fontSize)
    doc.setFont(fontBold)

    y = mixins.methods.getPositionAfterHeader(
      data.integrator, 40, true, false, true
    )
    y -= doc.getLineHeight(text) / scaleFactor / 2
    doc.setLineHeightFactor(textLineHeightFactor)
    doc.text('МЕДИЦИНСКАЯ КАРТА', hCenter, y, 'center')
    y += doc.getLineHeight(text) / scaleFactor
    doc.text('стоматологического пациента №' + data.patientCardNumber, hCenter, y, 'center')

    y += (doc.getLineHeight(text) / scaleFactor) * 1.5
    doc.setFont(fontRegular)

    let dayXPosition = (hCenter) - 27
    doc.line(dayXPosition, y + 1, dayXPosition + 6, y + 1)
    let textPosition = dayXPosition + 3
    doc.text(splitDate[0], textPosition, y, 'center')
    let monthXPosition = hCenter
    doc.line(monthXPosition - 20, y + 1, monthXPosition + 20, y + 1)
    textPosition = hCenter
    doc.text(splitDate[1], monthXPosition, y, 'center')
    let title = '20'
    doc.text(title, (hCenter) + 21, y)
    let yearXPosition = (hCenter) + 25
    doc.line(yearXPosition, y + 1, yearXPosition + 5.5, y + 1)
    doc.text(splitDate[2], yearXPosition + 0.5, y)
    doc.text('г.', yearXPosition + 5.5, y)
    doc.setFontSize(smallFontSize)
    doc.text('(дата заполнения)', hCenter, y + 3.5, 'center')

    // Full name
    doc.setFontSize(data.oneMainPage ? fontSize - 1 : fontSize)
    y += (doc.getLineHeight(text) / scaleFactor) * 2
    let label = 'Фамилия, собственное имя, отчество пациента (если таковое имеется)'
    doc.text(label, pdfMargin, y)
    y = textToRows(data.patientFIO, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)

    // Birthdate
    label = 'Число, месяц, год рождения '
    doc.text(label, pdfMargin, y)
    doc.text(
      data.patientBirthday,
      pdfMargin + doc.getTextWidth(label) + (doc.getTextWidth(data.patientBirthday) / 1.5),
      y,
      'center'
    )

    // Gender
    let squareSize = 6
    let firstSquarePosition = (pdfWidth - (pdfWidth / 4) - squareSize) - pdfMargin
    let secondSquarePosition = firstSquarePosition + squareSize
    doc.rect(firstSquarePosition, y - (squareSize / 1.5), squareSize, squareSize)
    doc.rect(secondSquarePosition, y - (squareSize / 1.5), squareSize, squareSize)
    let male = 'М'
    let female = 'Ж'
    let genderLabel = 'Пол '
    doc.text(genderLabel, firstSquarePosition - doc.getTextWidth(genderLabel), y)
    doc.text(male, firstSquarePosition + squareSize / 2, y, 'center')
    doc.text(female, secondSquarePosition + squareSize / 2, y, 'center')
    doc.line(
      data.patientGender === 1 ? firstSquarePosition : secondSquarePosition,
      y - (squareSize / 4),
      data.patientGender === 1 ? firstSquarePosition + squareSize : secondSquarePosition + squareSize,
      y - (squareSize / 4)
    )

    y += 1
    doc.line(pdfMargin + doc.getTextWidth(label), y, hCenter, y)

    // Mobile phone
    y += doc.getLineHeight(label) / scaleFactor
    label = 'Номер контактного телефона'
    doc.text(label, pdfMargin, y)
    y = textToRows(data.patientPhone, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)

    // Identification number
    label = 'Идентификационный номер'
    doc.text(label, pdfMargin, y)
    y = textToRows(
      data.patientDocumentIdentifier, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor
    )

    // Citizenship
    label = 'Гражданство'
    doc.text(label, pdfMargin, y)
    y = textToRows(
      data.patientCitizenship, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor
    )

    // Registration address
    label = 'Адрес места жительства (регистрации)'
    doc.text(label, pdfMargin, y)
    y = textToRows(
      data.patientResidencePlace, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor
    )

    let lineLength = 30
    // Consent to personal data processing
    if (data.documentUseConsentProcessingPersonalData) {
      if (data.useA4) {
        y = data.documentUseInternalRegulations
          ? (pdfHeight / 2) - (doc.getLineHeight(label) / scaleFactor) * 6
          : (pdfHeight / 2) - (doc.getLineHeight(label) / scaleFactor) * 3
      } else {
        y = data.documentUseInternalRegulations
          ? (pdfHeight / 2) + (doc.getLineHeight(label) / scaleFactor) * 7
          : (pdfHeight / 2) + (doc.getLineHeight(label) / scaleFactor) * 10
      }
      doc.setFont(fontBold)
      doc.setFontSize(fontSize)
      doc.text('Даю согласие на обработку персональных данных:', hCenter, y, 'center')
      doc.setFont(fontRegular)

      y += doc.getLineHeight(label) / scaleFactor
      doc.line(pdfMargin, y, (hCenter) - lineLength, y)
      doc.line((hCenter) + lineLength - 12, y, pdfWidth - pdfMargin, y)

      doc.setFontSize(7)
      doc.text('(подпись)', pdfMargin + lineLength, y + 3)
      doc.text('(ф.и.о)', pdfWidth - pdfMargin - lineLength - 12, y + 3)
      y += doc.getLineHeight(label) / scaleFactor + 4
    } else {
      if (data.useA4) {
        y = (pdfHeight / 2) - (doc.getLineHeight(label) / scaleFactor) * 3
      } else if (data.documentUseInternalRegulations) {
        y = (pdfHeight / 2) + (doc.getLineHeight(label) / scaleFactor) * 10
      }
    }

    // Confirm terms
    if (data.documentUseInternalRegulations) {
      doc.setFont(fontBold)
      doc.setFontSize(fontSize)
      doc.text('С правилами внутреннего распорядка для пациентов ознакомлен:', hCenter, y, 'center')
      doc.setFont(fontRegular)

      y += doc.getLineHeight(label) / scaleFactor - 1
      doc.line(pdfMargin, y, (hCenter) - lineLength, y)
      doc.line((hCenter) + lineLength - 12, y, pdfWidth - pdfMargin, y)

      doc.setFontSize(7)
      doc.text('(подпись)', pdfMargin + lineLength, y + 3)
      doc.text('(ф.и.о)', pdfWidth - pdfMargin - lineLength - 12, y + 3)
    }

    let dividerY = pdfHeight / 2
    if (data.useA4) {
      // Divider
      doc.setLineDashPattern([3, 3], 0)
      doc.line(pdfMargin, dividerY, pdfWidth - pdfMargin, dividerY)
      doc.setLineDashPattern([], 0)

      y = pdfHeight / 2
      y += (doc.getLineHeight(label) / scaleFactor) * 3
    } else if (data.documentUseConsentProcessingPersonalData || data.documentUseInternalRegulations) {
      doc.addPage()
      y = 10
    }

    doc.setFontSize(data.oneMainPage ? fontSize - 1 : fontSize)

    // Current address
    label = 'Адрес места пребывания (фактического проживания)'
    doc.text(label, pdfMargin, y)
    y = textToRows(data.patientAddress, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)

    // Social status
    label = 'Социальное положение (указать)'
    doc.text(label, pdfMargin, y)
    y = textToRows(
      data.patientSocialStatus, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor
    )

    // Work place
    label = 'Место работы (должность служащего), службы (учебы)'
    doc.text(label, pdfMargin, y)
    y = textToRows(data.patientWorkPlace, label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)

    // Social benefits
    let rectWidth = 12
    let rectHeight = 6
    label = 'Государственные социальные льготы:'
    let labelWidth = doc.getTextWidth(label)
    doc.text(label, pdfMargin, y)
    doc.text('нет', pdfMargin + labelWidth + 1 + rectWidth / 2, y, 'center')
    doc.text('да', pdfMargin + labelWidth + 1 + rectWidth + rectWidth / 2, y, 'center')
    firstSquarePosition = pdfMargin + labelWidth + 1
    secondSquarePosition = firstSquarePosition + rectWidth
    doc.rect(firstSquarePosition, y - (rectHeight / 1.5), rectWidth, rectHeight)
    doc.rect(secondSquarePosition, y - (rectHeight / 1.5), rectWidth, rectHeight)

    y += (doc.getLineHeight(label) / scaleFactor) + 1
    label = 'Категория льгот:'
    doc.text(label, pdfMargin, y)
    y = textToRows('', label, doc, pdfMargin, y, fontRegular, false, textLineHeightFactor)
    label = 'Документ, удостоверяющий право на льготы:'
    doc.text(label, pdfMargin, y)
    let positionXStart = pdfMargin + doc.getTextWidth(label) + 1
    label = 'серия'
    doc.text(label, (hCenter) + 56, y)
    doc.line(positionXStart, y + 1, (hCenter) + 55, y + 1)
    positionXStart = (hCenter) + 57 + doc.getTextWidth(label)
    doc.line(positionXStart, y + 1, pdfWidth - pdfMargin, y + 1)
    y += (doc.getLineHeight(label) / scaleFactor) + 1
    label = 'номер'
    doc.text(label, pdfMargin, y)
    positionXStart = pdfMargin + doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, positionXStart + 50, y + 1)
    positionXStart += 51
    label = 'выдан'
    doc.text(label, positionXStart, y)
    positionXStart += doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, pdfWidth - pdfMargin, y + 1)
    y += (doc.getLineHeight(label) / scaleFactor) + 1
    positionXStart = pdfMargin
    label = 'дата выдачи'
    doc.text(label, positionXStart, y)
    positionXStart += doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, positionXStart + 50, y + 1)
    positionXStart += 51
    label = 'срок действия'
    doc.text(label, positionXStart, y)
    positionXStart += doc.getTextWidth(label) + 1
    doc.line(positionXStart, y + 1, pdfWidth - pdfMargin, y + 1)
  }

  return y
}